/* ============================================================================
   ATELIER PROJECT STUDIO — Project Manager store.
   Same reactive-localStorage pattern as the bid store, scoped to the
   construction phase. Two seeded projects: one mid-construction, one just
   mobilising after award.
   ========================================================================== */
import { useEffect, useState } from "react";
import { authHeader } from "./managerAuth";
import { STUDIO, uid, getTenders, APP_URL } from "./store";
import { recordChange } from "./undoStore";
import { notify } from "./notify";
import { longLeadSchedule, matchTasks } from "./longLead";
import { LONG_LEAD_LABEL } from "./types";
import type { LongLeadItem, LongLeadStatus } from "./types";
import { peopleActions, companyLogo } from "./people";
import type {
  ActivityLine, ClientRole, ClientRequest, ClientMessage,
  Project, DrawingSet, DrawingRevision, SheetRedline, RevisionRequest, PmRfi, DailyLog, GanttTask,

  DailyTarget, MaterialRequest, MaterialStatus, PunchItem, ProgressReport,
  MepSystem, MepStage, MepDiscipline, MepTestResult,
  Markup, BallInCourt, Weather, CrewLine, CrewMember, WorkArea, SiteChallenge, InfoRequest, SiteInstructionReceived,
  Activity, TeamMember, TeamRole,
  Observation, ObservationCategory, SiteVisit, SiteVisitDirective,
  ClientViewConfig, MindmapLink, Meeting, MeetingKind, AgendaItem, DocCategory,
  RenderCrop, BimModel, ModelFormat, AdhocTask, TaskNote, RfiAttachment, RfiPointFile, RfiPointAnswer,
  SiteViewpoint, DailyReportRules,
  DrawingShare, ProjectPhase, ExecShare, ContractClause, BoqItem, TradePackage, PackagePayment, SafetyRecord, ClientApproval, ApprovalTarget, TelemetryEvent,
  ScopeItem, SubInvite, SubQuote, SubClaim, SubAttachment, SubExposure,
  Bottleneck, BottleneckComment, BottleneckStakeholder, BottleneckKind, BottleneckImpact,
} from "./types";

const KEY = "atelier.pm.v1";

/** Who "the ball is with" reads as, in an email with no UI around it. */
export function bottleneckBallLabel(b: BallInCourt): string {
  return b === "manager" ? `${STUDIO.manager} (studio)`
    : ({ contractor: "the contractor", consultant: "the consultant", client: "the client", supplier: "the supplier" } as const)[b];
}

type State = { projects: Project[] };

const nowISO = () => new Date().toISOString();
function iso(d: string) { return new Date(d).toISOString(); }
function day(d: string) { return d; } // ISO date strings kept plain

/* ---------------------------------------------------------------- seed -- */
function seedProjects(): Project[] {
  const p1: Project = {
    id: "prj-potaro",
    code: "PRJ-2601",
    name: "Potaro Gardens — Phase 2",
    location: "Bartica, Guyana",
    geo: { lat: 6.404, lon: -58.621 },
    details: {
      siteAddress: "Potaro Road, Bartica, Cuyuni-Mazaruni, Guyana",
      plotArea: "2.8 ha",
      builtArea: "3,640 m² GFA",
      scope: "18 duplex units, roads & drainage, community pavilion",
      procurement: "Measured contract — re-measurable BOQ",
      contractForm: "FIDIC Red Book 2017",
    },
    description: "Phase 2 of the Potaro Gardens housing development: 18 duplex units, access roads, drainage and a community pavilion. Measured contract under FIDIC Red Book 2017.",
    hue: 96,
    currency: "USD",
    contractSum: 6_800_000,
    client: { name: "R. Singh", company: "TRICAN Ltd.", email: "r.singh@trican.gy" },
    contractor: { name: "M. Persaud", company: "Cornerstone Civil Ltd.", email: "site@cornerstonecivil.gy", accessCode: "PG-CORNER" },
    team: [
      { id: "tm-corner", name: "M. Persaud", company: "Cornerstone Civil Ltd.", email: "site@cornerstonecivil.gy", role: "contractor", accessCode: "PG-CORNER", invitedAt: iso("2026-01-20T09:00"), status: "active" },
      { id: "tm-geo", name: "S. Ali", company: "GeoTech Partners", email: "s.ali@geotechpartners.gy", role: "consultant", accessCode: "PG-GEO", invitedAt: iso("2026-02-10T09:00"), status: "active" },
      { id: "tm-trican", name: "R. Singh", company: "TRICAN Ltd.", email: "r.singh@trican.gy", role: "client", accessCode: "PG-TRICAN", invitedAt: iso("2026-02-02T09:00"), status: "invited" },
    ],
    startDate: day("2026-02-02"),
    targetCompletion: day("2027-03-26"),
    programmeUpdatedAt: iso("2026-07-10T16:20"),
    programmeUpdatedBy: "Cornerstone Civil Ltd.",
    drawings: [
      {
        id: "ds-a101", number: "A-101", title: "Duplex unit plans — Type A & B", discipline: "Architectural", category: "drawing",
        revisions: [
          { rev: "P1", issuedAt: iso("2026-01-12T09:00"), by: STUDIO.manager, size: "14.2 MB" },
          { rev: "P2", issuedAt: iso("2026-03-04T11:30"), by: STUDIO.manager, note: "Kitchen layout revised per client sign-off", size: "14.6 MB" },
          { rev: "P3", issuedAt: iso("2026-06-18T15:10"), by: STUDIO.manager, note: "Window schedule aligned with supplier sizes", size: "14.6 MB" },
        ],
      },
      {
        id: "ds-a201", number: "A-201", title: "Elevations & sections — duplex blocks", discipline: "Architectural", category: "drawing",
        revisions: [{ rev: "P1", issuedAt: iso("2026-01-12T09:00"), by: STUDIO.manager, size: "9.1 MB" }],
      },
      {
        id: "ds-s301", number: "S-301", title: "Foundations — strip & pad details", discipline: "Structural", category: "drawing",
        revisions: [
          { rev: "P1", issuedAt: iso("2026-01-12T09:00"), by: STUDIO.manager, size: "11.4 MB" },
          { rev: "P2", issuedAt: iso("2026-04-22T10:05"), by: STUDIO.manager, note: "Pad F3 enlarged after soil test at grid D2", size: "11.8 MB" },
        ],
      },
      {
        id: "ds-s302", number: "S-302", title: "Ring beam & roof anchorage", discipline: "Structural", category: "drawing",
        revisions: [{ rev: "P1", issuedAt: iso("2026-02-20T09:00"), by: STUDIO.manager, size: "8.3 MB" }],
      },
      {
        id: "ds-m401", number: "M-401", title: "Plumbing risers & septic layout", discipline: "MEP", category: "drawing",
        revisions: [{ rev: "P1", issuedAt: iso("2026-02-02T09:00"), by: STUDIO.manager, size: "6.9 MB" }],
      },
      {
        id: "ds-c101", number: "C-101", title: "Roads, drainage & external works", discipline: "Civil", category: "drawing",
        revisions: [
          { rev: "P1", issuedAt: iso("2026-01-12T09:00"), by: STUDIO.manager, size: "12.7 MB" },
          { rev: "P2", issuedAt: iso("2026-05-30T14:40"), by: STUDIO.manager, note: "Culvert relocated at chainage 0+140", size: "13.0 MB" },
        ],
      },
    ],
    meetings: [],
    rfis: [
      {
        id: "prfi-1", ref: "RFI-014", subject: "Pad F3 reinforcement congestion", question: "The enlarged pad at grid D2 (S-301 P2) now clashes with the ring beam starter bars. Can the starters be cranked, or should the pad depth increase?",
        askedBy: "Cornerstone Civil Ltd.", ballInCourt: "manager", askedAt: iso("2026-07-09T08:40"), dueDate: day("2026-07-16"), status: "open",
        docId: "ds-s301", markups: [{ id: "pmk-1", kind: "rect", x: 0.34, y: 0.3, w: 0.18, h: 0.24, note: "Starter bar clash zone" }],
      },
      {
        id: "prfi-2", ref: "RFI-013", subject: "Septic tank invert levels", question: "Confirm invert level at the septic outfall — M-401 shows 1.20 m but the drainage drawing implies 1.35 m.",
        askedBy: "Cornerstone Civil Ltd.", ballInCourt: "contractor", askedAt: iso("2026-06-28T10:15"), dueDate: day("2026-07-05"),
        answer: "Use 1.35 m per C-101 P2. M-401 will be corrected at the next revision.", answeredAt: iso("2026-07-02T09:30"), status: "answered",
        docId: "ds-m401",
      },
      {
        id: "prfi-3", ref: "RFI-012", subject: "Paint system for pavilion steel", question: "Specify the coating system for exposed pavilion steel — coastal exposure.",
        askedBy: "Cornerstone Civil Ltd.", ballInCourt: "consultant", askedAt: iso("2026-06-20T13:00"), dueDate: day("2026-06-30"),
        answer: "Two-pack epoxy zinc primer + polyurethane topcoat, 240 µm DFT total.", answeredAt: iso("2026-06-24T16:45"), status: "answered",
      },
    ],
    observations: [
      {
        id: "ob-3", ref: "OBS-003", by: "S. Ali", company: "GeoTech Partners", category: "quality",
        note: "Bedding gravel in the drainage trench at 0+120 looks under-compacted along a 4 m stretch — recommend re-rolling before pipe laying.",
        location: "Chainage 0+120", at: iso("2026-07-13T11:20"), status: "open",
      },
      {
        id: "ob-2", ref: "OBS-002", by: "M. Persaud", company: "Cornerstone Civil Ltd.", category: "safety",
        note: "Edge protection missing on the unit 9 first-floor slab after shutter strike. Barriers reinstated same day — flagging for the record.",
        location: "Unit 9", at: iso("2026-07-11T09:05"), status: "closed",
        response: "Noted and appreciated — keep the reinstatement photos with the daily report.", respondedAt: iso("2026-07-11T14:30"),
      },
      {
        id: "ob-1", ref: "OBS-001", by: "R. Singh", company: "TRICAN Ltd.", category: "design",
        note: "Client walk: could the pavilion face the creek rather than the access road? Sightline is much better from the north-east corner.",
        location: "Pavilion pad", at: iso("2026-07-10T11:00"), status: "closed",
        response: "Reviewed — orientation swap is feasible before setting out. Revised C-101 to follow.", respondedAt: iso("2026-07-12T10:00"),
      },
    ],
    visits: [
      {
        id: "sv-2", ref: "SV-002", date: day("2026-07-10"), attendees: "R. Ferdinand (studio), R. Singh (TRICAN), M. Persaud (Cornerstone)",
        purpose: "Monthly client walk & slab pour witness",
        notes: [
          "Ground slab pour at unit 11 witnessed — finish acceptable, cube samples taken.",
          "Blockwork line and plumb checked at units 7–8: within tolerance.",
          "Compound housekeeping improved since SV-001; rebar storage now racked.",
        ],
        directives: [
          { id: "dir-3", text: "Re-roll drainage bedding 0+100–0+140 before pipe laying", assignee: "Cornerstone Civil Ltd.", due: day("2026-07-16"), done: false },
          { id: "dir-4", text: "Submit cube results for unit 11 pour", assignee: "Cornerstone Civil Ltd.", due: day("2026-07-24"), done: false },
        ],
        photoIds: ["ph-seed-3", "ph-seed-6"],
        by: STUDIO.manager, issuedAt: iso("2026-07-10T17:40"),
      },
      {
        id: "sv-1", ref: "SV-001", date: day("2026-06-12"), attendees: "R. Ferdinand (studio), M. Persaud (Cornerstone)",
        purpose: "Foundations progress & quality walk",
        notes: [
          "Pads complete to unit 9; steel for unit 10 inspected and passed.",
          "Culvert relocation at 0+140 agreed on site — C-101 P2 to capture it.",
        ],
        directives: [
          { id: "dir-1", text: "Protect exposed starter bars at block B", assignee: "Cornerstone Civil Ltd.", done: true },
          { id: "dir-2", text: "Provide updated pour schedule for July", assignee: "Cornerstone Civil Ltd.", done: true },
        ],
        photoIds: [],
        by: STUDIO.manager, issuedAt: iso("2026-06-12T18:00"),
      },
    ],
    logs: [
      {
        id: "dl-5", date: day("2026-07-13"), weather: "sunny", tempC: 31,
        crew: [{ trade: "Masons", count: 8 }, { trade: "Carpenters", count: 6 }, { trade: "Labourers", count: 12 }, { trade: "Steel fixers", count: 4 }],
        equipment: "Excavator ×1, concrete mixer ×2, dumper ×1",
        workDone: ["Blockwork to first floor, units 7–9", "Ring beam shutter to unit 10", "Drainage trench 0+080 to 0+140"],
        delays: "Rebar delivery arrived 2 hrs late — steel fixing resequenced to afternoon.",
        by: "Cornerstone Civil Ltd.",
        photoIds: ["ph-seed-1", "ph-seed-2"],
        acknowledged: { by: STUDIO.manager, at: iso("2026-07-14T08:10") },
      },
      {
        id: "dl-4", date: day("2026-07-11"), weather: "rain", tempC: 27,
        crew: [{ trade: "Masons", count: 6 }, { trade: "Labourers", count: 10 }],
        workDone: ["Internal blockwork units 5–6 (covered areas)", "Materials restocked at compound"],
        delays: "Heavy rain 09:00–13:00; external pours suspended.",
        by: "Cornerstone Civil Ltd.",
      },
      {
        id: "dl-3", date: day("2026-07-10"), weather: "cloudy", tempC: 29,
        crew: [{ trade: "Masons", count: 8 }, { trade: "Carpenters", count: 5 }, { trade: "Labourers", count: 11 }],
        workDone: ["Cast ground slab unit 11", "Roof anchorage brackets set out, units 1–4"],
        visitors: "TRICAN site walk — R. Singh, 10:30",
        by: "Cornerstone Civil Ltd.",
        photoIds: ["ph-seed-3", "ph-seed-4"],
        acknowledged: { by: STUDIO.manager, at: iso("2026-07-11T08:00") },
      },
      {
        id: "dl-2", date: day("2026-07-09"), weather: "sunny", tempC: 32,
        crew: [{ trade: "Masons", count: 7 }, { trade: "Steel fixers", count: 5 }, { trade: "Labourers", count: 12 }],
        workDone: ["Pad F3 excavation at grid D2", "Blockwork to first floor, units 6–7"],
        note: "RFI-014 raised on pad F3 congestion — see Drawings.",
        by: "Cornerstone Civil Ltd.",
      },
      {
        id: "dl-1", date: day("2026-07-08"), weather: "sunny", tempC: 30,
        crew: [{ trade: "Masons", count: 8 }, { trade: "Carpenters", count: 4 }, { trade: "Labourers", count: 10 }],
        workDone: ["Strip footings units 12–13", "Culvert base at chainage 0+140"],
        by: "Cornerstone Civil Ltd.",
      },
    ],
    tasks: [
      { id: "gt-1", name: "Mobilisation & site setup", phase: "Mobilisation", start: day("2026-02-02"), end: day("2026-02-20"), progress: 100, owner: "Cornerstone", baselineStart: day("2026-02-02"), baselineEnd: day("2026-02-18") },
      { id: "gt-2", name: "Bulk earthworks & platforms", phase: "Substructure", start: day("2026-02-16"), end: day("2026-03-20"), progress: 100, owner: "Cornerstone", baselineStart: day("2026-02-16"), baselineEnd: day("2026-03-13") },
      { id: "gt-3", name: "Strip & pad foundations, units 1–9", phase: "Substructure", start: day("2026-03-09"), end: day("2026-04-24"), progress: 100, owner: "Cornerstone", baselineStart: day("2026-03-09"), baselineEnd: day("2026-04-17") },
      { id: "gt-4", name: "Strip & pad foundations, units 10–18", phase: "Substructure", start: day("2026-04-20"), end: day("2026-06-05"), progress: 92, owner: "Cornerstone", baselineStart: day("2026-04-13"), baselineEnd: day("2026-05-22") },
      { id: "gt-5", name: "Ground slabs & services rough-in", phase: "Substructure", start: day("2026-05-04"), end: day("2026-07-17"), progress: 78, owner: "Cornerstone", baselineStart: day("2026-04-27"), baselineEnd: day("2026-07-03") },
      { id: "gt-6", name: "Blockwork & ring beams, units 1–9", phase: "Superstructure", start: day("2026-05-25"), end: day("2026-08-14"), progress: 55, owner: "Cornerstone", baselineStart: day("2026-05-18"), baselineEnd: day("2026-07-31") },
      { id: "gt-7", name: "Blockwork & ring beams, units 10–18", phase: "Superstructure", start: day("2026-07-06"), end: day("2026-09-25"), progress: 12, owner: "Cornerstone", baselineStart: day("2026-06-29"), baselineEnd: day("2026-09-11") },
      { id: "gt-8", name: "Roof structure & covering", phase: "Superstructure", start: day("2026-08-17"), end: day("2026-11-06"), progress: 0, owner: "Cornerstone", baselineStart: day("2026-08-03"), baselineEnd: day("2026-10-23") },
      { id: "gt-9", name: "Windows & external doors", phase: "Envelope", start: day("2026-09-28"), end: day("2026-11-20"), progress: 0, owner: "Cornerstone", baselineStart: day("2026-09-14"), baselineEnd: day("2026-11-06") },
      { id: "gt-10", name: "First fix M&E", phase: "Fit-out", start: day("2026-10-05"), end: day("2026-12-11"), progress: 0, owner: "Cornerstone", baselineStart: day("2026-09-21"), baselineEnd: day("2026-11-27") },
      { id: "gt-11", name: "Plaster, screeds & finishes", phase: "Fit-out", start: day("2026-11-09"), end: day("2027-01-29"), progress: 0, owner: "Cornerstone", baselineStart: day("2026-10-26"), baselineEnd: day("2027-01-15") },
      { id: "gt-12", name: "Roads, drainage & external works", phase: "External", start: day("2026-06-08"), end: day("2026-12-18"), progress: 34, owner: "Cornerstone", baselineStart: day("2026-06-01"), baselineEnd: day("2026-12-04") },
      { id: "gt-13", name: "Community pavilion", phase: "External", start: day("2026-09-07"), end: day("2027-01-08"), progress: 0, owner: "Cornerstone", baselineStart: day("2026-08-24"), baselineEnd: day("2026-12-25") },
      { id: "gt-14", name: "Snagging & handover", phase: "Handover", start: day("2027-02-01"), end: day("2027-03-26"), progress: 0, owner: "Cornerstone", baselineStart: day("2027-01-18"), baselineEnd: day("2027-03-12"), milestone: false },
    ],
    targets: [
      { id: "tg-1", date: day("2026-07-14"), text: "Cast ring beam, unit 10", trade: "Concrete", done: false },
      { id: "tg-2", date: day("2026-07-14"), text: "Blockwork first lift, units 8–9", trade: "Masons", done: true },
      { id: "tg-3", date: day("2026-07-14"), text: "Close drainage trench 0+080–0+140", trade: "Civil", done: false },
      { id: "tg-4", date: day("2026-07-14"), text: "Set out pavilion pad locations", trade: "Engineer", done: false },
      { id: "tg-5", date: day("2026-07-15"), text: "Strip shutters unit 10 ring beam", trade: "Carpenters", done: false },
      { id: "tg-6", date: day("2026-07-15"), text: "Rebar cages for pads F4–F6", trade: "Steel fixers", done: false },
      { id: "tg-w1", date: day("2026-07-13"), scope: "week", text: "Ring beams cast to units 8–10", qty: 3, qtyDone: 1, unit: "units", done: false },
      { id: "tg-w2", date: day("2026-07-13"), scope: "week", text: "Drainage run complete to chainage 0+200", qty: 200, qtyDone: 140, unit: "m", done: false },
      { id: "tg-w3", date: day("2026-07-13"), scope: "week", text: "Pad foundations F4–F6 poured", done: true },
      { id: "tg-7", date: day("2026-07-13"), text: "Blockwork to first floor, units 7–9", trade: "Masons", done: true },
      { id: "tg-8", date: day("2026-07-13"), text: "Drainage trench to 0+140", trade: "Civil", done: true },
    ],
    materials: [
      { id: "mr-1", ref: "MR-018", item: "Reinforcement steel, 12 mm", spec: "Grade 60, 12 m lengths", qty: 6, unit: "tonnes", neededBy: day("2026-07-20"), status: "ordered", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-07-06T09:00"), supplier: "Toolsie Persaud Ltd.", updatedAt: iso("2026-07-08T14:00") },
      { id: "mr-2", ref: "MR-017", item: "Hollow concrete blocks, 6\"", qty: 4200, unit: "pcs", neededBy: day("2026-07-24"), status: "approved", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-07-05T10:30"), updatedAt: iso("2026-07-07T11:00") },
      { id: "mr-3", ref: "MR-016", item: "uPVC window frames — Type W2", spec: "Per A-101 P3 window schedule", qty: 36, unit: "sets", neededBy: day("2026-09-28"), status: "under-review", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-07-10T15:20"), note: "Long lead — supplier quotes 10 weeks." },
      { id: "mr-4", ref: "MR-015", item: "Roof anchorage brackets", spec: "S-302 detail 4, hot-dip galv.", qty: 144, unit: "pcs", neededBy: day("2026-08-10"), status: "requested", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-07-12T08:45") },
      { id: "mr-5", ref: "MR-014", item: "Cement, Portland", qty: 900, unit: "bags", neededBy: day("2026-07-10"), status: "delivered", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-06-26T09:00"), supplier: "TCL Guyana", updatedAt: iso("2026-07-09T13:30") },
      { id: "mr-6", ref: "MR-013", item: "Site dewatering pump, 4\"", qty: 1, unit: "no.", neededBy: day("2026-07-01"), status: "rejected", requestedBy: "Cornerstone Civil Ltd.", requestedAt: iso("2026-06-24T14:00"), note: "Rejected — hire under contractor's plant, not a supply item." },
    ],
    submittals: [
      {
        id: "sub-1", ref: "SUB-003", title: "Reinforcement shop drawings — ring beam & first-floor slab",
        kind: "shop-drawing", specSection: "03 20 00 — Concrete reinforcing", discipline: "Structural",
        requiredOnSite: day("2026-08-05"), leadTimeWeeks: 3, status: "under-review",
        raisedAt: iso("2026-07-02T09:00"), raisedBy: STUDIO.manager,
        ballInCourt: "manager", dueDate: day("2026-07-19"), assignee: STUDIO.manager,
        revisions: [
          { rev: "01", submittedAt: iso("2026-07-05T14:20"), submittedBy: "M. Persaud", note: "Bar bending schedules included.", size: "4.1 MB",
            response: "revise-resubmit", reviewedBy: STUDIO.manager, reviewedAt: iso("2026-07-09T11:00"),
            reviewNote: "1. Lap lengths at the ring-beam corners fall short of the 40d called for on S-302.\n2. Confirm cover to the external face is 40mm, not 25mm.\n3. Bar marks 12–18 are missing from the schedule." },
          { rev: "02", submittedAt: iso("2026-07-16T08:40"), submittedBy: "M. Persaud", note: "Laps corrected to 40d, cover to 40mm, schedule completed.", size: "4.3 MB" },
        ],
        comments: [
          { id: "cm-s1", by: "M. Persaud", company: "Cornerstone Civil Ltd.", at: iso("2026-07-16T08:45"), text: "Rev 02 uploaded — steel is on order subject to your approval, we need it released by Friday to hold the pour date." },
        ],
      },
      {
        id: "sub-2", ref: "SUB-002", title: "Concrete mix design — 30 MPa structural",
        kind: "mix-design", specSection: "03 30 00 — Cast-in-place concrete", discipline: "Structural",
        status: "approved", raisedAt: iso("2026-06-12T09:00"), raisedBy: STUDIO.manager, closedAt: iso("2026-06-20T15:30"),
        revisions: [
          { rev: "01", submittedAt: iso("2026-06-15T10:00"), submittedBy: "M. Persaud", size: "820 KB",
            response: "approved", reviewedBy: STUDIO.manager, reviewedAt: iso("2026-06-20T15:30"),
            reviewNote: "Approved. Trial cubes to be taken from the first pour as specified." },
        ],
      },
      {
        id: "sub-3", ref: "SUB-004", title: "Roof sheeting — colour sample & profile",
        kind: "material-sample", specSection: "07 41 13 — Metal roof panels", discipline: "Architectural",
        requiredOnSite: day("2026-09-01"), leadTimeWeeks: 6, status: "draft",
        raisedAt: iso("2026-07-14T09:00"), raisedBy: STUDIO.manager,
        ballInCourt: "contractor", dueDate: day("2026-07-25"), revisions: [],
      },
    ],
    variations: [
      {
        id: "var-1", ref: "CE-002", variationNo: "VO-001", title: "Rock excavation at pads F3–F6",
        description: "Rock encountered at 1.2m below existing ground across pads F3 to F6, requiring breaker excavation and additional lean concrete blinding. Confirmed on site 24 June with the engineer present.",
        origin: "site-condition", originRef: "RFI-014", status: "approved",
        estimatedCost: 18_000, quotedCost: 22_400, approvedCost: 21_000, timeImpactDays: 4,
        raisedAt: iso("2026-06-24T14:00"), raisedBy: STUDIO.manager, quotedAt: iso("2026-06-28T10:00"),
        decidedAt: iso("2026-07-02T16:00"), decidedBy: STUDIO.manager,
        decisionNote: "Agreed at 21,000 on measured quantities; the 4-day extension is granted against the substructure activity only.",
      },
      {
        id: "var-2", ref: "CE-003", title: "Upgrade external doors to hardwood",
        description: "Client requests external doors to units 1–12 be upgraded from the specified engineered doors to solid purpleheart, to match the pavilion joinery.",
        origin: "client-request", status: "submitted",
        estimatedCost: 26_000, quotedCost: 31_800, timeImpactDays: 0,
        raisedAt: iso("2026-07-10T11:00"), raisedBy: STUDIO.manager, quotedAt: iso("2026-07-15T09:20"),
        ballInCourt: "client", dueDate: day("2026-07-24"),
      },
      {
        id: "var-3", ref: "CE-004", title: "Additional site drainage at the northern boundary",
        description: "Standing water observed after the June rains along the northern boundary; additional 150mm perforated land drain and gravel surround proposed over 60m.",
        origin: "design-change", status: "quoting",
        estimatedCost: 9_500, raisedAt: iso("2026-07-16T08:00"), raisedBy: STUDIO.manager,
        ballInCourt: "contractor", dueDate: day("2026-07-23"),
      },
    ],
    correspondence: [
      {
        id: "cor-1", ref: "SI-004", kind: "site-instruction",
        subject: "Relocate the culvert at chainage 0+140",
        body: "Further to the site meeting of 9 July, the 600mm culvert at chainage 0+140 is to be relocated 3.5m north of its drawn position to clear the existing utility corridor, as marked on C-101 revision P2 issued herewith.\n\nAll other details remain as drawn. This instruction takes effect immediately.",
        issuedBy: STUDIO.manager, issuedTo: "Cornerstone Civil Ltd.", issuedAt: iso("2026-07-10T09:30"),
        requiresResponseBy: day("2026-07-17"), status: "acknowledged",
        acknowledgedBy: "M. Persaud", acknowledgedAt: iso("2026-07-10T16:05"),
        costImpact: true, ballInCourt: "contractor", dueDate: day("2026-07-17"),
      },
      {
        id: "cor-2", ref: "EW-001", kind: "early-warning",
        subject: "Cement supply — possible delay to the August pours",
        body: "We give early warning that our cement supplier has advised of a possible two-week interruption to deliveries from 4 August owing to a shipping delay. We are seeking an alternative source and will confirm by 25 July.\n\nNo delay is claimed at this stage; this notice is given so the matter can be managed jointly.",
        issuedBy: "M. Persaud", issuedTo: "Atelier Project Studio", issuedAt: iso("2026-07-15T11:00"),
        status: "issued", ballInCourt: "manager", requiresResponseBy: day("2026-07-25"),
      },
    ],
    checklists: [
      {
        id: "chk-1", ref: "CHK-001", name: "Pre-pour — slab & ring beam", category: "pre-pour",
        intro: "Run immediately before any concrete is placed, with the contractor's engineer present. No pour proceeds on a failed line.",
        updatedAt: iso("2026-06-01T09:00"), updatedBy: STUDIO.manager,
        sections: [
          {
            id: "sec-1", title: "Setting out & formwork", lines: [
              { id: "ln-1", text: "Setting out checked against the current drawing revision", guidance: "Confirm the sheet revision on site matches the register." },
              { id: "ln-2", text: "Formwork line, level and plumb within tolerance" },
              { id: "ln-3", text: "Formwork props and ties secure, no visible deflection" },
              { id: "ln-4", text: "Release agent applied evenly, no pooling" },
            ],
          },
          {
            id: "sec-2", title: "Reinforcement", lines: [
              { id: "ln-5", text: "Bar sizes and spacing match the approved shop drawing" },
              { id: "ln-6", text: "Laps at or above the specified length", guidance: "40d unless noted otherwise on the structural sheets." },
              { id: "ln-7", text: "Cover spacers in place at the specified centres", photoRequired: true },
              { id: "ln-8", text: "Starter bars positioned for the next lift" },
            ],
          },
          {
            id: "sec-3", title: "Services & readiness", lines: [
              { id: "ln-9", text: "Conduits, sleeves and box-outs installed and secured" },
              { id: "ln-10", text: "Pour sequence, construction joints and finish agreed" },
              { id: "ln-11", text: "Weather forecast checked; curing materials on site" },
            ],
          },
        ],
      },
      {
        id: "chk-2", ref: "CHK-002", name: "Weekly site safety walk", category: "safety",
        intro: "Walked every Friday with the contractor's safety officer.",
        updatedAt: iso("2026-06-01T09:00"), updatedBy: STUDIO.manager,
        sections: [
          {
            id: "sec-4", title: "Access & edge protection", lines: [
              { id: "ln-12", text: "Edge protection complete at all open edges and voids" },
              { id: "ln-13", text: "Ladders and scaffolds tagged and in good order" },
              { id: "ln-14", text: "Access routes clear of materials and standing water" },
            ],
          },
          {
            id: "sec-5", title: "People & plant", lines: [
              { id: "ln-15", text: "PPE worn by everyone on site, visitors included" },
              { id: "ln-16", text: "Plant operators certified; daily checks recorded" },
              { id: "ln-17", text: "First-aid provision and emergency contacts displayed" },
            ],
          },
        ],
      },
    ],
    inspections: [
      {
        id: "insp-1", ref: "INS-002", templateId: "chk-1", templateName: "Pre-pour — slab & ring beam",
        category: "pre-pour", location: "Unit 10 — ring beam", date: day("2026-07-13"),
        by: STUDIO.manager, company: "Atelier Project Studio", witnessedBy: "M. Persaud",
        status: "failed", signedBy: STUDIO.manager, signedAt: iso("2026-07-13T15:40"),
        note: "Pour held until the cover spacers are corrected; re-inspection agreed for the following morning.",
        sections: [],   // filled from the template on load (see normalize)
        lines: [
          { lineId: "ln-1", result: "pass" }, { lineId: "ln-2", result: "pass" },
          { lineId: "ln-3", result: "pass" }, { lineId: "ln-4", result: "pass" },
          { lineId: "ln-5", result: "pass" }, { lineId: "ln-6", result: "pass" },
          { lineId: "ln-7", result: "fail", note: "Cover spacers at 1.2m centres against 600mm specified along the east face.", raisedRef: "PL-005" },
          { lineId: "ln-8", result: "pass" }, { lineId: "ln-9", result: "pass" },
          { lineId: "ln-10", result: "pass" }, { lineId: "ln-11", result: "na" },
        ],
      },
    ],
    punch: [
      { id: "pl-5", ref: "PL-005", title: "Cover spacers at 1.2m centres against 600mm specified", location: "Unit 10 — ring beam", trade: "pre-pour", assignee: "Cornerstone Civil Ltd.", status: "open", raisedAt: iso("2026-07-13T15:40"), due: day("2026-07-17"), fromInspection: "INS-002", ballInCourt: "contractor" },
      { id: "pl-1", ref: "PL-004", title: "Honeycombing to column face, unit 3 porch", location: "Unit 3", trade: "Concrete", assignee: "Cornerstone Civil Ltd.", status: "open", raisedAt: iso("2026-07-08T10:00"), due: day("2026-07-18") },
      { id: "pl-2", ref: "PL-003", title: "Damp-proof course lapped short at unit 5 east wall", location: "Unit 5", trade: "Masonry", assignee: "Cornerstone Civil Ltd.", status: "ready-for-review", raisedAt: iso("2026-06-30T11:20") },
      { id: "pl-3", ref: "PL-002", title: "Drainage outfall headwall — exposed rebar", location: "Chainage 0+060", trade: "Civil", assignee: "Cornerstone Civil Ltd.", status: "closed", raisedAt: iso("2026-06-18T09:30") },
      { id: "pl-4", ref: "PL-001", title: "Setting-out pin disturbed at grid A1", location: "Block A", trade: "Engineer", assignee: "Cornerstone Civil Ltd.", status: "closed", raisedAt: iso("2026-05-12T08:00") },
    ],
    reports: [
      {
        id: "pr-2", title: "Progress report — June", period: "June 2026", progressPercent: 38,
        summary: "Substructure works substantially complete across units 1–13. Superstructure blockwork advancing on the first nine units; drainage and road formation progressing in parallel. Programme shows a two-week slip against baseline driven by April rainfall — recovery plan agreed with the contractor.",
        highlights: ["Ground slabs cast: 11 of 18 units", "Blockwork underway: units 1–9", "Culvert relocation at 0+140 resolved (C-101 P2)", "Two-week recovery plan in place"],
        issuedAt: iso("2026-07-03T17:00"),
      },
      {
        id: "pr-1", title: "Progress report — May", period: "May 2026", progressPercent: 27,
        summary: "Foundations complete to unit 9 and advancing on the second block. Soil test at grid D2 required an enlarged pad (S-301 P2). Materials supply stable; cement and block deliveries on schedule.",
        highlights: ["Foundations complete: units 1–9", "S-301 revised for pad F3", "Road formation to 0+200"],
        issuedAt: iso("2026-06-04T17:00"),
      },
    ],
    collab: [
      {
        id: "col-1", ref: "COL-003", title: "Confirm uPVC window supplier & lead time", kind: "material",
        description: "We need the window package confirmed for units 1–9 — supplier, the exact profile against A-101 P3, and a firm lead time so it doesn't hold the first-fix. Attach the quotation once you have it.",
        assignedTo: "Cornerstone Civil Ltd.", assignedBy: STUDIO.manager, priority: "high", status: "in-progress",
        dueDate: day("2026-07-28"), ballInCourt: "contractor", createdAt: iso("2026-07-14T09:00"), updatedAt: iso("2026-07-18T15:00"),
        followUps: 1, lastFollowUpAt: iso("2026-07-18T15:00"),
        updates: [
          { id: "cu-1", at: iso("2026-07-16T10:20"), by: "M. Persaud", text: "Sourcing two options — a local agent and an overseas supplier. Overseas is ~12% cheaper but 9 weeks lead. Will confirm once the sample profile is checked against the drawing.", event: "update" },
          { id: "cu-2", at: iso("2026-07-18T15:00"), by: STUDIO.manager, text: "Followed up — please confirm before Friday so it doesn't hold first-fix.", event: "follow-up" },
        ],
      },
      {
        id: "col-2", ref: "COL-002", title: "Method statement — ring-beam pour, unit 10", kind: "spec",
        description: "Submit the method statement and pour sequence for the unit 10 ring beam. Needs to cover the 40d lap detail on S-302 P2 and the propping arrangement.",
        assignedTo: "Cornerstone Civil Ltd.", assignedBy: STUDIO.manager, priority: "urgent", status: "submitted",
        dueDate: day("2026-07-22"), ballInCourt: "manager", createdAt: iso("2026-07-12T09:00"), updatedAt: iso("2026-07-19T11:00"),
        approvalRequested: true,
        updates: [
          { id: "cu-3", at: iso("2026-07-19T11:00"), by: "M. Persaud", text: "Method statement uploaded — laps set to 40d, back-propping to two floors. Requesting approval to proceed with the pour.", event: "approval-request" },
        ],
      },
      {
        id: "col-3", ref: "COL-001", title: "Provide soil bearing test results — grid D2", kind: "information",
        description: "We need the independent soil bearing test results for the enlarged pad at grid D2 to close out S-301 P2.",
        assignedTo: "Cornerstone Civil Ltd.", assignedBy: STUDIO.manager, priority: "normal", status: "approved",
        ballInCourt: "contractor", createdAt: iso("2026-06-28T09:00"), updatedAt: iso("2026-07-02T14:00"),
        approval: { granted: true, by: STUDIO.manager, at: iso("2026-07-02T14:00"), note: "Received and accepted — pad depth confirmed adequate." },
        updates: [
          { id: "cu-4", at: iso("2026-07-01T16:00"), by: "M. Persaud", text: "GeoTech report attached — allowable bearing 180 kPa at founding level.", event: "spec" },
          { id: "cu-5", at: iso("2026-07-02T14:00"), by: STUDIO.manager, text: "Approved. Pad depth confirmed adequate.", event: "approval-granted" },
        ],
      },
    ],
    activity: [
      { id: "pa-1", at: iso("2026-07-12T08:45"), who: "Cornerstone Civil Ltd.", what: "requested MR-015 — roof anchorage brackets", kind: "docs" },
      { id: "pa-2", at: iso("2026-07-10T16:20"), who: "Cornerstone Civil Ltd.", what: "uploaded a programme update — superstructure resequenced", kind: "timeline" },
      { id: "pa-3", at: iso("2026-07-09T08:40"), who: "Cornerstone Civil Ltd.", what: "raised RFI-014 on S-301 — pad F3 congestion", kind: "rfi" },
      { id: "pa-4", at: iso("2026-07-08T10:00"), who: STUDIO.manager, what: "raised punch item PL-004 — honeycombing at unit 3", kind: "docs" },
      { id: "pa-5", at: iso("2026-07-03T17:00"), who: STUDIO.manager, what: "issued the June progress report", kind: "docs" },
    ],
  };

  const p2: Project = {
    id: "prj-meridian",
    code: "PRJ-2602",
    name: "Meridian Loft — Fit-Out",
    location: "Tribeca, New York",
    geo: { lat: 40.7163, lon: -74.0086 },
    details: {
      siteAddress: "Hudson & Franklin, Tribeca, New York, NY",
      builtArea: "480 m² over one level",
      scope: "Full interior fit-out — joinery, finishes, MEP alterations, lighting",
      procurement: "Lump sum on drawings & finishes schedule",
      contractForm: "AIA A101-2017",
    },
    description: "Interior fit-out of the Meridian Loft residence following the tender award: joinery, finishes, MEP alterations and decorative lighting over a seven-month programme.",
    hue: 200,
    currency: "USD",
    contractSum: 862_400,
    client: { name: "L. Osei-Mensah", company: "Meridian Holdings", email: "l.osei@meridianholdings.com" },
    contractor: { name: "P. Deygoo", company: "Oakfield Interiors", email: "site@oakfieldinteriors.com", accessCode: "ML-FITOUT" },
    team: [
      { id: "tm2-oak", name: "P. Deygoo", company: "Oakfield Interiors", email: "site@oakfieldinteriors.com", role: "contractor", accessCode: "ML-FITOUT", invitedAt: iso("2026-07-10T09:00"), status: "active" },
      // Cornerstone are also on this job for the shell/groundworks — the same
      // firm as Potaro, so their one login (PG-CORNER) opens both projects.
      { id: "tm2-corner", name: "M. Persaud", company: "Cornerstone Civil Ltd.", email: "site@cornerstonecivil.gy", role: "contractor", accessCode: "PG-CORNER", invitedAt: iso("2026-07-12T09:00"), status: "active" },
    ],
    startDate: day("2026-08-03"),
    targetCompletion: day("2027-03-05"),
    drawings: [
      { id: "ds2-id100", number: "ID-100", title: "Fit-out plans & RCPs", discipline: "Interiors", category: "drawing", revisions: [{ rev: "P1", issuedAt: iso("2026-06-16T09:00"), by: STUDIO.manager, size: "11.3 MB" }] },
      { id: "ds2-id200", number: "ID-200", title: "Joinery details & elevations", discipline: "Interiors", category: "drawing", revisions: [{ rev: "P1", issuedAt: iso("2026-06-16T09:00"), by: STUDIO.manager, size: "8.7 MB" }] },
      { id: "ds2-fs01", number: "FS-01", title: "Finishes schedule", discipline: "Interiors", category: "specification", revisions: [{ rev: "B", issuedAt: iso("2026-06-16T09:00"), by: STUDIO.manager, size: "1.2 MB" }] },
    ],
    rfis: [],
    logs: [],
    observations: [],
    visits: [],
    meetings: [],
    tasks: [
      { id: "g2-1", name: "Contract signing & mobilisation", phase: "Mobilisation", start: day("2026-08-03"), end: day("2026-08-14"), progress: 0, owner: "Oakfield" },
      { id: "g2-2", name: "Protection & strip-out", phase: "Enabling", start: day("2026-08-17"), end: day("2026-09-04"), progress: 0, owner: "Oakfield" },
      { id: "g2-3", name: "MEP alterations", phase: "Services", start: day("2026-09-07"), end: day("2026-10-16"), progress: 0, owner: "Oakfield" },
      { id: "g2-4", name: "Joinery fabrication (off-site)", phase: "Joinery", start: day("2026-09-07"), end: day("2026-11-13"), progress: 0, owner: "Oakfield" },
      { id: "g2-5", name: "Joinery installation", phase: "Joinery", start: day("2026-11-16"), end: day("2027-01-08"), progress: 0, owner: "Oakfield" },
      { id: "g2-6", name: "Finishes & decoration", phase: "Finishes", start: day("2026-12-07"), end: day("2027-02-12"), progress: 0, owner: "Oakfield" },
      { id: "g2-7", name: "Snagging & handover", phase: "Handover", start: day("2027-02-15"), end: day("2027-03-05"), progress: 0, owner: "Oakfield" },
    ],
    targets: [],
    materials: [
      { id: "mr2-1", ref: "MR-001", item: "White oak veneer panels", spec: "Per FS-01 finish F-03", qty: 68, unit: "sheets", neededBy: day("2026-09-14"), status: "under-review", requestedBy: "Oakfield Interiors", requestedAt: iso("2026-07-11T10:00"), note: "Early procurement — 8-week lead from mill." },
    ],
    punch: [],
    mep: [
      { id: "mep2-1", ref: "MEP-001", discipline: "Electrical", name: "Lighting circuits — living & gallery", zone: "Zones 1–2", contractor: "Oakfield Interiors", stage: "first-fix", createdAt: iso("2026-08-05T09:00"), updatedAt: iso("2026-08-20T15:00"), tests: [] },
      { id: "mep2-2", ref: "MEP-002", discipline: "Electrical", name: "Small power & data — study wall", zone: "Zone 3", contractor: "Oakfield Interiors", stage: "not-started", createdAt: iso("2026-08-05T09:00"), tests: [] },
      { id: "mep2-3", ref: "MEP-003", discipline: "Plumbing", name: "Hot & cold reroute — master bath", zone: "Master bathroom", contractor: "Oakfield Interiors", stage: "testing", createdAt: iso("2026-08-05T09:05"), updatedAt: iso("2026-08-21T11:30"),
        tests: [
          { id: "mt2-1", name: "Pressure test (1.5× working, 60 min)", result: "passed", at: iso("2026-08-21T11:30"), by: "P. Deygoo" },
          { id: "mt2-2", name: "Hot-water flow & temperature check", result: "pending" },
        ] },
      { id: "mep2-4", ref: "MEP-004", discipline: "Mechanical", name: "Ducted AC — living zone AHU-1", zone: "Zones 1–2 ceiling void", contractor: "Oakfield Interiors", stage: "not-started", createdAt: iso("2026-08-05T09:10"), tests: [], note: "Ductwork on order — 3-week lead. First fix follows strip-out." },
    ],
    reports: [],
    activity: [
      { id: "pa2-1", at: iso("2026-07-11T10:00"), who: "Oakfield Interiors", what: "requested MR-001 — white oak veneer (early procurement)", kind: "docs" },
    ],
  };

  return [p1, p2];
}

/* -------------------------------------------------------------- engine -- */
/** Tolerate aggregates saved before newer fields existed. */
function normalize(p: Project): Project {
  /* The core lists every page reads without a guard. A record written by an
     older build, or one whose empty list was dropped in a server merge, could
     arrive without one — and the Dashboard, the daily log and the client
     portal then blanked on `undefined.map`. */
  const lists = p as unknown as Record<string, unknown>;
  for (const k of ["drawings", "rfis", "logs", "tasks", "targets", "materials", "punch", "reports", "activity"]) {
    if (!Array.isArray(lists[k])) lists[k] = [];
  }
  if (!p.client) p.client = { name: "", company: "", email: "" };
  if (!p.contractor) p.contractor = { name: "", company: "", email: "", accessCode: "" };
  if (typeof p.startDate !== "string" || !p.startDate) p.startDate = new Date().toISOString().slice(0, 10);
  if (typeof p.targetCompletion !== "string" || !p.targetCompletion) p.targetCompletion = p.startDate;
  for (const l of p.logs) {
    const log = l as unknown as Record<string, unknown>;
    if (!Array.isArray(log.crew)) log.crew = [];
    if (!Array.isArray(log.workDone)) log.workDone = [];
  }
  for (const d of p.drawings) if (!Array.isArray(d.revisions)) (d as { revisions: unknown[] }).revisions = [];
  if (!Array.isArray(p.team)) {

    p.team = p.contractor ? [{
      id: uid("tm-"), name: p.contractor.name, company: p.contractor.company, email: p.contractor.email,
      role: "contractor", accessCode: p.contractor.accessCode, invitedAt: p.startDate, status: "active",
    }] : [];
  }
  if (!Array.isArray(p.observations)) p.observations = [];
  if (!Array.isArray(p.visits)) p.visits = [];
  if (!Array.isArray(p.meetings)) p.meetings = [];
  if (!p.clientView) p.clientView = { programme: true, reports: true, gallery: true, diary: false };
  if (!Array.isArray(p.clientSeen)) p.clientSeen = [];
  // contract-administration registers arrived after the first projects did
  if (!Array.isArray(p.submittals)) p.submittals = [];
  if (!Array.isArray(p.variations)) p.variations = [];
  if (!Array.isArray(p.correspondence)) p.correspondence = [];
  if (!Array.isArray(p.checklists)) p.checklists = [];
  if (!Array.isArray(p.inspections)) p.inspections = [];
  if (!Array.isArray(p.savedViews)) p.savedViews = [];
  if (!Array.isArray(p.schedules)) p.schedules = [];
  // an inspection always carries its own snapshot of the checklist; a seeded
  // one is written without it, so fill it from the template the first time.
  for (const insp of p.inspections) {
    if (!insp.sections?.length && insp.templateId) {
      const tpl = p.checklists.find((t) => t.id === insp.templateId);
      if (tpl) insp.sections = JSON.parse(JSON.stringify(tpl.sections));
    }
  }
  return p;
}

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw) as State;
      s.projects = s.projects.map(normalize);
      return s;
    }
  } catch { /* fall through */ }
  return { projects: seedProjects() };
}

let state: State = load();
const subs = new Set<() => void>();

/* ---- the device copy -------------------------------------------------------
   Projects used to be written to localStorage — the whole workspace, as one
   string, on every change. Safari gives a site 5 MB there and counts each
   character twice; the studio's projects passed that, the write failed, and a
   MacBook rebooted onto whatever last fitted (days old) while the sign-in
   token, sharing the same 5 MB, could not be saved either. The copy now lives
   in IndexedDB (hundreds of MB, no string encoding), written in the background
   at most every 600 ms and at once when the page is hidden. localStorage is
   read once on the first boot after this change, then cleared. */
const PDB_NAME = "atelier-projects";
const PDB_STORE = "kv";
let pdbPromise: Promise<IDBDatabase | null> | null = null;
function pdb(): Promise<IDBDatabase | null> {
  if (pdbPromise) return pdbPromise;
  pdbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") { resolve(null); return; }
      const req = indexedDB.open(PDB_NAME, 1);
      req.onupgradeneeded = () => { const db = req.result; if (!db.objectStoreNames.contains(PDB_STORE)) db.createObjectStore(PDB_STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  return pdbPromise;
}
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let committedBeforeReady = false;
let readyDone = false;
let legacyCleared = false;
async function writeDeviceCopy(): Promise<void> {
  if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
  const snapshot = state.projects;
  const db = await pdb();
  if (db) {
    const ok = await new Promise<boolean>((resolve) => {
      try {
        const tx = db.transaction(PDB_STORE, "readwrite");
        tx.objectStore(PDB_STORE).put(snapshot, "projects");
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch { resolve(false); }
    });
    if (ok) {
      if (!legacyCleared) { legacyCleared = true; try { localStorage.removeItem(KEY); } catch { /* ignore */ } }
      return;
    }
  }
  // no IndexedDB (some private modes) — best effort in localStorage
  try { localStorage.setItem(KEY, JSON.stringify({ projects: snapshot })); } catch { /* full — the server still has it */ }
}
function persist() {
  if (!readyDone) committedBeforeReady = true;
  if (persistTimer) return;
  persistTimer = setTimeout(() => { void writeDeviceCopy(); }, 600);
}
if (typeof window !== "undefined") {
  const now = () => { if (persistTimer) void writeDeviceCopy(); };
  window.addEventListener("pagehide", now);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") now(); });
}
/** Resolves once the device copy in IndexedDB has been read. Sync waits on it
    so the first pull merges into what this device actually had. */
export const projectsReady: Promise<void> = (async () => {
  try {
    const db = await pdb();
    if (!db) return;
    const saved = await new Promise<Project[] | null>((resolve) => {
      try {
        const req = db.transaction(PDB_STORE, "readonly").objectStore(PDB_STORE).get("projects");
        req.onsuccess = () => resolve(Array.isArray(req.result) ? (req.result as Project[]) : null);
        req.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
    // an edit made in the first instant of the page wins over the disk copy
    if (saved && !committedBeforeReady) {
      state = { projects: saved.map(normalize) };
      subs.forEach((f) => f());
    } else if (!saved && localStorage.getItem(KEY)) {
      void writeDeviceCopy();   // first run after the move — carry the old copy over
    }
  } catch { /* stay on what loaded synchronously */ }
  finally { readyDone = true; }
})();

function commit(next: State) {
  state = next;
  persist();
  subs.forEach((f) => f());
}

export function usePmStore(): State {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state;
}

export function useProject(id: string | undefined) {
  return usePmStore().projects.find((p) => p.id === id);
}
export function getProjects() { return state.projects; }

/** When something last happened on the project — the newest of the activity
    log, daily reports, programme imports, period reports, correspondence and
    inspections. Drives "sort by latest update" and the digest. */
export function lastUpdateAt(p: Project): string {
  const c: string[] = [];
  if (p.activity?.[0]?.at) c.push(p.activity[0].at);
  for (const l of p.logs ?? []) c.push(`${l.date}T18:00:00.000Z`);
  if (p.programmeUpdatedAt) c.push(p.programmeUpdatedAt);
  for (const r of p.reports ?? []) if (r.issuedAt) c.push(r.issuedAt);
  for (const x of p.correspondence ?? []) if (x.issuedAt) c.push(x.issuedAt);
  for (const i of p.inspections ?? []) if (i.date) c.push(`${i.date}T12:00:00.000Z`);
  c.push(`${p.startDate}T00:00:00.000Z`);
  return c.reduce((m, v) => (v > m ? v : m), "");
}

/** Projects in the studio's hand-set order (then creation order). */
export function orderedProjects(list: Project[]): Project[] {
  return list.map((p, i) => ({ p, i })).sort((a, b) => (a.p.listOrder ?? 1e6 + a.i) - (b.p.listOrder ?? 1e6 + b.i)).map((x) => x.p);
}


/** One reading of overall progress per day the programme moved. Kept short;
    the frozen snapshots carry the longer history. */
function withProgressTrail(prev: Project, next: Project): Project {
  if (next === prev || next.tasks === prev.tasks) return next;
  const v = overallProgress(next);
  const d = new Date().toISOString().slice(0, 10);
  const trail = (next.progressTrail ?? []).filter((x) => x.d !== d);
  const last = trail[trail.length - 1];
  if (last && last.v === v) return next;              // nothing moved in the number that matters
  return { ...next, progressTrail: [...trail, { id: `pt-${d}`, d, v }].slice(-400) };
}
/** Progress gained over the last `days` — against the trail, with the frozen
    snapshots as a longer memory. `from` is the reading compared against;
    `partial` when nothing that old is on record and the oldest reading stood in. */
export function progressGain(p: Project, days = 14, now = Date.now()): { gain: number; from: { d: string; v: number } | null; partial: boolean } {
  const points: { d: string; v: number }[] = [
    ...(p.progressTrail ?? []).map((x) => ({ d: x.d, v: x.v })),
    ...(p.snapshots ?? []).filter((s) => typeof s.progress === "number").map((s) => ({ d: s.at.slice(0, 10), v: Math.round(s.progress) })),
  ].filter((x) => x.d && !Number.isNaN(+new Date(x.d))).sort((a, b) => a.d.localeCompare(b.d));
  if (!points.length) return { gain: 0, from: null, partial: true };
  const cutoff = new Date(now - days * 86_400_000).toISOString().slice(0, 10);
  const older = points.filter((x) => x.d <= cutoff);
  const from = older.length ? older[older.length - 1] : points[0];
  const current = overallProgress(p);
  return { gain: current - from.v, from, partial: !older.length };
}

export function patchProject(id: string, fn: (p: Project) => Project) {
  const before = state.projects.find((p) => p.id === id);
  commit({ projects: state.projects.map((p) => (p.id === id ? withProgressTrail(p, fn(p)) : p)) });
  const changed = state.projects.find((p) => p.id === id);
  // one choke point for every mutation — so undo gets it for free
  if (before && changed && changed !== before) recordChange(before, changed);
  if (changed) projectListener?.(changed);
}

/** Write a whole project back — used by the undo journal to restore a
    remembered state. Syncs like any other change. */
export function restoreProject(id: string, project: Project) {
  commit({ projects: state.projects.map((p) => (p.id === id ? project : p)) });
  const changed = state.projects.find((p) => p.id === id);
  if (changed) projectListener?.(changed);
}

/* --- sync hooks (registered by lib/sync.ts) ---------------------------- */
let projectListener: ((p: Project) => void) | null = null;
export function onProjectChange(f: (p: Project) => void) { projectListener = f; }
let projectDeleteListener: ((pid: string) => void) | null = null;
export function onProjectDelete(f: (pid: string) => void) { projectDeleteListener = f; }
/** Replace state from the server without re-triggering a push. */
export function replaceProjects(projects: Project[]) {
  state = { projects: projects.map(normalize) };
  persist();
  subs.forEach((f) => f());
}
export function logActivity(p: Project, who: string, what: string, kind: Activity["kind"]): Project {
  return { ...p, activity: [{ id: uid("pa-"), at: nowISO(), who, what, kind }, ...p.activity].slice(0, 60) };
}

/* ------------------------------------------------------------ selectors -- */
/** A sheet with no issue on record (a half-finished import, a merge gap) reads
    as an empty placeholder — sixty call sites read `.rev` without a check. */
const NO_REVISION = { rev: "—", issuedAt: "", by: "" } as unknown as DrawingRevision;
export function currentRev(s: DrawingSet): DrawingRevision {
  return s.revisions?.[s.revisions.length - 1] ?? NO_REVISION;
}
/** Adapter: a DrawingSet at a given revision, shaped like a TenderDoc so the
    sheet renderer and viewer work unchanged. */
export function setAsDoc(s: DrawingSet, rev?: string) {
  const r = rev ? s.revisions.find((x) => x.rev === rev)! : currentRev(s);
  return { id: s.id, number: s.number, title: s.title, discipline: s.discipline, revision: r.rev, category: s.category, size: r.size ?? "—", issuedAt: r.issuedAt, previewId: r.previewId, fileId: r.fileId, fileName: r.fileName, filePage: r.filePage, pageFileId: r.pageFileId, obsolete: !!s.obsolete };
}
/** One sheet, one number — however the title block was scanned. "A-1.1.3",
    "A.1.1.3" and "a 1 1 3" are the same sheet; the register matches on this. */
export function sheetKey(number: string): string {
  return number.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
/** Sheets that are the same number under different punctuation — each group
    is one drawing registered more than once, its history split between them. */
export function duplicateSheetGroups(p: Pick<Project, "drawings">): DrawingSet[][] {
  const by = new Map<string, DrawingSet[]>();
  for (const d of p.drawings) { const k = sheetKey(d.number); if (!by.has(k)) by.set(k, []); by.get(k)!.push(d); }
  return [...by.values()].filter((g) => g.length > 1).map((g) => [...g].sort((a, b) => b.revisions.length - a.revisions.length || (currentRev(b).issuedAt > currentRev(a).issuedAt ? 1 : -1)));
}
export function nextRev(rev: string): string {
  const m = rev.match(/^(.*?)(\d+)$/);
  if (m) return m[1] + (parseInt(m[2], 10) + 1);
  if (/^[A-Y]$/i.test(rev)) return String.fromCharCode(rev.charCodeAt(0) + 1);
  return rev + ".1";
}
/** A row that is work, not a material need date. Every programme measure
    reads through this so a "steel on site by…" line never counts as an
    activity that is 0% done and behind. */
export function isActivity(t: Pick<GanttTask, "kind">): boolean { return t.kind !== "procurement"; }
export function programmeTasks(p: Pick<Project, "tasks">): GanttTask[] { return (p.tasks ?? []).filter(isActivity); }
export function procurementItems(p: Pick<Project, "tasks">): GanttTask[] { return (p.tasks ?? []).filter((t) => t.kind === "procurement"); }

/** Actual against planned, in days. Positive = late. */
export function taskVariance(t: Pick<GanttTask, "start" | "end" | "actualStart" | "actualFinish" | "progress">, now = Date.now()) {
  const DAY = 86400000;
  const d = (a?: string, b?: string) => (a && b ? Math.round((+new Date(a + "T12:00:00") - +new Date(b + "T12:00:00")) / DAY) : null);
  const startVar = d(t.actualStart, t.start);
  const finishVar = d(t.actualFinish, t.end);
  // not finished and past the planned end: the slip is already at least this big
  const runningLate = !t.actualFinish && t.progress < 100 && +new Date(t.end + "T12:00:00") < now ? Math.round((now - +new Date(t.end + "T12:00:00")) / DAY) : null;
  return { startVar, finishVar, runningLate };
}

export interface ProcurementImpact {
  item: GanttTask;
  requiredBy: string;
  status: "to-order" | "ordered" | "delivered";
  /** Activities that wait on this item, each with the days between the need date and its planned start (negative = the item is needed after the activity already started). */
  affected: { task: GanttTask; slackDays: number; late: boolean }[];
  /** Activities in the same phase starting soon after the need date that are not linked yet. */
  suggested: GanttTask[];
  /** Days until it is needed; negative = overdue. */
  daysToNeed: number;
  risk: "delivered" | "overdue" | "critical" | "watch" | "ok";
}
/** What a material need date holds up, and how close it is. */
export function procurementImpact(p: Project, item: GanttTask, now = Date.now()): ProcurementImpact {
  const DAY = 86400000;
  const requiredBy = item.end || item.start;
  const need = +new Date(requiredBy + "T12:00:00");
  const status = item.procurement?.status ?? "to-order";
  const linked = new Set(item.feeds ?? []);
  const acts = programmeTasks(p);
  const affected = acts.filter((t) => linked.has(t.id)).map((t) => {
    const slackDays = Math.round((+new Date(t.start + "T12:00:00") - need) / DAY);
    return { task: t, slackDays, late: status !== "delivered" && (need < now || slackDays < 0) };
  }).sort((a, b) => a.slackDays - b.slackDays);
  const suggested = acts.filter((t) => !linked.has(t.id) && t.progress < 100 && t.phase === item.phase && +new Date(t.start + "T12:00:00") >= need - 3 * DAY && +new Date(t.start + "T12:00:00") <= need + 21 * DAY).slice(0, 6);
  const daysToNeed = Math.round((need - now) / DAY);
  const risk: ProcurementImpact["risk"] = status === "delivered" ? "delivered" : daysToNeed < 0 ? "overdue" : status === "to-order" && daysToNeed <= 14 ? "critical" : daysToNeed <= 21 ? "watch" : "ok";
  return { item, requiredBy, status, affected, suggested, daysToNeed, risk };
}

export function overallProgress(p: Project): number {
  const tasks = programmeTasks(p);
  if (!tasks.length) return 0;
  const weighted = tasks.reduce((s, t) => s + t.progress * (+new Date(t.end) - +new Date(t.start)), 0);
  const total = tasks.reduce((s, t) => s + (+new Date(t.end) - +new Date(t.start)), 0);
  return total ? Math.round(weighted / total) : 0;
}
export function behindTasks(p: Project, now = Date.now()): GanttTask[] {
  return programmeTasks(p).filter((t) => {
    const start = +new Date(t.start), end = +new Date(t.end);
    if (now <= start) return false;
    const expected = Math.min(1, (now - start) / Math.max(1, end - start)) * 100;
    return t.progress < expected - 10 && t.progress < 100;
  });
}
/** Activities running right now — started, not finished, within their window. */
export function tasksInProgress(p: Project, now = Date.now()): GanttTask[] {
  return programmeTasks(p)
    .filter((t) => !t.milestone && +new Date(t.start) <= now && t.progress < 100 && +new Date(t.end) >= now - 3 * 86400000)
    .sort((a, b) => +new Date(a.end) - +new Date(b.end));
}
/** Activities due to start soon (next `days` days), not yet begun. */
export function tasksUpcoming(p: Project, days = 21, now = Date.now()): GanttTask[] {
  const horizon = now + days * 86400000;
  return programmeTasks(p)
    .filter((t) => t.progress === 0 && +new Date(t.start) > now && +new Date(t.start) <= horizon)
    .sort((a, b) => +new Date(a.start) - +new Date(b.start));
}

export interface ProgrammeInsight { kind: "blocked" | "conflict" | "query" | "snag" | "risk" | "behind" | "procurement"; taskId: string; task: string; detail: string; }
/** Smart scan of the programme: blocked activities, dependency conflicts
    (a task scheduled to start before a predecessor finishes), open queries /
    snags / risks, and activities behind programme. Drives the attention band. */
export function programmeInsights(p: Project, now = Date.now()): ProgrammeInsight[] {
  const out: ProgrammeInsight[] = [];
  const byId = new Map(p.tasks.map((t) => [t.id, t] as const));
  const behind = new Set(behindTasks(p, now).map((t) => t.id));
  // a material need date that is overdue, or close with nothing ordered, and the work waiting on it
  for (const item of procurementItems(p)) {
    const im = procurementImpact(p, item, now);
    if (im.risk === "overdue" || im.risk === "critical") {
      const waits = im.affected.length ? ` — ${im.affected.length} activit${im.affected.length === 1 ? "y waits" : "ies wait"} on it` : "";
      out.push({ kind: "procurement", taskId: item.id, task: item.name, detail: im.risk === "overdue" ? `Required by ${im.requiredBy}, not delivered${waits}` : `Not ordered; required in ${im.daysToNeed} day${im.daysToNeed === 1 ? "" : "s"}${waits}` });
    }
  }
  for (const t of programmeTasks(p)) {
    if (t.blocked) out.push({ kind: "blocked", taskId: t.id, task: t.name, detail: t.blockReason || "Work is blocked" });
    for (const depId of t.dependsOn ?? []) {
      const dep = byId.get(depId);
      if (dep && +new Date(t.start) < +new Date(dep.end)) out.push({ kind: "conflict", taskId: t.id, task: t.name, detail: `Starts before "${dep.name}" finishes` });
    }
    for (const n of t.notes ?? []) {
      if (n.resolved || n.kind === "comment") continue;
      out.push({ kind: n.kind, taskId: t.id, task: t.name, detail: n.text });
    }
    if (behind.has(t.id) && !t.blocked) out.push({ kind: "behind", taskId: t.id, task: t.name, detail: `${Math.round(t.progress)}% done, behind the plan` });
  }
  return out;
}

/** Materials and RFIs / drawing requests linked to a programme activity. */
/* Indexes keyed on the list itself. Project updates always replace a list
   rather than mutate it, so a cached index is valid exactly as long as the
   array it came from. These turn the per-activity scans (every task × every
   task, material and RFI) from quadratic into lookups — a 400-line programme
   made the dashboard, look-ahead and forecast stutter on every render. */
const byIdIdx = new WeakMap<object, Map<string, GanttTask>>();
function tasksById(tasks: GanttTask[]): Map<string, GanttTask> {
  let m = byIdIdx.get(tasks);
  if (!m) { m = new Map(tasks.map((t) => [t.id, t] as const)); byIdIdx.set(tasks, m); }
  return m;
}
const byTaskIdx = new WeakMap<object, Map<string, unknown[]>>();
function groupByTask<T extends { taskId?: string }>(list: T[]): Map<string, T[]> {
  let m = byTaskIdx.get(list) as Map<string, T[]> | undefined;
  if (!m) {
    m = new Map();
    for (const x of list) if (x.taskId) { const a = m.get(x.taskId); if (a) a.push(x); else m.set(x.taskId, [x]); }
    byTaskIdx.set(list, m as Map<string, unknown[]>);
  }
  return m;
}
export function taskRequirements(p: Project, taskId: string) {
  return {
    materials: groupByTask(p.materials ?? []).get(taskId) ?? [],
    rfis: groupByTask(p.rfis ?? []).get(taskId) ?? [],
  };
}

export interface TaskReadiness {
  predecessors: { done: number; total: number };
  drawings: { open: number; total: number };     // linked drawing/info RFIs still open
  materials: { late: number; pending: number; total: number };
  openQueries: number;
  blocked: boolean;
  ready: boolean;        // everything the activity needs is in place
  atRisk: boolean;       // not started and something won't be ready in time
}

/** Is a scheduled activity ready to run — predecessors complete, drawings
    issued, materials on site, no open queries or blocks? The heart of the
    look-ahead: it warns before site does. */
export function taskReadiness(p: Project, task: GanttTask, now = Date.now()): TaskReadiness {
  const DAY = 86400000;
  const byId = tasksById(p.tasks);
  const preds = (task.dependsOn ?? []).map((id) => byId.get(id)).filter((x): x is GanttTask => !!x);
  const predsDone = preds.filter((d) => d.progress >= 100).length;
  const { materials, rfis } = taskRequirements(p, task.id);
  const openRfis = rfis.filter((r) => r.status === "open");
  const start = +new Date(task.start);
  const matLate = materials.filter((m) => m.status !== "delivered" && (
    (m.shipment?.eta ? +new Date(m.shipment.eta) > start : +new Date(m.neededBy) > start) ||
    (m.status === "requested" && !!m.leadDays && now + m.leadDays * DAY > start)
  ));
  const matPending = materials.filter((m) => m.status !== "delivered");
  const openQueries = (task.notes ?? []).filter((n) => n.kind !== "comment" && !n.resolved).length;
  const blocked = !!task.blocked;
  const notStarted = task.progress === 0;
  const ready = predsDone === preds.length && openRfis.length === 0 && matPending.length === 0 && !blocked;
  const atRisk = !blocked && notStarted && (matLate.length > 0 || openRfis.length > 0 || predsDone < preds.length);
  return { predecessors: { done: predsDone, total: preds.length }, drawings: { open: openRfis.length, total: rfis.length }, materials: { late: matLate.length, pending: matPending.length, total: materials.length }, openQueries, blocked, ready, atRisk };
}

export interface LookAheadRow { task: GanttTask; readiness: TaskReadiness }
/** Activities in the next `weeks`, each with its readiness — the look-ahead. */
export function lookAhead(p: Project, weeks = 6, now = Date.now()): LookAheadRow[] {
  const horizon = now + weeks * 7 * 86400000;
  return programmeTasks(p)
    .filter((t) => !t.milestone && t.progress < 100 && +new Date(t.start) <= horizon && +new Date(t.end) >= now - 7 * 86400000)
    .sort((a, b) => +new Date(a.start) - +new Date(b.start))
    .map((t) => ({ task: t, readiness: taskReadiness(p, t, now) }));
}

export interface ProgrammeAction { taskId: string; task: string; kind: TaskNote["kind"]; detail: string; at: string }
/** Programme items waiting on the CONTRACTOR — studio-raised queries/snags/risks
    with the ball on their side. Drives the contractor dashboard. */
export function contractorProgrammeActions(p: Project): ProgrammeAction[] {
  const out: ProgrammeAction[] = [];
  for (const t of p.tasks)
    for (const n of t.notes ?? [])
      if (!n.resolved && n.ball === "contractor" && n.kind !== "comment")
        out.push({ taskId: t.id, task: t.name, kind: n.kind, detail: n.text, at: n.at });
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

/** A meeting point still needing attention — unresolved, and either never
    discussed or carrying an incomplete action. These roll into the next meeting. */
export function isOpenPoint(a: AgendaItem): boolean {
  return !a.resolved && (a.status === "pending" || a.actions.some((x) => !x.done));
}
/** Open points to carry forward — from held meetings (or already-carried ones). */
export function openMeetingPoints(p: Project): { meeting: Meeting; item: AgendaItem }[] {
  const out: { meeting: Meeting; item: AgendaItem }[] = [];
  for (const m of p.meetings) for (const a of m.agenda) if (isOpenPoint(a) && (m.status === "held" || a.carried)) out.push({ meeting: m, item: a });
  return out;
}
/** Deterministic minutes narrative from the recorded points — the fallback when
    AI is off, and the base the AI improves on. */
export function draftMinutesText(m: Meeting): string {
  return m.agenda.map((a, i) => {
    const acts = a.actions.map((x) => `   • Action: ${x.text} — ${x.assignee}${x.due ? `, due ${x.due}` : ""}${x.done ? " ✓" : ""}`).join("\n");
    const body = a.outcome ? a.outcome : a.status === "pending" ? "Not reached." : "Discussed; no outcome recorded.";
    return `${i + 1}. ${a.topic}${a.instruction ? ` [Instruction ${a.instructionRef ?? ""}]` : ""}\n   ${body}${acts ? "\n" + acts : ""}`;
  }).join("\n\n");
}

/** Every project this contractor/team member belongs to — matched by name +
    company across the contractor seat and team seats. Powers the multi-project
    switcher so someone on several jobs can hop between them from one link. */
/** The access code a contractor company already holds on another project, if
    any — so appointing the same firm again reuses ONE login across all their
    jobs (the switcher then lists every project the code opens). */
export function existingContractorCode(company: string, projects: Project[] = getProjects()): string | undefined {
  const co = company.trim().toLowerCase();
  if (!co || co === "to be appointed") return undefined;
  for (const p of projects) {
    if (p.contractor.company.trim().toLowerCase() === co && p.contractor.accessCode) return p.contractor.accessCode;
    const seat = (p.team ?? []).find((m) => m.role === "contractor" && m.company.trim().toLowerCase() === co && m.accessCode);
    if (seat) return seat.accessCode;
  }
  return undefined;
}

export function contractorProjects(session: { role?: string; name?: string; company?: string } | null | undefined, projects: Project[]): Project[] {
  if (!session || session.role !== "contractor") return [];
  const nm = (session.name ?? "").trim().toLowerCase();
  const co = (session.company ?? "").trim().toLowerCase();
  if (!co) return [];
  const hit = (n?: string, c?: string) => (n ?? "").trim().toLowerCase() === nm && (c ?? "").trim().toLowerCase() === co;
  return projects.filter((p) =>
    hit(p.contractor.name, p.contractor.company) ||
    (p.team ?? []).some((m) => m.role !== "client" && hit(m.name, m.company)),
  );
}

/** Every project where this signed-in CLIENT belongs — the main client
    contact or a client-role seat, matched by name+company exactly like the
    contractor selector above. One passcode, every job; the portal switcher
    and the route guard both read this, so what can be listed can be opened. */
export function clientProjects(session: { role?: string; name?: string; company?: string } | null | undefined, projects: Project[]): Project[] {
  if (!session || session.role !== "pclient") return [];
  const nm = (session.name ?? "").trim().toLowerCase();
  const co = (session.company ?? "").trim().toLowerCase();
  if (!nm && !co) return [];
  const hit = (n?: string, c?: string) =>
    (n ?? "").trim().toLowerCase() === nm && (c ?? "").trim().toLowerCase() === co;
  return projects.filter((p) =>
    hit(p.client.name, p.client.company) ||
    (p.team ?? []).some((m) => m.role === "client" && hit(m.name, m.company)),
  );
}

export interface ProgrammeAnalytics {
  actual: number;          // time-weighted % complete
  planned: number;         // % that should be complete by now
  variance: number;        // actual - planned (negative = behind)
  spi: number;             // schedule performance index (actual/planned)
  forecastEnd: string;     // projected completion at the current pace (ISO date)
  targetEnd: string;
  forecastDeltaDays: number; // + = late vs target
  phases: { phase: string; progress: number; weight: number }[];
  curve: { t: number; planned: number }[];  // planned S-curve, 0..1 x, 0..100 y
  todayX: number;          // 0..1 position of "now" on the curve
  blocked: number;
  atRisk: number;
}

/** Rich programme analytics for the dashboard hero: planned-vs-actual, an S-curve,
    a pace-based completion forecast, a phase breakdown and risk counts. */
/* Ten pages call this on every render, often several times. The answer only
   changes when the project changes, or as the clock moves — so it is kept per
   project object for a minute. */
const analyticsCache = new WeakMap<Project, { bucket: number; out: ProgrammeAnalytics | null }>();
export function programmeAnalytics(p: Project, now = Date.now()): ProgrammeAnalytics | null {
  const bucket = Math.floor(now / 60_000);
  const hit = analyticsCache.get(p);
  if (hit && hit.bucket === bucket) return hit.out;
  const out = computeProgrammeAnalytics(p, now);
  analyticsCache.set(p, { bucket, out });
  return out;
}
function computeProgrammeAnalytics(p: Project, now: number): ProgrammeAnalytics | null {
  const DAY = 86400000;
  // dates parsed once per activity, not once per use (the curve alone used 24×n parses)
  const parsed = programmeTasks(p)
    .filter((t) => !t.milestone)
    .map((t) => ({ t, s: +new Date(t.start), e: +new Date(t.end) }))
    .filter((x) => !isNaN(x.s) && !isNaN(x.e) && x.e > x.s);
  if (!parsed.length) return null;
  const tasks = parsed.map((x) => x.t);
  const times = new Map(parsed.map((x) => [x.t, x] as const));
  const dur = (t: GanttTask) => { const x = times.get(t)!; return Math.max(1, (x.e - x.s) / DAY); };
  const expectedAt = (t: GanttTask, at: number) => { const x = times.get(t)!; return Math.max(0, Math.min(1, (at - x.s) / (x.e - x.s))) * 100; };
  const totalW = tasks.reduce((s, t) => s + dur(t), 0);
  const actual = tasks.reduce((s, t) => s + dur(t) * t.progress, 0) / totalW;
  const planned = tasks.reduce((s, t) => s + dur(t) * expectedAt(t, now), 0) / totalW;
  const variance = actual - planned;
  const spi = planned > 0 ? actual / planned : 1;

  // reduce, not Math.min(...spread) — the spread overflows the stack on very long lists
  const start = parsed.reduce((m, x) => Math.min(m, x.s), Infinity);
  const latestEnd = parsed.reduce((m, x) => Math.max(m, x.e), -Infinity);
  const targetEndMs = p.targetCompletion && !isNaN(+new Date(p.targetCompletion)) ? +new Date(p.targetCompletion) : latestEnd;

  const targetDur = Math.max(1, targetEndMs - start);
  const forecastMs = start + Math.min(targetDur / Math.max(spi, 0.15), targetDur * 3);
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

  const phaseMap = new Map<string, { w: number; wp: number }>();
  for (const t of tasks) {
    const k = t.phase || "General";
    const e = phaseMap.get(k) ?? { w: 0, wp: 0 };
    e.w += dur(t); e.wp += dur(t) * t.progress;
    phaseMap.set(k, e);
  }
  const phases = [...phaseMap.entries()].map(([phase, e]) => ({ phase, progress: e.wp / e.w, weight: e.w })).sort((a, b) => b.weight - a.weight);

  const curveEnd = Math.max(targetEndMs, forecastMs);
  const span = Math.max(1, curveEnd - start);
  const N = 24;
  const curve = Array.from({ length: N + 1 }, (_, i) => {
    const at = start + (span * i) / N;
    const planAt = tasks.reduce((s, t) => s + dur(t) * expectedAt(t, at), 0) / totalW;
    return { t: i / N, planned: planAt };
  });
  const todayX = Math.max(0, Math.min(1, (now - start) / span));

  const blocked = tasks.filter((t) => t.blocked).length;
  const atRisk = tasks.filter((t) => taskReadiness(p, t, now).atRisk).length;

  return { actual, planned, variance, spi, forecastEnd: iso(forecastMs), targetEnd: iso(targetEndMs), forecastDeltaDays: Math.round((forecastMs - targetEndMs) / DAY), phases, curve, todayX, blocked, atRisk };
}

/** Programme activities live during a day (or the week beginning Monday) that
    haven't yet been adopted as a target for that period — the "set targets from
    the programme" suggestions. */
export function programmeTargetsFor(p: Project, dateKey: string, scope: "day" | "week"): GanttTask[] {
  const dayMs = +new Date(dateKey + "T12:00:00");
  const from = scope === "week" ? +new Date(weekKey(dateKey) + "T00:00:00") : dayMs;
  const to = scope === "week" ? from + 7 * 86400000 : dayMs + 86400000;
  const taken = new Set(p.targets.filter((t) => (t.scope ?? "day") === scope && (scope === "week" ? weekKey(t.date) === weekKey(dateKey) : t.date === dateKey) && t.taskId).map((t) => t.taskId));
  return programmeTasks(p).filter((t) =>
    !t.milestone && t.progress < 100 && !taken.has(t.id) &&
    +new Date(t.start) < to && +new Date(t.end) >= from,
  ).sort((a, b) => +new Date(a.end) - +new Date(b.end));
}

/** Discipline scope for the signed-in member — a subcontractor sees only their
    trades; the main contractor / studio sees all (null). */
export function drawingDisciplineScope(p: Project, session: { role?: string; name?: string; company?: string } | null | undefined): string[] | null {
  if (!session || session.role !== "contractor") return null;
  const nm = (session.name ?? "").trim().toLowerCase(), co = (session.company ?? "").trim().toLowerCase();
  const seat = p.team.find((m) => m.name?.trim().toLowerCase() === nm && m.company?.trim().toLowerCase() === co);
  const ds = seat?.drawingDisciplines;
  return ds && ds.length ? ds : null;
}

/** Does a programme line read as "materials required by…" rather than work?
    Chinese and English, the phrasings contractors actually use. */
export function looksLikeProcurement(name: string): boolean {
  const s = name.toLowerCase();
  return /(material|materials|supply|supplied|deliver|delivery|delivered|procure|procurement|purchase|order\b|ordered|on site by|to site|arrival|arrive|lead[- ]time|required by|req'?d by)/.test(s)
    || /(材料|采购|供应|到场|进场|送达|运抵|订购|采买|到货|进料|供货)/.test(name);
}

export interface SubScheduleRow {
  task: import("./types").SubTask;
  state: "done" | "blocked" | "behind" | "active" | "planned";
  /** The main activity it waits on, when that is not finished. */
  waitingOn?: GanttTask;
  variance: ReturnType<typeof taskVariance>;
}
export interface SubScheduleAnalysis {
  scope: GanttTask[];
  /** Scope activities that are blocked, behind or not ready. */
  affected: { task: GanttTask; why: string }[];
  /** Blocking activities outside the scope that are not finished. */
  blockers: { task: GanttTask; done: boolean; late: boolean }[];
  rows: SubScheduleRow[];
  progress: number;
}
/** A package's position: its scope on the main programme, what holds it up,
    and the state of its own schedule line by line. */
export function subScheduleAnalysis(p: Project, pkg: import("./types").TradePackage, now = Date.now()): SubScheduleAnalysis {
  const byId = new Map(p.tasks.map((t) => [t.id, t] as const));
  const behind = new Set(behindTasks(p, now).map((t) => t.id));
  const scope = (pkg.taskIds ?? []).map((id) => byId.get(id)).filter((t): t is GanttTask => !!t);
  const affected = scope.flatMap((t) => {
    if (t.blocked) return [{ task: t, why: t.blockReason ? `Blocked — ${t.blockReason}` : "Blocked" }];
    if (behind.has(t.id)) return [{ task: t, why: `${Math.round(t.progress)}% done, behind the plan` }];
    const r = taskReadiness(p, t, now);
    if (r.atRisk) return [{ task: t, why: r.materials.late ? "Materials will not be on site in time" : r.drawings.open ? "Drawings / information outstanding" : "Predecessors not finished" }];
    return [];
  });
  const blockers = (pkg.blockedByTaskIds ?? []).map((id) => byId.get(id)).filter((t): t is GanttTask => !!t)
    .map((t) => ({ task: t, done: t.progress >= 100, late: t.progress < 100 && +new Date(t.end) < now }));
  const rows: SubScheduleRow[] = (pkg.schedule ?? []).map((s) => {
    const after = s.afterTaskId ? byId.get(s.afterTaskId) : undefined;
    const waitingOn = after && after.progress < 100 && +new Date(s.start) <= now + 7 * 86400000 ? after : undefined;
    const start = +new Date(s.start), end = +new Date(s.end);
    const expected = now <= start ? 0 : Math.min(1, (now - start) / Math.max(1, end - start)) * 100;
    const state: SubScheduleRow["state"] = s.progress >= 100 ? "done" : waitingOn ? "blocked" : s.progress < expected - 10 && now > start ? "behind" : s.progress > 0 ? "active" : "planned";
    return { task: s, state, waitingOn, variance: taskVariance(s, now) };
  });
  const sched = pkg.schedule ?? [];
  const w = (s: import("./types").SubTask) => Math.max(1, +new Date(s.end) - +new Date(s.start));
  const progress = sched.length ? Math.round(sched.reduce((a, s) => a + s.progress * w(s), 0) / sched.reduce((a, s) => a + w(s), 0)) : 0;
  return { scope, affected, blockers, rows, progress };
}

export function openPmRfis(p: Project) { return p.rfis.filter((r) => r.status === "open"); }
export function isRfiOverdue(r: PmRfi, now = Date.now()) {
  return r.status === "open" && !!r.dueDate && now > +new Date(r.dueDate) + 86400000;
}
/** Whole days from submission to the answer (or to now while still open). */
export function rfiTurnaroundDays(r: PmRfi, now = Date.now()): number {
  const end = r.answeredAt ? +new Date(r.answeredAt) : now;
  return Math.max(0, Math.round((end - +new Date(r.askedAt)) / 86_400_000));
}
/** Days the answer landed after the required-by date; null when on time or no date. */
export function rfiAnsweredLateDays(r: PmRfi): number | null {
  if (!r.answeredAt || !r.dueDate) return null;
  const late = Math.floor((+new Date(r.answeredAt) - (+new Date(r.dueDate) + 86_400_000)) / 86_400_000) + 1;
  return late > 0 ? late : null;
}
/** The register's turnaround picture. */
export function rfiStats(p: Project, now = Date.now()) {
  const rfis = p.rfis ?? [];
  const open = rfis.filter((r) => r.status === "open");
  const answered = rfis.filter((r) => r.status === "answered" && r.answeredAt);
  const overdue = open.filter((r) => isRfiOverdue(r, now));
  const dueSoon = open.filter((r) => !isRfiOverdue(r, now) && r.dueDate && +new Date(r.dueDate) - now < 3 * 86_400_000);
  const turn = answered.map((r) => rfiTurnaroundDays(r));
  const avg = turn.length ? Math.round((turn.reduce((s, n) => s + n, 0) / turn.length) * 10) / 10 : null;
  const late = answered.filter((r) => rfiAnsweredLateDays(r) !== null).length;
  const oldestOpenDays = open.length ? Math.max(...open.map((r) => rfiTurnaroundDays(r, now))) : 0;
  return { total: rfis.length, open: open.length, overdue: overdue.length, dueSoon: dueSoon.length, answered: answered.length, avgTurnaround: avg, answeredLate: late, oldestOpenDays };
}
export function todayKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ---- what a complete daily report looks like -----------------------------
   A report is only worth reading if the fields that carry meaning are filled,
   and on a busy site that only happens if the form asks properly. So the studio
   declares what this project insists on, and the composer walks the crew
   through it rather than handing them a blank page and hoping. */

/** The vantage points the daily report asks for. A project that has never set
    any gets the four elevations, which is the shape almost every job wants. */
export const DEFAULT_VIEWPOINTS: SiteViewpoint[] = [
  { id: "vp-n", name: "North elevation", guide: "Whole face in frame, from the same standing point each day", active: true },
  { id: "vp-e", name: "East elevation", guide: "Whole face in frame", active: true },
  { id: "vp-s", name: "South elevation", guide: "Whole face in frame", active: true },
  { id: "vp-w", name: "West elevation", guide: "Whole face in frame", active: true },
];

export function activeViewpoints(p: Pick<Project, "viewpoints">): SiteViewpoint[] {
  const set = p.viewpoints ?? DEFAULT_VIEWPOINTS;
  return set.filter((v) => v.active !== false);
}

/** Nothing is required unless the studio switched it on, so no project starts
    refusing reports it accepted yesterday. */
export function reportRules(p: Pick<Project, "reportRules">): DailyReportRules {
  return p.reportRules ?? {};
}

/** What a draft is still missing. Empty means it can be filed.
    Each entry names the step so the composer can send the crew straight to it. */
export interface ReportGap { step: string; label: string; detail?: string }

export function reportGaps(
  p: Pick<Project, "reportRules" | "viewpoints">,
  draft: {
    crew?: { trade: string; count: number | string }[];
    activities?: { text: string }[];
    areas?: { name: string; status?: string; photoIds?: string[] }[];
    nextTargets?: string[];
    roster?: { name: string }[];
    equipment?: string;
    viewpointShots?: Record<string, number>;   // viewpoint id → photos taken
  },
): ReportGap[] {
  const r = reportRules(p);
  const gaps: ReportGap[] = [];

  if (r.crew && !(draft.crew ?? []).some((c) => c.trade.trim() && Number(c.count) > 0)) {
    gaps.push({ step: "crew", label: "Who was on site", detail: "Add at least one trade with a head count." });
  }
  if (r.activities && !(draft.activities ?? []).some((a) => a.text.trim())) {
    gaps.push({ step: "work", label: "What was done", detail: "Add at least one activity line." });
  }
  if (r.workAreas) {
    const areas = (draft.areas ?? []).filter((a) => a.name.trim());
    if (!areas.length) {
      gaps.push({ step: "areas", label: "Work areas", detail: "Name at least one area worked today." });
    } else if (areas.some((a) => !a.status?.trim())) {
      gaps.push({ step: "areas", label: "Work areas", detail: "Every area needs its state — “cast, curing”, “blockwork to sill”." });
    }
  }
  if (r.areaPhotos) {
    const bare = (draft.areas ?? []).filter((a) => a.name.trim() && !(a.photoIds ?? []).length);
    if (bare.length) {
      gaps.push({
        step: "areas", label: "Photographs of each area",
        detail: `${bare.length === 1 ? "One area has" : `${bare.length} areas have`} no photograph: ${bare.map((a) => a.name.trim()).join(", ")}.`,
      });
    }
  }
  if (r.viewpointPhotos) {
    const missing = activeViewpoints(p).filter((v) => !(draft.viewpointShots?.[v.id] ?? 0));
    if (missing.length) {
      gaps.push({
        step: "progress", label: "Site progression photographs",
        detail: `Still to photograph: ${missing.map((v) => v.name).join(", ")}.`,
      });
    }
  }
  if (r.nextTargets && !(draft.nextTargets ?? []).some((x) => x.trim())) {
    gaps.push({ step: "tomorrow", label: "Tomorrow's commitments", detail: "Say what the site will do next — this is what the day is measured against." });
  }
  if (r.roster && !(draft.roster ?? []).some((x) => x.name.trim())) {
    gaps.push({ step: "crew", label: "Named personnel", detail: "This project records who was on site by name." });
  }
  if (r.equipment && !draft.equipment?.trim()) {
    gaps.push({ step: "crew", label: "Plant & equipment", detail: "List the plant on site, or write “none”." });
  }
  return gaps;
}

/** One report per day, richest wins.

    Re-filing a day used to leave two rows for that date (the client dropped the
    old one and minted a new id; the server's union brought the old one back),
    and whichever sorted first won the display — often the thinner, earlier
    draft, which read as though the report had lost detail. `addLog` now re-uses
    the existing id so it can't happen again, but days already doubled up stay
    in the record, so collapse them on the way out. Nothing is deleted: the
    fuller entry is simply the one shown. */
export function oneLogPerDay(logs: DailyLog[]): DailyLog[] {
  const weight = (l: DailyLog) =>
    (l.crew?.length ?? 0) + (l.activities?.length ?? 0) + (l.workDone?.length ?? 0)
    + (l.photoIds?.length ?? 0) * 2 + (l.roster?.length ?? 0) + (l.areas?.length ?? 0)
    + (l.challenges?.length ?? 0) + (l.infoNeeded?.length ?? 0)
    + (l.acknowledged ? 1 : 0) + (l.delays ? 1 : 0) + (l.equipment ? 1 : 0);
  const best = new Map<string, DailyLog>();
  for (const l of logs) {
    const cur = best.get(l.date);
    if (!cur || weight(l) > weight(cur)) best.set(l.date, l);
  }
  return [...best.values()];
}

/* ---- the site's own clock ------------------------------------------------
   A daily report belongs to the day it happened ON SITE. Reading the date off
   the filer's device gets that wrong whenever the filer isn't standing in the
   project's timezone — an overseas crew whose phones are still on their home
   time files an afternoon report under tomorrow's date, and the day the studio
   is looking for reads as never reported. So resolve every project "today" in
   the site's zone. */

/** The site's IANA zone: explicit if set, otherwise a whole-hour guess from
    the site's longitude, otherwise the device. The longitude fallback is a
    rough approximation — good enough to keep a report on the right calendar
    day, and any project can pin the exact zone in Project Data. */
export function projectZone(p: Pick<Project, "timezone" | "geo">): string | undefined {
  if (p.timezone) return p.timezone;
  const lon = p.geo?.lon;
  if (typeof lon === "number" && isFinite(lon)) {
    // Etc/GMT signs are inverted from the usual offset notation
    const hours = Math.max(-12, Math.min(14, Math.round(lon / 15)));
    return hours === 0 ? "UTC" : `Etc/GMT${hours > 0 ? "-" : "+"}${Math.abs(hours)}`;
  }
  return undefined;
}

/** "YYYY-MM-DD" as it reads on site right now. */
export function projectToday(p: Pick<Project, "timezone" | "geo">, d = new Date()): string {
  const zone = projectZone(p);
  if (!zone) return todayKey(d);
  try {
    // en-CA formats as YYYY-MM-DD, which is exactly the key shape we store
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch {
    return todayKey(d);   // unknown zone — better the device than nothing
  }
}

/** How far the filer's own clock is from the site's, in whole days. 0 when
    they agree; ±1 when the device would date the report to the wrong day. */
export function siteDayOffset(p: Pick<Project, "timezone" | "geo">, d = new Date()): number {
  const site = projectToday(p, d), device = todayKey(d);
  if (site === device) return 0;
  return Math.round((+new Date(device + "T12:00:00") - +new Date(site + "T12:00:00")) / 86400000);
}
export function targetsFor(p: Project, dateKey: string) {
  return p.targets.filter((t) => t.date === dateKey && (t.scope ?? "day") === "day");
}

/** The Monday of the week a date falls in — weekly targets hang off it. */
export function weekKey(d: Date | string = new Date()): string {
  const date = typeof d === "string" ? new Date(d + "T12:00:00") : new Date(d);
  const day = (date.getDay() + 6) % 7;          // 0 = Monday
  date.setDate(date.getDate() - day);
  return todayKey(date);
}

export function weeklyTargetsFor(p: Project, monday: string) {
  return p.targets.filter((t) => t.date === monday && t.scope === "week");
}

/** Working days in the recent window (default 14) with no daily report filed —
    within the project's start→today bounds, Sundays excluded. Newest first.
    Drives the in-app "you have missed days" prompt and the email reminder. */
export function missedReportDays(p: Project, windowDays = 14): string[] {
  const today = todayKey();
  const start = (p.startDate ?? "").slice(0, 10);
  const logged = new Set(p.logs.map((l) => l.date));
  const out: string[] = [];
  const base = new Date(today + "T12:00:00");
  for (let i = 1; i <= windowDays; i++) {
    const d = new Date(base);
    d.setDate(d.getDate() - i);
    const key = todayKey(d);
    if (start && key < start) break;
    if (d.getDay() === 0) continue;          // skip Sundays
    if (!logged.has(key)) out.push(key);
  }
  return out;
}

/** How a week actually went: the weekly commitments plus every daily target
    inside it, so the review is one number rather than seven. */
export function weekReview(p: Project, monday: string) {
  const end = (() => { const d = new Date(monday + "T12:00:00"); d.setDate(d.getDate() + 6); return todayKey(d); })();
  const weekly = weeklyTargetsFor(p, monday);
  const daily = p.targets.filter((t) => (t.scope ?? "day") === "day" && t.date >= monday && t.date <= end);
  const all = [...weekly, ...daily];
  const done = all.filter((t) => t.done).length;
  return {
    monday, end, weekly, daily,
    total: all.length, done,
    rate: all.length ? Math.round((done / all.length) * 100) : 0,
    carried: daily.filter((t) => t.carried).length,
  };
}

/* --- day-on-day comparison ----------------------------------------------- */
/** What was promised for a day versus what the day actually produced, and
    what is promised for the next one. Sources in priority order: the previous
    report's `nextTargets` (the contractor's own commitment) and the project's
    daily targets for that date. A promise that nobody checks is just a wish,
    so the report checks it. */
export function dayComparison(p: Project, date: string) {
  const shift = (d: string, by: number) => {
    const x = new Date(d + "T12:00:00");
    x.setDate(x.getDate() + by);
    return todayKey(x);
  };
  const prevKey = shift(date, -1);
  const nextKey = shift(date, 1);
  const logFor = (d: string) => p.logs.find((l) => l.date === d);

  const prevLog = logFor(prevKey);
  const today = logFor(date);

  /** Everything that was committed for `date` before it started. */
  const promised = [
    ...(prevLog?.nextTargets ?? []),
    ...targetsFor(p, date).map((t) => t.text),
  ].filter((x, i, a) => a.indexOf(x) === i);

  /** Did the day's work cover a promise? Matched on significant words rather
      than exact text — nobody writes tomorrow's plan and today's record the
      same way twice. */
  const doneText = [
    ...(today?.activities ?? []).map((a) => a.text),
    ...(today?.workDone ?? []),
  ].join(" ").toLowerCase();

  const ticked = targetsFor(p, date).filter((t) => t.done).map((t) => t.text.toLowerCase());

  const assess = (promise: string) => {
    const low = promise.toLowerCase();
    if (ticked.some((t) => t === low)) return true;
    const words = low.split(/[^a-z0-9]+/).filter((w) => w.length > 4);
    if (!words.length) return false;
    const hits = words.filter((w) => doneText.includes(w)).length;
    return hits / words.length >= 0.5;
  };

  const met = promised.filter(assess);
  const missed = promised.filter((x) => !met.includes(x));

  return {
    prevKey, nextKey,
    promised, met, missed,
    rate: promised.length ? Math.round((met.length / promised.length) * 100) : null,
    /** What tomorrow is committed to — this report's own look-ahead. */
    next: [
      ...(today?.nextTargets ?? []),
      ...targetsFor(p, nextKey).map((t) => t.text),
    ].filter((x, i, a) => a.indexOf(x) === i),
    carriedOver: missed,   // what has to be picked up again
  };
}

/* --- critical path ------------------------------------------------------- */
/** Activities that drive the completion date: anything flagged critical, plus
    anything whose finish already lands on or past the target completion. */
export function criticalTasks(p: Project): GanttTask[] {
  return p.tasks.filter((t) => t.critical || (t.end >= p.targetCompletion && t.progress < 100));
}

export interface CriticalInfo { criticalIds: Set<string>; floatById: Map<string, number>; hasDeps: boolean }
/** Critical-path detection: total float per activity from its successors'
    scheduled starts (or the project finish when it has none). Zero-float
    activities drive the end date — the critical path. Uses the dependency
    graph where set; falls back to the schedule dates so it always highlights
    the end-driving chain. */
export function criticalPath(p: Project): CriticalInfo {
  const DAY = 86400000;
  const tasks = programmeTasks(p).filter((t) => !t.milestone && !isNaN(+new Date(t.start)) && !isNaN(+new Date(t.end)));
  const byId = new Map(tasks.map((t) => [t.id, t] as const));
  const succ = new Map<string, string[]>();
  let hasDeps = false;
  for (const t of tasks) for (const d of t.dependsOn ?? []) if (byId.has(d)) { succ.set(d, [...(succ.get(d) ?? []), t.id]); hasDeps = true; }
  const projectFinish = tasks.length ? Math.max(...tasks.map((t) => +new Date(t.end))) : 0;
  const floatById = new Map<string, number>();
  const criticalIds = new Set<string>();
  for (const t of tasks) {
    const ss = (succ.get(t.id) ?? []).map((id) => byId.get(id)!).filter(Boolean);
    const latestFinish = ss.length ? Math.min(...ss.map((s) => +new Date(s.start))) : projectFinish;
    const fl = Math.round((latestFinish - +new Date(t.end)) / DAY);
    floatById.set(t.id, fl);
    if (fl <= 1 || t.critical) criticalIds.add(t.id);
  }
  return { criticalIds, floatById, hasDeps };
}

/** A critical activity that is also behind — the only list worth waking up to. */
export function criticalAtRisk(p: Project, now = Date.now()): GanttTask[] {
  const behind = new Set(behindTasks(p, now).map((t) => t.id));
  return criticalTasks(p).filter((t) => behind.has(t.id) || (+new Date(t.end) < now && t.progress < 100));
}

/** Days a critical activity can slip before it moves the completion date. */
export function taskFloat(p: Project, t: GanttTask): number {
  if (t.floatDays !== undefined) return t.floatDays;
  return Math.floor((+new Date(p.targetCompletion) - +new Date(t.end)) / 86400000);
}
export const MATERIAL_FLOW: MaterialStatus[] = ["requested", "under-review", "approved", "ordered", "in-transit", "delivered"];

/** Workforce per day from the daily logs, most recent `days` calendar days. */
export function workforceSeries(p: Project, days = 14): { date: string; total: number; byTrade: CrewLine[] }[] {
  const map = new Map(oneLogPerDay(p.logs).map((l) => [l.date, l]));
  const out: { date: string; total: number; byTrade: CrewLine[] }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = todayKey(d);
    const log = map.get(key);
    out.push({ date: key, total: log ? log.crew.reduce((s, c) => s + c.count, 0) : 0, byTrade: log?.crew ?? [] });
  }
  return out;
}
export function openObservations(p: Project) { return p.observations.filter((o) => o.status === "open").length; }
/** Actions raised in meetings that are still open. */
export function openMeetingActions(p: Project): number {
  return p.meetings.reduce((s, m) => s + m.agenda.reduce((t, a) => t + a.actions.filter((x) => !x.done).length, 0), 0);
}

/* -------------------------------------------------------------- actions -- */
const PROJECT_HUES = [96, 200, 20, 260, 330, 150, 45];

/** Every access code in play — used to keep new codes unique so two people
    from the same company never share a login (a duplicate routes the second
    person into the first one's seat). */
function allAccessCodes(): Set<string> {
  const codes = new Set<string>();
  for (const p of state.projects) {
    codes.add(p.contractor.accessCode.toUpperCase());
    for (const m of p.team ?? []) codes.add(m.accessCode.toUpperCase());
  }
  try {
    for (const t of getTenders()) {
      codes.add(t.client.passcode.toUpperCase());
      for (const b of t.bidders) codes.add(b.accessCode.toUpperCase());
    }
  } catch { /* bid store not loaded */ }
  return codes;
}

/** Hard-to-guess access code: PREFIX-XXXXXXXX from an unambiguous alphabet
    (no 0/O/1/I), crypto-random, unique across every code in both stores.
    Company/fallback params remain for call-site compatibility — codes are no
    longer derived from names, which made them guessable. */
export function uniqueAccessCode(prefix: string, _company?: string, _fallback = "TEAM", extraTaken: string[] = []): string {
  const ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  const taken = allAccessCodes();
  for (const x of extraTaken) taken.add(x.toUpperCase());
  for (let attempt = 0; attempt < 20; attempt++) {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const code = `${prefix.toUpperCase()}-${[...bytes].map((b) => ALPHABET[b % ALPHABET.length]).join("")}`;
    if (!taken.has(code)) return code;
  }
  return `${prefix.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
}

export const pmActions = {
  /* --- new project ------------------------------------------------------ */
  createProject(data: {
    name: string; location: string; description?: string;
    clientName: string; clientCompany: string; clientEmail?: string;
    contractorName?: string; contractorCompany?: string; contractorEmail?: string;
    startDate: string; targetCompletion: string;
    contractSum?: number; currency?: string;
    siteAddress?: string; scope?: string; contractForm?: string;
    lat?: number; lon?: number;
  }): string {
    const codeNum = state.projects.length
      ? Math.max(...state.projects.map((p) => parseInt(p.code.split("-")[1], 10) || 0)) + 1
      : (new Date().getFullYear() % 100) * 100 + 1;
    const code = `PRJ-${codeNum}`;
    const prefix = String(codeNum).slice(0, 2);

    const contractorCompany = data.contractorCompany?.trim() || "To be appointed";
    const contractorCode = uniqueAccessCode(prefix, contractorCompany === "To be appointed" ? "SITE" : contractorCompany, "SITE");
    const team: TeamMember[] = [];
    if (data.contractorCompany?.trim()) {
      team.push({ id: uid("tm-"), name: data.contractorName?.trim() || contractorCompany, company: contractorCompany, email: data.contractorEmail?.trim() ?? "", role: "contractor", accessCode: contractorCode, invitedAt: nowISO(), status: "invited" });
    }
    if (data.clientEmail?.trim()) {
      // extraTaken keeps the client's code distinct even when both parties share a company name
      team.push({ id: uid("tm-"), name: data.clientName.trim(), company: data.clientCompany.trim(), email: data.clientEmail.trim(), role: "client", accessCode: uniqueAccessCode(prefix, data.clientCompany, "CLIENT", [contractorCode]), invitedAt: nowISO(), status: "invited" });
    }
    // remember both parties so the next project can pick them from the directory
    if (data.clientCompany?.trim()) peopleActions.remember({ name: data.clientName?.trim() || data.clientCompany, company: data.clientCompany, email: data.clientEmail, role: "client" });
    if (data.contractorCompany?.trim()) peopleActions.remember({ name: data.contractorName?.trim() || contractorCompany, company: contractorCompany, email: data.contractorEmail, role: "contractor" });

    const p: Project = normalize({
      id: uid("prj-"),
      code,
      name: data.name.trim(),
      location: data.location.trim(),
      geo: data.lat !== undefined && data.lon !== undefined ? { lat: data.lat, lon: data.lon } : undefined,
      details: {
        siteAddress: data.siteAddress?.trim() || undefined,
        scope: data.scope?.trim() || undefined,
        contractForm: data.contractForm || undefined,
      },
      description: data.description?.trim() || `${data.scope?.trim() || "Construction works"} at ${data.location.trim()}.`,
      hue: PROJECT_HUES[state.projects.length % PROJECT_HUES.length],
      currency: data.currency || "USD",
      contractSum: data.contractSum,
      client: { name: data.clientName.trim(), company: data.clientCompany.trim(), email: data.clientEmail?.trim() ?? "" },
      contractor: { name: data.contractorName?.trim() || contractorCompany, company: contractorCompany, email: data.contractorEmail?.trim() ?? "", accessCode: contractorCode },
      team,
      startDate: data.startDate,
      targetCompletion: data.targetCompletion,
      drawings: [], rfis: [], logs: [], observations: [], visits: [], meetings: [],
      tasks: [], targets: [], materials: [], punch: [], reports: [],
      activity: [{ id: uid("pa-"), at: nowISO(), who: STUDIO.manager, what: `created the project ${code}`, kind: "docs" }],
    });
    commit({ projects: [...state.projects, p] });
    projectListener?.(p);
    for (const m of team) {
      if (m.email) notify([m.email], `You've been invited to ${p.name} (${p.code})`,
        `${STUDIO.name} ${STUDIO.unit}: you've been added to ${p.name} as ${m.role}. Tap the link below to open your project directly — your access code is ${m.accessCode} if you ever need to sign in manually.`,
        { link: `${APP_URL}/join?c=${encodeURIComponent(m.accessCode)}`, label: "Open my project" });
    }
    return p.id;
  },

  /* --- drawings: upload with auto-revision --------------------------- */
  /** The studio's ask for the current work programme — synced, so the site
      sees the banner on their Work Program page and the bell carries it. */
  requestProgramme(pid: string, by: string) {
    patchProject(pid, (p) => logActivity({ ...p, programmeRequest: { at: nowISO(), by } }, by,
      "requested the current work programme from the contractor", "timeline"));
  },

  /** Morning client email on/off — every day the previous day's report goes
      to the client automatically unless the manager pauses it here. */
  setClientDailyEmail(pid: string, on: boolean) {
    patchProject(pid, (p) => logActivity({ ...p, clientDailyEmail: on }, STUDIO.manager,
      `${on ? "resumed" : "paused"} the morning client report email`, "timeline"));
  },

  /** Local mirror of the server's sent-mark so the row updates instantly
      after the share button — the synced copy carries the same stamp. */
  markLogEmailed(pid: string, logId: string, to: number) {
    patchProject(pid, (p) => ({
      ...p,
      logs: p.logs.map((l) => (l.id === logId ? { ...l, clientEmailed: { at: nowISO(), to } } : l)),
    }));
  },

  /** Heal pass after a set import: any sheet whose current revision recorded
      this source fileName but never got a stored copy (failed upload, or a
      re-scan that named its pages differently) adopts the fresh fileId. The
      revision history is untouched — only the missing pointer is filled in. */
  attachOriginals(pid: string, by: string, files: { fileName: string; fileId: string }[]) {
    if (!files.length) return;
    const byName = new Map(files.map((f) => [f.fileName, f.fileId]));
    patchProject(pid, (p) => {
      let healed = 0;
      const drawings = p.drawings.map((d) => {
        const cur = d.revisions[d.revisions.length - 1];
        const fid = !cur.fileId && cur.fileName ? byName.get(cur.fileName) : undefined;
        if (!fid) return d;
        healed++;
        return { ...d, revisions: d.revisions.map((r, i) => i === d.revisions.length - 1 ? { ...r, fileId: fid } : r) };
      });
      if (!healed) return p;
      return logActivity({ ...p, drawings }, by, `attached originals to ${healed} sheet${healed === 1 ? "" : "s"}`, "docs");
    });
  },

  /** Attach a sheet's own one-page PDF (split from the combined original)
      to one revision — downloads and phones then get the small file. */
  /** A preview drawn later from the original file, for the revisions that
      show that file (and page) but have no preview yet. */
  attachPreviewForFile(fileId: string, page: number, previewId: string) {
    const shows = (r: DrawingRevision) => !r.previewId && (r.pageFileId === fileId || (!r.pageFileId && r.fileId === fileId && (r.filePage ?? 1) === page));
    for (const p of state.projects) {
      if (!p.drawings.some((d) => d.revisions.some(shows))) continue;
      patchProject(p.id, (q) => ({
        ...q,
        drawings: q.drawings.map((d) => d.revisions.some(shows)
          ? { ...d, revisions: d.revisions.map((r) => (shows(r) ? { ...r, previewId } : r)) }
          : d),
      }));
    }
  },

  setRevisionPageFile(pid: string, setId: string, rev: string, pageFileId: string) {
    patchProject(pid, (p) => ({
      ...p,
      drawings: p.drawings.map((d) => d.id === setId
        ? { ...d, revisions: d.revisions.map((r) => (r.rev === rev ? { ...r, pageFileId } : r)) }
        : d),
    }));
  },

  /** The revision of a sheet that already carries this exact file, if any. */
  duplicateRevision(pid: string, number: string, sha: string | undefined): string | null {
    if (!sha) return null;
    const p = state.projects.find((x) => x.id === pid);
    const s = p?.drawings.find((d) => sheetKey(d.number) === sheetKey(number));
    return s?.revisions.find((r) => r.sha === sha)?.rev ?? null;
  },
  uploadDrawing(pid: string, by: string, data: { number: string; title: string; discipline: string; note?: string; size?: string; rev?: string; previewId?: string; fileId?: string; fileName?: string; filePage?: number; pageFileId?: string; sha?: string; sameAs?: string }): { action: "new" | "revised" | "reattached" | "duplicate"; rev: string } {
    let result: { action: "new" | "revised" | "reattached" | "duplicate"; rev: string } = { action: "new", rev: data.rev?.trim() || "P1" };
    patchProject(pid, (p) => {
      const num = data.number.trim().toUpperCase();
      // the same number however the title block was punctuated — never a second sheet
      const existing = p.drawings.find((d) => sheetKey(d.number) === sheetKey(num) && !d.archived)
        ?? p.drawings.find((d) => sheetKey(d.number) === sheetKey(num));
      // the very same file again — the original stays, nothing is re-issued
      const dup = existing && data.sha ? existing.revisions.find((r) => r.sha === data.sha) : undefined;
      if (existing && dup) {
        result = { action: "duplicate", rev: dup.rev };
        return logActivity(p, by, `re-uploaded ${num} — identical file to ${dup.rev}, original kept`, "docs");
      }
      // a re-export of the same drawing — nothing drawn differently — is not a new issue either
      const same = existing && data.sameAs ? existing.revisions.find((r) => r.rev === data.sameAs) : undefined;
      if (existing && same) {
        result = { action: "duplicate", rev: same.rev };
        return logActivity(p, by, `re-uploaded ${num} — same drawing as ${same.rev} (re-export, no changes), original kept`, "docs");
      }
      if (existing && data.rev?.trim().toUpperCase() === currentRev(existing).rev.toUpperCase()) {
        result = { action: "reattached", rev: currentRev(existing).rev };
        // same revision in the title block = the same issue re-uploaded —
        // attach the drawing/file to the current revision instead of minting a new one
        const next: Project = {
          ...p,
          drawings: p.drawings.map((d) => d.id === existing.id
            ? {
              ...d,
              title: data.title.trim() || d.title,
              revisions: d.revisions.map((r, i) => i === d.revisions.length - 1
                ? { ...r, size: data.size ?? r.size, previewId: data.previewId ?? r.previewId, fileId: data.fileId ?? r.fileId, fileName: data.fileName ?? r.fileName, filePage: data.filePage ?? r.filePage, pageFileId: data.fileId ? data.pageFileId : (data.pageFileId ?? r.pageFileId), sha: data.sha ?? r.sha }
                : r),
            }
            : d),
        };
        return logActivity(next, by, `re-attached ${num} at ${currentRev(existing).rev}`, "docs");
      }
      if (existing) {
        const rev = nextRev(currentRev(existing).rev);
        result = { action: "revised", rev };
        const revision: DrawingRevision = { rev, issuedAt: nowISO(), by, note: data.note, size: data.size, previewId: data.previewId, fileId: data.fileId, fileName: data.fileName, filePage: data.filePage, pageFileId: data.pageFileId, sha: data.sha };
        const next: Project = {
          ...p,
          drawings: p.drawings.map((d) => d.id === existing.id
            ? { ...d, title: data.title.trim() || d.title, revisions: [...d.revisions, revision] }
            : d),
        };
        return logActivity(next, by, `uploaded ${num} — auto-revisioned to ${rev}, ${currentRev(existing).rev} superseded`, "docs");
      }
      const firstRev = data.rev?.trim() || "P1"; // scanned title-block revision wins for brand-new sheets
      const set: DrawingSet = {
        id: uid("ds-"), number: num, title: data.title.trim(), discipline: data.discipline, category: "drawing",
        // new sheets nest under the project's phase — preliminary in the early
        // phases, formally issued once on site
        stage: p.phase === "preliminary" || p.phase === "bid" ? "preliminary" : "issued",
        revisions: [{ rev: firstRev, issuedAt: nowISO(), by, note: data.note, size: data.size, previewId: data.previewId, fileId: data.fileId, fileName: data.fileName, filePage: data.filePage, pageFileId: data.pageFileId, sha: data.sha }],
      };
      result = { action: "new", rev: firstRev };
      return logActivity({ ...p, drawings: [...p.drawings, set] }, by, `uploaded new drawing ${num} at ${firstRev}`, "docs");
    });
    return result;
  },

  /** Remove one SUPERSEDED (or wrongly issued current) revision from a sheet.
      The last remaining revision can never be removed — delete the sheet
      instead. Removing the current revision reinstates the previous one. */
  deleteRevision(pid: string, setId: string, rev: string, by: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s || s.revisions.length <= 1) return p;
      const wasCurrent = currentRev(s).rev === rev;
      const next: Project = {
        ...p,
        drawings: p.drawings.map((d) => d.id === setId
          ? { ...d, revisions: d.revisions.filter((r) => r.rev !== rev) }
          : d),
      };
      const nowCur = currentRev(next.drawings.find((d) => d.id === setId)!);
      return logActivity(next, by,
        wasCurrent
          ? `withdrew revision ${rev} of ${s.number} — ${nowCur.rev} reinstated as current`
          : `removed superseded revision ${rev} of ${s.number}`,
        "docs");
    });
  },

  /** Remove a sheet from the register — into the register's bin, where it
      keeps every revision for 30 days and can be restored. Open RFIs that
      referenced it keep their text; the sheet link goes dead meanwhile. */
  deleteDrawing(pid: string, setId: string, by: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s) return p;
      const cut = Date.now() - 30 * 86_400_000;
      const trash = [{ set: s, deletedAt: nowISO(), by }, ...(p.drawingTrash ?? []).filter((x) => +new Date(x.deletedAt) > cut)].slice(0, 200);
      return logActivity(
        { ...p, drawings: p.drawings.filter((d) => d.id !== setId), drawingTrash: trash, recordTombstones: [...new Set([...(p.recordTombstones ?? []), setId])].slice(-500) },
        by, `removed ${s.number} — ${s.title} from the drawing register (${s.revisions.length} revision${s.revisions.length === 1 ? "" : "s"}; restorable for 30 days)`,
        "docs");
    });
  },
  /** Fold duplicate registrations of one sheet into a single history. The
      keeper's number and status stay; every revision from the others joins
      the timeline in date order (relabelled when labels collide, with a note
      saying what it was), markups and requests come along, and anything that
      pointed at a merged sheet — RFIs, pins, packages, inspections — now
      points at the keeper. */
  mergeDrawingSets(pid: string, keepId: string, mergeIds: string[], by: string) {
    patchProject(pid, (p) => {
      const keep = p.drawings.find((d) => d.id === keepId);
      const others = mergeIds.map((id) => p.drawings.find((d) => d.id === id)).filter((d): d is DrawingSet => !!d && d.id !== keepId);
      if (!keep || !others.length) return p;
      const all = [{ set: keep, revs: keep.revisions }, ...others.map((s) => ({ set: s, revs: s.revisions }))];
      type Row = { rev: DrawingRevision; from: DrawingSet };
      // the same issue reaching the timeline twice (a merge re-run after a
      // sync brought a duplicate back) is folded once — matched on the file it
      // carries, else on its label and date
      const seenRev = new Set<string>();
      const rows: Row[] = all.flatMap(({ set, revs }) => revs.map((rev) => ({ rev, from: set })))
        .filter(({ rev }) => { const k = rev.sha || rev.previewId || rev.fileId || `${rev.rev}|${rev.issuedAt}`; if (seenRev.has(k)) return false; seenRev.add(k); return true; })
        .sort((a, b) => a.rev.issuedAt < b.rev.issuedAt ? -1 : a.rev.issuedAt > b.rev.issuedAt ? 1 : 0);
      const labels = rows.map((r) => r.rev.rev.toUpperCase());
      const unique = new Set(labels).size === labels.length;
      const prefix = (keep.revisions[0]?.rev.match(/^(.*?)\d+$/)?.[1]) ?? "P";
      const relabel = new Map<string, string>(); // `${setId}|${oldRev}` → new label
      const revisions: DrawingRevision[] = rows.map((r, i) => {
        const label = unique ? r.rev.rev : `${prefix}${i + 1}`;
        relabel.set(`${r.from.id}|${r.rev.rev}`, label);
        const was = r.from.id !== keep.id || label !== r.rev.rev ? `was ${r.rev.rev}${r.from.id !== keep.id ? ` on ${r.from.number}` : ""}` : "";
        return { ...r.rev, rev: label, note: [r.rev.note, was].filter(Boolean).join(" · ") || undefined };
      });
      const mapRev = (setId: string, rev?: string) => (rev ? relabel.get(`${setId}|${rev}`) ?? rev : rev);
      const redlines = all.flatMap(({ set }) => (set.redlines ?? []).map((rl) => ({ ...rl, rev: mapRev(set.id, rl.rev), comparedTo: mapRev(set.id, rl.comparedTo) })));
      const revisionRequests = all.flatMap(({ set }) => (set.revisionRequests ?? []).map((q) => ({ ...q, againstRev: mapRev(set.id, q.againstRev) ?? q.againstRev, resolvedRev: mapRev(set.id, q.resolvedRev) })));
      const merged: DrawingSet = { ...keep, revisions, redlines: redlines.length ? redlines : undefined, revisionRequests: revisionRequests.length ? revisionRequests : undefined, issuedAt: keep.issuedAt ?? others.find((o) => o.issuedAt)?.issuedAt, issuedTo: keep.issuedTo ?? others.find((o) => o.issuedTo)?.issuedTo };
      // everything that pointed at a merged sheet now points at the keeper
      const ids = new Map(others.map((o) => [o.id, keep.id] as const));
      const remap = (v: unknown): unknown => typeof v === "string" ? (ids.get(v) ?? v) : Array.isArray(v) ? v.map(remap) : v && typeof v === "object" ? Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, remap(x)])) : v;
      const { drawings: _d, ...rest } = p; void _d;
      const next = { ...(remap(rest) as Omit<Project, "drawings">), drawings: p.drawings.filter((d) => !ids.has(d.id)).map((d) => d.id === keep.id ? merged : d), recordTombstones: [...new Set([...(p.recordTombstones ?? []), ...ids.keys()])].slice(-500) } as Project;
      return logActivity(next, by, `merged ${others.map((o) => o.number).join(", ")} into ${keep.number} — one history of ${revisions.length} revision${revisions.length === 1 ? "" : "s"}`, "docs");
    });
  },
  restoreDrawing(pid: string, setId: string, by: string) {

    patchProject(pid, (p) => {
      const hit = (p.drawingTrash ?? []).find((x) => x.set.id === setId);
      if (!hit) return p;
      return logActivity(
        { ...p, drawings: [...p.drawings, hit.set], drawingTrash: (p.drawingTrash ?? []).filter((x) => x.set.id !== setId), recordTombstones: (p.recordTombstones ?? []).filter((id) => id !== setId) },
        by, `restored ${hit.set.number} — ${hit.set.title} to the drawing register`, "docs");
    });
  },
  purgeDrawing(pid: string, setId: string, by: string) {
    patchProject(pid, (p) => {
      const hit = (p.drawingTrash ?? []).find((x) => x.set.id === setId);
      if (!hit) return p;
      return logActivity({ ...p, drawingTrash: (p.drawingTrash ?? []).filter((x) => x.set.id !== setId) }, by, `permanently deleted ${hit.set.number} — ${hit.set.title}`, "docs");
    });
  },
  /** Housekeeping: off the working register, out of downloads and packages,
      kept with every revision. Nobody is emailed. */
  archiveDrawing(pid: string, setId: string, by: string, reason?: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s || s.archived) return p;
      return logActivity({ ...p, drawings: p.drawings.map((d) => d.id === setId ? { ...d, archived: { at: nowISO(), by, reason: reason?.trim() || undefined } } : d) }, by, `archived ${s.number} — ${s.title}`, "docs");
    });
  },
  unarchiveDrawing(pid: string, setId: string, by: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s?.archived) return p;
      const { archived: _a, ...rest } = s; void _a;
      return logActivity({ ...p, drawings: p.drawings.map((d) => d.id === setId ? rest : d) }, by, `brought ${s.number} — ${s.title} back from the archive`, "docs");
    });
  },
  /** Make an older issue current again. It is re-issued as a NEW revision
      (next label) carrying the old files, so the history stays linear and
      the rollback itself is on the record. */
  /** A revision that is really a different drawing — a page the title-block
      scan filed under the wrong number — moves out to its own sheet. If the
      number already exists it joins that sheet as its next issue instead. */
  splitRevision(pid: string, setId: string, rev: string, by: string, data: { number: string; title?: string }): { ok: boolean; message: string } {
    let result = { ok: false, message: "Nothing moved" };
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      const old = s?.revisions.find((r) => r.rev === rev);
      const number = data.number.trim();
      if (!s || !old || !number) { result = { ok: false, message: "That revision is not on the sheet" }; return p; }
      if (s.revisions.length < 2) { result = { ok: false, message: "A sheet keeps at least one issue — rename the sheet instead" }; return p; }
      if (sheetKey(number) === sheetKey(s.number)) { result = { ok: false, message: "That is the same number — give the page its own" }; return p; }
      const remaining = s.revisions.filter((r) => r.rev !== rev);
      const target = p.drawings.find((d) => sheetKey(d.number) === sheetKey(number) && !d.obsolete);
      const moved: DrawingRevision = { ...old, note: `Moved from ${s.number} ${old.rev}${old.note ? ` — ${old.note}` : ""}` };
      if (target) {
        const label = nextRev(currentRev(target).rev);
        result = { ok: true, message: `Moved to ${target.number} as ${label}` };
        return logActivity({ ...p, drawings: p.drawings.map((d) => d.id === setId ? { ...d, revisions: remaining } : d.id === target.id ? { ...d, revisions: [...d.revisions, { ...moved, rev: label, issuedAt: nowISO(), by }] } : d) },
          by, `moved ${s.number} ${rev} to ${target.number} as ${label}`, "docs");
      }
      const fresh: DrawingSet = {
        ...s, id: uid("ds-"), number, title: data.title?.trim() || old.fileName?.replace(/\.\w+$/, "") || s.title,
        revisions: [{ ...moved, rev: "P1" }], redlines: undefined, revisionRequests: undefined, obsolete: undefined, archived: undefined,
      };
      result = { ok: true, message: `${number} is now its own sheet at P1` };
      return logActivity({ ...p, drawings: [...p.drawings.map((d) => d.id === setId ? { ...d, revisions: remaining } : d), fresh] },
        by, `moved ${s.number} ${rev} to its own sheet ${number}`, "docs");
    });
    return result;
  },
  rollbackRevision(pid: string, setId: string, rev: string, by: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      const old = s?.revisions.find((r) => r.rev === rev);
      if (!s || !old || currentRev(s).rev === rev) return p;
      const label = nextRev(currentRev(s).rev);
      const revision: DrawingRevision = { ...old, rev: label, issuedAt: nowISO(), by, note: `Rolled back to ${rev}${old.note ? ` — ${old.note}` : ""}`, restoredFrom: rev };
      return logActivity(
        { ...p, drawings: p.drawings.map((d) => d.id === setId ? { ...d, revisions: [...d.revisions, revision] } : d) },
        by, `rolled ${s.number} back to ${rev} — re-issued as ${label}`, "docs");
    });
  },
  /** Ask whoever draws the sheet for a new issue. Logged on the sheet and
      emailed to the people chosen, with a link straight to the sheet. */
  requestRevision(pid: string, setId: string, by: string, data: { note: string; to: string[]; dueOn?: string }) {
    const id = uid("rr-");
    const to = [...new Set(data.to.map((e) => e.trim()).filter((e) => e.includes("@")))];
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s) return p;
      const req: RevisionRequest = { id, at: nowISO(), by, to, note: data.note, againstRev: currentRev(s).rev, dueOn: data.dueOn, status: "open" };
      return logActivity(
        { ...p, drawings: p.drawings.map((d) => d.id === setId ? { ...d, revisionRequests: [...(d.revisionRequests ?? []), req] } : d) },
        by, `requested a revision of ${s.number} — ${s.title}${to.length ? ` from ${to.join(", ")}` : ""}`, "docs");
    });
    const p = state.projects.find((x) => x.id === pid);
    const s = p?.drawings.find((d) => d.id === setId);
    if (p && s && to.length) {
      void notify(to, `Revision requested — ${s.number} ${s.title} (${p.code})`,
        `${by} has asked for a revised issue of ${s.number} — ${s.title} (currently ${currentRev(s).rev}) on ${p.name}.\n\n${data.note}${data.dueOn ? `\n\nNeeded by ${data.dueOn}.` : ""}\n\nUpload the new issue to the drawing register and it will supersede ${currentRev(s).rev} automatically.`,
        { link: `${APP_URL}/pm/${p.id}/drawings?sheet=${s.id}`, label: "Open the sheet" })
        .then((r) => {
          const ok = typeof r === "object" && r !== null && "ok" in r ? !!(r as { ok?: boolean }).ok : !!r;
          patchProject(pid, (x) => ({ ...x, drawings: x.drawings.map((d) => d.id === setId ? { ...d, revisionRequests: (d.revisionRequests ?? []).map((q) => q.id === id ? { ...q, emailed: ok } : q) } : d) }));
        })
        .catch(() => undefined);
    }
  },
  resolveRevisionRequest(pid: string, setId: string, reqId: string, by: string, resolvedRev?: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s) return p;
      return logActivity(
        { ...p, drawings: p.drawings.map((d) => d.id === setId ? { ...d, revisionRequests: (d.revisionRequests ?? []).map((q) => q.id === reqId ? { ...q, status: "resolved" as const, resolvedAt: nowISO(), resolvedBy: by, resolvedRev } : q) } : d) },
        by, `closed a revision request on ${s.number}${resolvedRev ? ` — answered by ${resolvedRev}` : ""}`, "docs");
    });
  },

  /** Withdraw a sheet from use without losing its record: it stays on the
      register stamped OBSOLETE, leaves every download and package, and the
      site is told to stop using it now — not on its next visit. */
  markDrawingObsolete(pid: string, setId: string, by: string, opts: { reason?: string; replacedById?: string } = {}) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s || s.obsolete) return p;
      const rep = opts.replacedById ? p.drawings.find((d) => d.id === opts.replacedById) : undefined;
      const to = [...new Set([
        ...(p.team ?? []).filter((m) => m.role === "contractor" && m.email).map((m) => m.email),
        ...(p.contractor?.email ? [p.contractor.email] : []),
      ])];
      if (to.length) {
        void notify(to, `Drawing withdrawn — ${s.number} ${s.title} (${p.code})`,
          `${s.number} — ${s.title} (rev ${currentRev(s).rev}) is OBSOLETE as of today and must not be used for construction.` +
          (rep ? ` Work to ${rep.number} — ${rep.title} (rev ${currentRev(rep).rev}) instead.` : "") +
          (opts.reason?.trim() ? `\n\nReason: ${opts.reason.trim()}` : "") + `\n\nWithdrawn by ${by}.`,
          { link: `${typeof location !== "undefined" ? location.origin : "https://studio.studiogy.design"}/pm/${p.id}/drawings`, label: "Open the drawing register" });
      }
      return logActivity(
        { ...p, drawings: p.drawings.map((d) => (d.id === setId ? { ...d, obsolete: { at: nowISO(), by, reason: opts.reason?.trim() || undefined, replacedById: opts.replacedById || undefined } } : d)) },
        by, `marked ${s.number} — ${s.title} obsolete${rep ? `, replaced by ${rep.number}` : ""}`, "docs");
    });
  },
  reinstateDrawing(pid: string, setId: string, by: string) {
    patchProject(pid, (p) => {
      const s = p.drawings.find((d) => d.id === setId);
      if (!s?.obsolete) return p;
      const { obsolete: _gone, ...rest } = s;
      void _gone;
      return logActivity({ ...p, drawings: p.drawings.map((d) => (d.id === setId ? rest : d)) }, by, `reinstated ${s.number} — ${s.title}`, "docs");
    });
  },

  /* --- drawings: bulk register import (from Atelier) -------------------- */
  /** Import a whole drawing register in one commit — new numbers are created at
      their stated revision, numbers we already hold are auto-revisioned so the
      site never loses the supersession trail. One activity line, not one per sheet. */
  importRegister(pid: string, by: string, sheets: { number: string; title: string; discipline: string; category?: DocCategory; rev?: string; note?: string }[]) {
    patchProject(pid, (p) => {
      let drawings = [...p.drawings];
      let added = 0, revised = 0;
      for (const s of sheets) {
        const num = s.number.trim().toUpperCase();
        if (!num) continue;
        const i = drawings.findIndex((d) => sheetKey(d.number) === sheetKey(num));
        if (i === -1) {
          drawings.push({
            id: uid("ds-"), number: num, title: s.title.trim(), discipline: s.discipline,
            category: s.category ?? "drawing",
            revisions: [{ rev: s.rev?.trim() || "P1", issuedAt: nowISO(), by, note: s.note }],
          });
          added++;
        } else {
          const cur = currentRev(drawings[i]);
          if (s.rev?.trim().toUpperCase() === cur.rev.toUpperCase()) continue;  // same issue — nothing to record
          drawings[i] = {
            ...drawings[i],
            title: s.title.trim() || drawings[i].title,
            revisions: [...drawings[i].revisions, { rev: nextRev(cur.rev), issuedAt: nowISO(), by, note: s.note }],
          };
          revised++;
        }
      }
      const what = [added ? `${added} new sheet${added > 1 ? "s" : ""}` : "", revised ? `${revised} auto-revisioned` : ""].filter(Boolean).join(", ") || "no changes";
      return logActivity({ ...p, drawings }, by, `imported the drawing register from Atelier — ${what}`, "docs");
    });
  },

  /* --- RFIs ------------------------------------------------------------ */
  askRfi(pid: string, askedBy: string, subject: string, question: string, docId?: string, markups?: Markup[], dueDate?: string, lang?: { translated?: boolean; originalText?: string; originalSubject?: string; originalQuestion?: string }, link?: { taskId?: string; rfiKind?: PmRfi["rfiKind"]; priority?: PmRfi["priority"]; reason?: string }) {
    let rfiId = "";
    patchProject(pid, (p) => {
      const n = p.rfis.length ? Math.max(...p.rfis.map((r) => parseInt(r.ref.split("-")[1], 10))) + 1 : 1;
      const ref = `RFI-${String(n).padStart(3, "0")}`;
      const r: PmRfi = { id: uid("prfi-"), ref, subject, question, askedBy, ballInCourt: "manager", askedAt: nowISO(), dueDate, status: "open", docId, markups: markups?.length ? markups : undefined, translated: lang?.translated, originalText: lang?.originalText, originalSubject: lang?.originalSubject, originalQuestion: lang?.originalQuestion, taskId: link?.taskId, rfiKind: link?.rfiKind, priority: link?.priority, reason: link?.reason?.trim() || undefined };
      const onSheet = docId ? ` on ${p.drawings.find((d) => d.id === docId)?.number ?? "a sheet"}` : "";
      // the studio is mailed by the server the moment the RFI lands on record
      // (notifyProjectEvents) — nothing depends on the contractor's device
      rfiId = r.id;
      return logActivity({ ...p, rfis: [r, ...p.rfis] }, askedBy, `raised ${ref}${onSheet} — ${subject}`, "rfi");
    });
    // a contractor RFI also lands on the Mind Map's RFI board
    // every RFI lands on the mind maps of the people who coordinate them
    if (rfiId) void pmActions.pushRfiToMindmap(pid, rfiId);
    return rfiId;
  },
  /** Attach a file to an RFI — a supporting doc on the question, or a sketch
      issued with the answer. */
  addRfiAttachment(pid: string, rfiId: string, data: { fileId?: string; name: string; size?: string; role: "question" | "answer"; kind?: "image" | "file" | "sheet"; thumb?: string; docId?: string; sheetNumber?: string }, by: string) {
    patchProject(pid, (p) => {
      const att: RfiAttachment = { id: uid("rfa-"), fileId: data.fileId, name: data.name, size: data.size, by, at: nowISO(), role: data.role, kind: data.kind, thumb: data.thumb, docId: data.docId, sheetNumber: data.sheetNumber };

      return { ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, attachments: [...(r.attachments ?? []), att] } : r) };
    });
  },
  removeRfiAttachment(pid: string, rfiId: string, attId: string) {
    patchProject(pid, (p) => ({ ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, attachments: (r.attachments ?? []).filter((a) => a.id !== attId) } : r) }));
  },

  /** Push a contractor RFI onto the Mind Map's RFI board (its own sheet per
      project); safe to retry, no-op once linked. Mirrors the material bridge. */
  async pushRfiToMindmap(pid: string, rfiId: string): Promise<boolean> {
    const p = state.projects.find((x) => x.id === pid);
    const r = p?.rfis.find((x) => x.id === rfiId);
    if (!p || !r || r.mindmapNodeId) return !!r?.mindmapNodeId;
    try {
      const res = await fetch("/api/mindmap/rfi", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          projectTitle: `${p.name} (${p.code})`, projectCode: p.code,
          sheetId: p.rfiBoard?.sheetId, hubId: p.rfiBoard?.hubId,
          ref: r.ref, subject: r.subject, question: r.question, dueDate: r.dueDate,
          priority: r.priority, askedBy: r.askedBy, reason: r.reason,
          link: `${typeof location !== "undefined" ? location.origin : "https://studio.studiogy.design"}/pm/${p.id}/rfis`,
        }),
      });
      const out = await res.json() as { linked?: boolean; sheetId?: string; hubId?: string; nodeId?: string };
      if (!res.ok || !out.linked || !out.nodeId) return false;
      patchProject(pid, (pr) => ({
        ...pr,
        rfiBoard: out.sheetId && out.hubId ? { sheetId: out.sheetId, hubId: out.hubId } : pr.rfiBoard,
        rfis: pr.rfis.map((x) => x.id === rfiId ? { ...x, mindmapNodeId: out.nodeId } : x),
      }));
      return true;
    } catch { return false; }
  },

  /** Every RFI on the record that never reached the mind map — pushed one by
      one, so a project opened after the bridge went live catches up. */
  async syncRfisToMindmap(pid: string): Promise<number> {
    const p = state.projects.find((x) => x.id === pid);
    if (!p) return 0;
    let n = 0;
    for (const r of p.rfis.filter((x) => x.status === "open" && !x.mindmapNodeId)) {
      if (await pmActions.pushRfiToMindmap(pid, r.id)) n++;
    }
    return n;
  },
  /** The answer went out — the node on everyone's map is ticked off. */
  async markRfiDoneOnMindmap(pid: string, rfiId: string): Promise<boolean> {
    const r = state.projects.find((x) => x.id === pid)?.rfis.find((x) => x.id === rfiId);
    if (!r?.mindmapNodeId) return false;
    try {
      const res = await fetch("/api/mindmap/rfi/done", { method: "POST", headers: { "Content-Type": "application/json", ...authHeader() }, body: JSON.stringify({ nodeId: r.mindmapNodeId }) });
      return res.ok;
    } catch { return false; }
  },

  /* --- ad-hoc tasks (anyone can add) ------------------------------------- */
  addAdhocTask(pid: string, data: { title: string; by: string; assignee?: string; due?: string }) {
    patchProject(pid, (p) => {
      const task: AdhocTask = { id: uid("at-"), title: data.title.trim(), by: data.by, assignee: data.assignee?.trim() || undefined, due: data.due || undefined, done: false, createdAt: nowISO() };
      const detail = `${task.title}${task.due ? ` (due ${task.due})` : ""}${task.assignee ? ` — for ${task.assignee}` : ""}`;
      const cta = { link: `${typeof location !== "undefined" ? location.origin : "https://studio.studiogy.design"}/pm/${p.id}/tasks`, label: "Open My Tasks" };
      // a task travels in both directions: the studio hears when the site
      // adds one, and the SITE hears when the studio puts work on its list —
      // a task nobody was told about was never really issued
      if (data.by !== STUDIO.manager) notify(STUDIO.managerEmail, `New task on ${p.name} (${p.code})`, `${data.by} added: ${detail}`, cta);
      const named = task.assignee ? (p.team ?? []).find((m) => m.name === task.assignee && m.email) : undefined;
      const siteTo = named
        ? [named.email!]
        : [p.contractor?.email, ...(p.team ?? []).filter((m) => m.role === "contractor").map((m) => m.email)]
            .filter((e, i, a): e is string => !!e && e.includes("@") && a.indexOf(e) === i);
      if ((data.by === STUDIO.manager || named) && siteTo.length) {
        notify(siteTo, `New task on ${p.name} (${p.code})`, `${data.by} issued: ${detail}`, cta);
      }
      return logActivity({ ...p, adhocTasks: [task, ...(p.adhocTasks ?? [])] }, data.by,
        `issued a task — ${task.title}${task.assignee ? ` (for ${task.assignee})` : ""}`, "docs");
    });
  },
  toggleAdhocTask(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, adhocTasks: (p.adhocTasks ?? []).map((tk) => tk.id === id ? { ...tk, done: !tk.done, doneAt: !tk.done ? nowISO() : undefined } : tk) }));
  },
  removeAdhocTask(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, adhocTasks: (p.adhocTasks ?? []).filter((tk) => tk.id !== id) }));
  },
  /** Retro-fit a machine translation onto an RFI that filed in Chinese —
      either because it predates translate-on-submit or because the translate
      call failed at filing time. The English becomes the canonical subject /
      question (what search, reports and the Mind Map bridge read) and the
      original Chinese moves to the original* fields, exactly as if it had
      translated on the way in. No activity-log entry: nothing was said, the
      record just became readable. */
  setRfiTranslation(pid: string, rfiId: string, tr: { subject?: string; question?: string; originalSubject?: string; originalQuestion?: string }) {
    patchProject(pid, (p) => ({
      ...p,
      rfis: p.rfis.map((r) => r.id === rfiId ? {
        ...r,
        subject: tr.subject ?? r.subject,
        question: tr.question ?? r.question,
        translated: true,
        originalSubject: tr.originalSubject ?? r.originalSubject,
        originalQuestion: tr.originalQuestion ?? r.originalQuestion,
      } : r),
    }));
  },
  answerRfi(pid: string, rfiId: string, answer: string, by: string, answerZh?: string) {
    patchProject(pid, (p) => {
      const rfi = p.rfis.find((r) => r.id === rfiId);
      if (!rfi) return p;
      const next: Project = { ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, answer, answerZh, answeredAt: nowISO(), status: "answered" as const, ballInCourt: "contractor" as BallInCourt } : r) };
      void pmActions.markRfiDoneOnMindmap(pid, rfiId);
      return logActivity(next, by, `answered ${rfi.ref}`, "rfi");
    });
  },
  /** The contractor confirms they've read the studio's answer — ball returns to
      the studio only if they reopen it; otherwise the RFI is settled. */
  acknowledgeRfi(pid: string, rfiId: string, by: string) {
    patchProject(pid, (p) => {
      const rfi = p.rfis.find((r) => r.id === rfiId);
      if (!rfi || rfi.acknowledgedAt) return p;
      const next: Project = { ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, acknowledgedAt: nowISO(), acknowledgedBy: by } : r) };
      return logActivity(next, by, `acknowledged the answer to ${rfi.ref}`, "rfi");
    });
  },
  /** Pin the site's timezone. Every "today" on the project resolves here, so a
      crew filing from a phone still on their home clock still dates the report
      to the day it happened on site. */
  setProjectZone(pid: string, timezone: string | undefined) {
    patchProject(pid, (p) => ({ ...p, timezone: timezone || undefined }));
  },
  /** Turn one daily-report requirement on or off for this project. */
  setReportRule(pid: string, key: keyof DailyReportRules, on: boolean) {
    patchProject(pid, (p) => ({ ...p, reportRules: { ...reportRules(p), [key]: on || undefined } }));
  },
  /** Replace the project's vantage points. Retiring one keeps its history —
      set `active: false` rather than removing it. */
  setViewpoints(pid: string, viewpoints: SiteViewpoint[]) {
    patchProject(pid, (p) => ({ ...p, viewpoints }));
  },
  addViewpoint(pid: string, name: string, guide?: string) {
    patchProject(pid, (p) => ({
      ...p,
      viewpoints: [...(p.viewpoints ?? DEFAULT_VIEWPOINTS), { id: uid("vp-"), name: name.trim(), guide: guide?.trim() || undefined, active: true }],
    }));
  },
  setBallInCourt(pid: string, rfiId: string, ball: BallInCourt) {
    patchProject(pid, (p) => ({ ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, ballInCourt: ball } : r) }));
  },
  /** Save (or clear) one line-by-line answer to a numbered RFI point. The RFI
      stays open — the studio drafts point by point, then sends the lot. */
  answerRfiPoint(pid: string, rfiId: string, key: string, en: string, zh: string | undefined, by: string) {
    patchProject(pid, (p) => ({
      ...p, rfis: p.rfis.map((r) => {
        if (r.id !== rfiId) return r;
        const prior = (r.pointAnswers ?? []).find((a) => a.key === key);
        const rest = (r.pointAnswers ?? []).filter((a) => a.key !== key);
        // carry the line's drawings and images across a text re-save — the
        // attachments outlive any edit to the wording
        const files = prior?.files;
        const pointAnswers = en.trim() || files?.length
          ? [...rest, { ...prior, key, en: en.trim(), zh: zh?.trim() || undefined, by, at: nowISO(), files }]
          : rest;
        return { ...r, pointAnswers };
      }),
    }));
  },
  /** Attach a drawing or image to ONE line of an RFI answer. Creates the point
      answer if the studio attaches a sketch before writing the wording. */
  addRfiPointFile(pid: string, rfiId: string, key: string, data: Omit<RfiPointFile, "id" | "by" | "at">, by: string) {
    patchProject(pid, (p) => ({
      ...p, rfis: p.rfis.map((r) => {
        if (r.id !== rfiId) return r;
        const f: RfiPointFile = { ...data, id: uid("rpf-"), by, at: nowISO() };
        const prior = (r.pointAnswers ?? []).find((a) => a.key === key);
        const rest = (r.pointAnswers ?? []).filter((a) => a.key !== key);
        const entry: RfiPointAnswer = prior
          ? { ...prior, files: [...(prior.files ?? []), f] }
          : { key, en: "", by, at: nowISO(), files: [f] };
        return { ...r, pointAnswers: [...rest, entry] };
      }),
    }));
  },
  removeRfiPointFile(pid: string, rfiId: string, key: string, fileId: string) {
    patchProject(pid, (p) => ({
      ...p, rfis: p.rfis.map((r) => {
        if (r.id !== rfiId) return r;
        const pointAnswers = (r.pointAnswers ?? []).map((a) =>
          a.key === key ? { ...a, files: (a.files ?? []).filter((f) => f.id !== fileId) } : a);
        // an empty line with nothing left attached is no answer at all
        return { ...r, pointAnswers: pointAnswers.filter((a) => a.en.trim() || a.files?.length) };
      }),
    }));
  },
  /** Finalise a line-by-line RFI: compose the consolidated answer from the point
      answers, mark it answered and pass the ball to the contractor. `points`
      carries the outline order + point text so the answer reads cleanly. */
  sendRfiPointAnswers(pid: string, rfiId: string, by: string, compose: { en: string; zh: string }) {
    patchProject(pid, (p) => {
      const rfi = p.rfis.find((r) => r.id === rfiId);
      if (!rfi) return p;
      const next: Project = { ...p, rfis: p.rfis.map((r) => r.id === rfiId ? { ...r, answer: compose.en, answerZh: compose.zh, answeredAt: nowISO(), status: "answered" as const, ballInCourt: "contractor" as BallInCourt } : r) };
      void pmActions.markRfiDoneOnMindmap(pid, rfiId);
      const to = p.team.filter((m) => (m.role === "contractor") && m.email).map((m) => m.email);
      if (to.length) notify(to, `${rfi.ref} answered — ${rfi.subject} (${p.code})`, `The studio has answered ${rfi.ref} point by point. Open the project room to read the responses.`);
      return logActivity(next, by, `answered ${rfi.ref} (point by point)`, "rfi");
    });
  },
  /** The contractor accepts or queries ONE answered point. Querying sends the
      ball back to the studio; when every answered point is accepted the whole
      RFI settles (acknowledged). */
  ackRfiPoint(pid: string, rfiId: string, key: string, ack: "accepted" | "queried", by: string, note?: string) {
    patchProject(pid, (p) => {
      const rfi = p.rfis.find((r) => r.id === rfiId);
      if (!rfi) return p;
      const pts = (rfi.pointAnswers ?? []).map((a) => a.key === key
        ? { ...a, ack, ackNote: ack === "queried" ? (note?.trim() || undefined) : undefined, ackBy: by, ackAt: nowISO() } : a);
      const allAccepted = pts.length > 0 && pts.every((a) => a.ack === "accepted");
      const anyQueried = pts.some((a) => a.ack === "queried");
      const next: Project = {
        ...p, rfis: p.rfis.map((r) => r.id !== rfiId ? r : {
          ...r, pointAnswers: pts,
          ballInCourt: anyQueried ? "manager" as BallInCourt : r.ballInCourt,
          acknowledgedAt: allAccepted && !r.acknowledgedAt ? nowISO() : r.acknowledgedAt,
          acknowledgedBy: allAccepted && !r.acknowledgedBy ? by : r.acknowledgedBy,
        }),
      };
      if (ack === "queried") notify(STUDIO.managerEmail, `${rfi.ref} — a point was queried (${p.code})`, `${by} queried the answer to a point on ${rfi.ref}${note ? `: ${note}` : ""}.`);
      return logActivity(next, by, `${ack === "accepted" ? "accepted" : "queried"} a point on ${rfi.ref}`, "rfi");
    });
  },

  /* --- daily log --------------------------------------------------------- */
  /** Portal level for a client seat — how much of /site they see. */
  setMemberClientRole(pid: string, memberId: string, clientRole: ClientRole) {
    patchProject(pid, (p) => ({ ...p, team: p.team.map((m) => (m.id === memberId ? { ...m, clientRole } : m)) }));
  },
  /** Scope a subcontractor to specific drawing disciplines (empty = all). */
  setMemberDisciplines(pid: string, memberId: string, disciplines: string[]) {
    patchProject(pid, (p) => ({ ...p, team: p.team.map((m) => (m.id === memberId ? { ...m, drawingDisciplines: disciplines.length ? disciplines : undefined } : m)) }));
  },

  /** Permanently remove a project — the row is deleted from D1 too, so every
      device loses it and team access codes stop working. Manager-only (UI). */
  /** The Projects page in the order the studio dragged it into. Every project
      whose position changed is written, so the order follows to every device. */
  setProjectOrder(ids: string[]) {
    ids.forEach((id, i) => {
      const p = state.projects.find((x) => x.id === id);
      if (p && p.listOrder !== i) patchProject(id, (x) => ({ ...x, listOrder: i }));
    });
  },
  deleteProject(pid: string) {
    const p = state.projects.find((x) => x.id === pid);
    if (!p) return;
    state = { projects: state.projects.filter((x) => x.id !== pid) };
    persist();
    subs.forEach((f) => f());
    projectDeleteListener?.(pid);
  },

  /* --- critical milestones ---------------------------------------------- */
  addMilestone(pid: string, data: { label: string; date: string; note?: string }) {
    patchProject(pid, (p) => {
      const m = { ...data, id: uid("ms-") };
      const milestones = [...(p.milestones ?? []), m].sort((a, b) => (a.date < b.date ? -1 : 1));
      return logActivity({ ...p, milestones }, STUDIO.manager, `set critical milestone “${data.label}” for ${data.date}`, "timeline");
    });
  },
  removeMilestone(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, milestones: (p.milestones ?? []).filter((m) => m.id !== id) }));
  },

  /** Stamp that the Mind Map start-up bundle went out (one send per project). */
  markOnboardingSent(pid: string, info: { sheetTitle?: string; hubId?: string; tasks: number; assignee?: string }) {
    patchProject(pid, (p) => logActivity(
      { ...p, onboarding: { sentAt: nowISO(), ...info } },
      STUDIO.manager,
      `sent the onboarding pack to the Mind Map — ${info.tasks} start-up tasks${info.assignee ? ` for ${info.assignee}` : ""}`,
      "docs",
    ));
  },

  addLog(pid: string, by: string, data: { company?: string; date: string; weather: Weather; tempC?: number; crew: CrewLine[]; equipment?: string; workDone: string[]; activities?: ActivityLine[]; teamPresent?: string[]; observationRefs?: string[]; delays?: string; visitors?: string; note?: string; photoIds?: string[]; roster?: CrewMember[]; areas?: WorkArea[]; nextTargets?: string[]; challenges?: SiteChallenge[]; infoNeeded?: InfoRequest[]; instructions?: SiteInstructionReceived[]; translated?: boolean; originalText?: string }): DailyLog | null {
    let written: DailyLog | null = null;
    patchProject(pid, (p) => {
      /* One report per day — and re-filing a day must REPLACE it, not sit
         beside it. Dropping the old row and adding a new id looked like a
         replacement locally, but the server reconciles arrays by unioning them
         on id, so the discarded row came straight back: two reports for one
         date, the older and thinner one often winning the display. Re-use the
         existing id and the union updates in place, which is what "replace"
         was always supposed to mean. */
      const prior = p.logs.find((l) => l.date === data.date);
      const log: DailyLog = { id: prior?.id ?? uid("dl-"), by, ...data };
      written = log;
      const next: Project = { ...p, logs: [log, ...p.logs.filter((l) => l.date !== data.date)] };
      const manpower = data.crew.reduce((s, c) => s + c.count, 0);
      if (by !== STUDIO.manager) {
        // a report every reader can act on: which project, what was done, and a
        // button that opens THIS report — not the studio front door
        const day = new Date(data.date + "T00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
        const work = (data.activities?.map((a) => a.text) ?? data.workDone).filter(Boolean).slice(0, 6);
        const parts = [
          `${p.name} · ${p.code}`,
          `${p.location}`,
          "",
          `${by} filed the site report for ${day}.`,
          "",
          `On site: ${manpower}${data.crew.length ? ` (${data.crew.map((c) => `${c.count} ${c.trade}`).join(", ")})` : ""}`,
          `Work items: ${data.workDone.length}${data.photoIds?.length ? `  ·  Photos: ${data.photoIds.length}` : ""}`,
          ...(work.length ? ["", "Work reported:", ...work.map((w) => `  • ${w}`)] : []),
          ...(data.delays ? ["", `Delay noted: ${data.delays}`] : []),
          ...(data.challenges?.length ? ["", `${data.challenges.length} challenge${data.challenges.length > 1 ? "s" : ""} flagged for the studio.`] : []),
          "",
          "Open the report below for the full record, photos and to acknowledge it.",
        ];
        notify(
          STUDIO.managerEmail,
          `Daily site report — ${p.name} — ${data.date}`,
          parts.join("\n"),
          { link: `${APP_URL}/pm/${pid}/dailylog?open=${log.id}`, label: "Open the daily report" },
        );
      }
      return logActivity(next, by, `filed the daily site report for ${data.date}`, "docs");
    });
    return written;
  },
  ackLog(pid: string, logId: string, by: string) {
    patchProject(pid, (p) => {
      const log = p.logs.find((l) => l.id === logId);
      if (!log || log.acknowledged) return p;
      const next: Project = { ...p, logs: p.logs.map((l) => l.id === logId ? { ...l, acknowledged: { by, at: nowISO() } } : l) };
      return logActivity(next, by, `acknowledged the ${log.date} site report`, "docs");
    });
  },

  /* --- observations -------------------------------------------------------- */
  /** Returns the new OBS ref so callers (daily report / site visit composers)
      can link the observation back to the record it was raised from. */
  addObservation(pid: string, data: { by: string; company: string; category: ObservationCategory; note: string; location?: string; photoId?: string; translated?: boolean; originalText?: string }): string | null {
    let ref: string | null = null;
    patchProject(pid, (p) => {
      const n = p.observations.length ? Math.max(...p.observations.map((o) => parseInt(o.ref.split("-")[1], 10))) + 1 : 1;
      const o: Observation = { id: uid("ob-"), ref: `OBS-${String(n).padStart(3, "0")}`, at: nowISO(), status: "open", ...data };
      ref = o.ref;
      if (data.company !== STUDIO.name) notify(STUDIO.managerEmail, `${o.ref} — ${data.category} observation (${p.code})`,
        `${data.by} of ${data.company}${data.location ? ` at ${data.location}` : ""}: ${data.note}`);
      return logActivity({ ...p, observations: [o, ...p.observations] }, data.company, `raised ${o.ref} — ${data.category} observation`, "rfi");
    });
    return ref;
  },
  respondObservation(pid: string, obsId: string, response: string, by: string) {
    patchProject(pid, (p) => {
      const o = p.observations.find((x) => x.id === obsId);
      if (!o) return p;
      const next: Project = { ...p, observations: p.observations.map((x) => x.id === obsId ? { ...x, status: "closed" as const, response, respondedAt: nowISO() } : x) };
      return logActivity(next, by, `responded to ${o.ref} and closed it`, "rfi");
    });
  },

  /* --- site visits ----------------------------------------------------------- */
  addVisit(pid: string, data: { date: string; attendees: string; purpose: string; weather?: Weather; tempC?: number; teamPresent?: string[]; activities?: ActivityLine[]; observationRefs?: string[]; notes: string[]; directives: Omit<SiteVisitDirective, "id" | "done">[]; photoIds: string[] }) {
    patchProject(pid, (p) => {
      const n = p.visits.length ? Math.max(...p.visits.map((v) => parseInt(v.ref.split("-")[1], 10))) + 1 : 1;
      const v: SiteVisit = {
        id: uid("sv-"), ref: `SV-${String(n).padStart(3, "0")}`, by: STUDIO.manager, issuedAt: nowISO(),
        date: data.date, attendees: data.attendees, purpose: data.purpose, notes: data.notes,
        weather: data.weather, tempC: data.tempC, teamPresent: data.teamPresent,
        activities: data.activities, observationRefs: data.observationRefs,
        directives: data.directives.map((d) => ({ ...d, id: uid("dir-"), done: false })),
        photoIds: data.photoIds,
      };
      const teamEmails = p.team.map((m) => m.email);
      notify(teamEmails, `${v.ref} — site visit report issued (${p.code})`,
        `${STUDIO.manager} issued the site visit report for ${data.date}: ${data.purpose}. ${v.directives.length} directive${v.directives.length === 1 ? "" : "s"} to action.`);
      return logActivity({ ...p, visits: [v, ...p.visits] }, STUDIO.manager, `issued site visit report ${v.ref}`, "docs");
    });
  },
  toggleDirective(pid: string, visitId: string, dirId: string, by: string) {
    patchProject(pid, (p) => {
      const next: Project = {
        ...p,
        visits: p.visits.map((v) => v.id === visitId
          ? { ...v, directives: v.directives.map((d) => d.id === dirId ? { ...d, done: !d.done } : d) }
          : v),
      };
      const v = next.visits.find((x) => x.id === visitId);
      const d = v?.directives.find((x) => x.id === dirId);
      return d?.done ? logActivity(next, by, `completed directive “${d.text.slice(0, 50)}” (${v!.ref})`, "docs") : next;
    });
  },

  /* --- meetings ------------------------------------------------------------ */
  addMeeting(pid: string, by: string, data: {
    title: string; kind: MeetingKind; date: string; time?: string; location?: string; attendees: string; link?: string;
    topics: { topic: string; owner?: string; translated?: boolean; originalText?: string }[];
    carryFromId?: string;   // roll open points forward from this meeting
  }): string {
    let created = "";
    patchProject(pid, (p) => {
      const n = p.meetings.length ? Math.max(...p.meetings.map((m) => parseInt(m.ref.split("-")[1], 10))) + 1 : 1;
      const source = data.carryFromId ? p.meetings.find((m) => m.id === data.carryFromId) : undefined;
      const rolled: AgendaItem[] = source
        ? source.agenda.filter(isOpenPoint).map((a) => ({
            id: uid("ag-"), topic: a.topic, owner: a.owner, status: "pending" as const, actions: [],
            carried: true, carriedFromRef: source.ref, note: a.outcome ? `Left open at ${source.ref}: ${a.outcome}` : undefined,
            translated: a.translated, originalText: a.originalText,
          }))
        : [];
      const m: Meeting = {
        id: uid("mtg-"), ref: `MTG-${String(n).padStart(3, "0")}`,
        title: data.title.trim(), kind: data.kind, date: data.date, time: data.time,
        location: data.location?.trim() || undefined, attendees: data.attendees.trim(),
        link: data.link?.trim() || undefined, rolledFrom: rolled.length ? source?.ref : undefined,
        agenda: [
          ...rolled,
          ...data.topics.filter((t) => t.topic.trim()).map((t) => ({
            id: uid("ag-"), topic: t.topic.trim(), owner: t.owner?.trim() || undefined,
            status: "pending" as const, actions: [], translated: t.translated, originalText: t.originalText,
          })),
        ],
        status: "planned", by, createdAt: nowISO(),
      };
      created = m.id;
      const teamEmails = p.team.map((x) => x.email).filter(Boolean);
      notify(teamEmails.length ? teamEmails : [STUDIO.managerEmail],
        `${m.ref} — ${m.title} scheduled (${p.code})`,
        `${by} scheduled ${m.title} for ${data.date}${data.time ? ` at ${data.time}` : ""}${m.location ? `, ${m.location}` : ""}. ${m.agenda.length} agenda point${m.agenda.length === 1 ? "" : "s"}.`);
      return logActivity({ ...p, meetings: [m, ...p.meetings] }, by, `scheduled ${m.ref} — ${m.title} for ${data.date}`, "docs");
    });
    return created;
  },
  addAgendaItem(pid: string, mid: string, by: string, data: { topic: string; owner?: string; translated?: boolean; originalText?: string }) {
    patchProject(pid, (p) => {
      const mtg = p.meetings.find((m) => m.id === mid);
      if (!mtg || mtg.status === "held") return p;
      const item: AgendaItem = { id: uid("ag-"), topic: data.topic.trim(), owner: data.owner?.trim() || undefined, status: "pending", actions: [], translated: data.translated, originalText: data.originalText };
      if (by !== STUDIO.manager) notify(STUDIO.managerEmail, `Agenda point added to ${mtg.ref} (${p.code})`, `${by} added: ${item.topic}`);
      const next: Project = { ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, agenda: [...m.agenda, item] } : m) };
      return logActivity(next, by, `added an agenda point to ${mtg.ref} — ${item.topic.slice(0, 60)}`, "docs");
    });
  },
  removeAgendaItem(pid: string, mid: string, itemId: string) {
    patchProject(pid, (p) => ({ ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, agenda: m.agenda.filter((a) => a.id !== itemId) } : m) }));
  },
  moveAgendaItem(pid: string, mid: string, itemId: string, dir: -1 | 1) {
    patchProject(pid, (p) => ({
      ...p,
      meetings: p.meetings.map((m) => {
        if (m.id !== mid) return m;
        const i = m.agenda.findIndex((a) => a.id === itemId);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= m.agenda.length) return m;
        const agenda = [...m.agenda];
        [agenda[i], agenda[j]] = [agenda[j], agenda[i]];
        return { ...m, agenda };
      }),
    }));
  },
  recordAgendaOutcome(pid: string, mid: string, itemId: string, outcome: string) {
    patchProject(pid, (p) => ({
      ...p,
      meetings: p.meetings.map((m) => m.id === mid
        ? { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, outcome: outcome.trim() || undefined, status: "discussed" as const } : a) }
        : m),
    }));
  },
  addAgendaAction(pid: string, mid: string, itemId: string, data: { text: string; assignee: string; due?: string }) {
    patchProject(pid, (p) => ({
      ...p,
      meetings: p.meetings.map((m) => m.id === mid
        ? { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, actions: [...a.actions, { id: uid("act-"), text: data.text.trim(), assignee: data.assignee, due: data.due, done: false }] } : a) }
        : m),
    }));
  },
  toggleAgendaAction(pid: string, mid: string, itemId: string, actionId: string, by: string) {
    patchProject(pid, (p) => {
      const next: Project = {
        ...p,
        meetings: p.meetings.map((m) => m.id === mid
          ? { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, actions: a.actions.map((x) => x.id === actionId ? { ...x, done: !x.done } : x) } : a) }
          : m),
      };
      const act = next.meetings.find((m) => m.id === mid)?.agenda.find((a) => a.id === itemId)?.actions.find((x) => x.id === actionId);
      return act?.done ? logActivity(next, by, `completed the action “${act.text.slice(0, 50)}”`, "docs") : next;
    });
  },
  holdMeeting(pid: string, mid: string, by: string) {
    patchProject(pid, (p) => {
      const mtg = p.meetings.find((m) => m.id === mid);
      if (!mtg || mtg.status === "held") return p;
      const discussed = mtg.agenda.filter((a) => a.status === "discussed").length;
      const actions = mtg.agenda.reduce((s, a) => s + a.actions.length, 0);
      const next: Project = { ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, status: "held" as const, heldAt: nowISO() } : m) };
      return logActivity(next, by, `held ${mtg.ref} — ${discussed}/${mtg.agenda.length} points discussed, ${actions} action${actions === 1 ? "" : "s"} raised`, "docs");
    });
  },
  issueMinutes(pid: string, mid: string) {
    patchProject(pid, (p) => {
      const mtg = p.meetings.find((m) => m.id === mid);
      if (!mtg || mtg.status !== "held") return p;
      const lines = mtg.agenda.map((a, i) => {
        const acts = a.actions.map((x) => `    → ${x.text} (${x.assignee}${x.due ? `, due ${x.due}` : ""})`).join("\n");
        return `${i + 1}. ${a.topic}${a.status === "pending" ? " — not reached" : ""}\n   ${a.outcome ?? "No outcome recorded."}${acts ? "\n" + acts : ""}`;
      }).join("\n");
      const teamEmails = p.team.map((x) => x.email).filter(Boolean);
      notify(teamEmails.length ? teamEmails : [STUDIO.managerEmail],
        `Minutes — ${mtg.ref} ${mtg.title} (${p.code})`,
        `Held ${mtg.date}${mtg.time ? ` at ${mtg.time}` : ""}. Attendees: ${mtg.attendees}.\n\n${lines}`);
      const next: Project = { ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, minutesIssuedAt: nowISO() } : m) };
      return logActivity(next, STUDIO.manager, `issued the minutes for ${mtg.ref}`, "docs");
    });
  },
  setMeetingLink(pid: string, mid: string, link: string) {
    patchProject(pid, (p) => ({ ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, link: link.trim() || undefined } : m) }));
  },
  /** A two-sided follow-up on a meeting point — studio ⇄ contractor; the other
      side is notified so points can be chased from either end. */
  addAgendaFollowUp(pid: string, mid: string, itemId: string, data: { by: string; side: "studio" | "contractor"; text: string; translated?: boolean; originalText?: string }) {
    patchProject(pid, (p) => {
      const mtg = p.meetings.find((m) => m.id === mid);
      const item = mtg?.agenda.find((a) => a.id === itemId);
      const fu = { id: uid("mfu-"), by: data.by, side: data.side, text: data.text.trim(), at: nowISO(), translated: data.translated, originalText: data.originalText };
      const next: Project = { ...p, meetings: p.meetings.map((m) => m.id !== mid ? m : { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, followUps: [...(a.followUps ?? []), fu] } : a) }) };
      // ping the OTHER side
      if (data.side === "contractor") notify(STUDIO.managerEmail, `Follow-up on ${mtg?.ref} — ${item?.topic?.slice(0, 50)} (${p.code})`, `${data.by}: ${data.text}`);
      else { const to = p.team.filter((t) => t.role === "contractor" && t.email).map((t) => t.email); if (to.length) notify(to, `Follow-up on ${mtg?.ref} — ${item?.topic?.slice(0, 50)} (${p.code})`, `${data.by}: ${data.text}`); }
      return logActivity(next, data.by, `followed up on a point in ${mtg?.ref}`, "docs");
    });
  },
  /** Mark a meeting point resolved (or reopen) so it stops rolling forward. */
  toggleAgendaResolved(pid: string, mid: string, itemId: string) {
    patchProject(pid, (p) => ({ ...p, meetings: p.meetings.map((m) => m.id !== mid ? m : { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, resolved: !a.resolved } : a) }) }));
  },
  /** Record that a meeting point was issued as a formal instruction — links the
      Correspondence ref so it shows on the point (the UI creates the SI record). */
  flagAgendaInstruction(pid: string, mid: string, itemId: string, ref: string | undefined) {
    patchProject(pid, (p) => ({ ...p, meetings: p.meetings.map((m) => m.id !== mid ? m : { ...m, agenda: m.agenda.map((a) => a.id === itemId ? { ...a, instruction: !!ref, instructionRef: ref } : a) }) }));
  },
  /** Store the AI / auto-drafted minutes narrative on a meeting. */
  setMinutesSummary(pid: string, mid: string, summary: string) {
    patchProject(pid, (p) => ({ ...p, meetings: p.meetings.map((m) => m.id === mid ? { ...m, minutesSummary: summary } : m) }));
  },

  /* --- targets ------------------------------------------------------------ */
  /** A dated probe — set, and later marked sent or removed. */
  addDateProbe(pid: string, data: { date: string; title: string; detail?: string; milestoneId?: string; by: string }) {
    patchProject(pid, (p) => logActivity(
      { ...p, dateProbes: [...(p.dateProbes ?? []), { id: uid("probe-"), ...data, setBy: data.by }] },
      data.by, `set a probe for ${data.date} — ${data.title}`, "timeline",
    ));
  },
  removeDateProbe(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, dateProbes: (p.dateProbes ?? []).filter((x) => x.id !== id) }));
  },
  markProbeSent(pid: string, id: string) {
    patchProject(pid, (p) => ({
      ...p,
      dateProbes: (p.dateProbes ?? []).map((x) => (x.id === id ? { ...x, sentAt: new Date().toISOString() } : x)),
    }));
  },
  /** The studio arms (or disarms) the planning probe. */
  setPlanProbe(pid: string, on: boolean) {
    patchProject(pid, (p) => logActivity(
      { ...p, planProbe: on || undefined },
      STUDIO.manager,
      on ? "switched the week-planning probe on" : "switched the week-planning probe off",
      "timeline",
    ));
  },
  /** Returns the new target's id, so a goal can be linked to the target raised
      against it and the week's accomplishment read off worked evidence. */
  addTarget(pid: string, date: string, text: string, trade?: string, extra?: { scope?: "day" | "week"; qty?: number; unit?: string; owner?: string; taskId?: string }): string {
    const id = uid("tg-");
    patchProject(pid, (p) => ({
      ...p,
      targets: [...p.targets, {
        id, date, text, trade, done: false,
        scope: extra?.scope, qty: extra?.qty, unit: extra?.unit, owner: extra?.owner, taskId: extra?.taskId,
        qtyDone: extra?.qty !== undefined ? 0 : undefined,
      }],
    }));
    return id;
  },
  /** Record progress against a measurable target (40 of 120 m² poured). */
  setTargetQty(pid: string, id: string, qtyDone: number) {
    patchProject(pid, (p) => ({
      ...p,
      targets: p.targets.map((t) => t.id === id
        ? { ...t, qtyDone, done: t.qty !== undefined ? qtyDone >= t.qty : t.done }
        : t),
    }));
  },
  removeTarget(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, targets: p.targets.filter((t) => t.id !== id) }));
  },
  toggleTarget(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, targets: p.targets.map((t) => t.id === id ? { ...t, done: !t.done } : t) }));
  },
  carryOver(pid: string, fromDate: string, toDate: string) {
    patchProject(pid, (p) => {
      const open = p.targets.filter((t) => t.date === fromDate && !t.done);
      if (!open.length) return p;
      const carried: DailyTarget[] = open.map((t) => ({ ...t, id: uid("tg-"), date: toDate, carried: true }));
      return { ...p, targets: [...p.targets.filter((t) => !(t.date === fromDate && !t.done)), ...carried] };
    });
  },

  /* --- programme ------------------------------------------------------------ */
  upsertTask(pid: string, task: Omit<GanttTask, "id"> & { id?: string }) {
    patchProject(pid, (p) => {
      /* The day progress first moves is the actual start; the day it reaches
         100 is the actual finish — unless someone has set them by hand. */
      const today = todayKey();
      const stamp = (prev: GanttTask | undefined, next: GanttTask): GanttTask => {
        if (next.kind === "procurement") return next;
        const out = { ...next };
        if (next.progress > 0 && !out.actualStart) out.actualStart = (prev?.progress ?? 0) > 0 ? (prev?.actualStart ?? today) : today;
        if (next.progress >= 100 && !out.actualFinish) out.actualFinish = today;
        if (next.progress < 100 && prev && prev.progress >= 100 && out.actualFinish === prev.actualFinish) out.actualFinish = undefined;
        return out;
      };
      if (task.id) return { ...p, tasks: p.tasks.map((t) => t.id === task.id ? stamp(t, { ...t, ...task, id: task.id }) : t) };
      return { ...p, tasks: [...p.tasks, stamp(undefined, { ...task, id: uid("gt-") } as GanttTask)] };
    });
  },
  /** Move an activity by whole days — a drag on the timeline. The duration is
      kept, the baseline is fixed from the dates being left (if it was not
      already), and the move is recorded as an adjusted start. */
  shiftTask(pid: string, taskId: string, deltaDays: number, by: string) {
    if (!deltaDays) return;
    patchProject(pid, (p) => {
      const t = p.tasks.find((x) => x.id === taskId);
      if (!t) return p;
      const shift = (iso: string) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + deltaDays); return d.toISOString().slice(0, 10); };
      const from = t.kind === "procurement" ? (t.end || t.start) : t.start;
      const next: GanttTask = t.kind === "procurement"
        ? { ...t, start: shift(from), end: shift(from), adjusted: { from, to: shift(from), at: nowISO(), by } }
        : { ...t, start: shift(t.start), end: shift(t.end), baselineStart: t.baselineStart ?? t.start, baselineEnd: t.baselineEnd ?? t.end, adjusted: { from: t.adjusted?.from ?? t.start, to: shift(t.start), at: nowISO(), by } };
      const what = t.kind === "procurement" ? "need date" : "start";
      return logActivity({ ...p, tasks: p.tasks.map((x) => x.id === taskId ? next : x) }, by, `adjusted the ${what} of "${t.name}" from ${from} to ${next.start} (${deltaDays > 0 ? "+" : ""}${deltaDays}d)`, "timeline");
    });
  },
  /** Record when the work really started / finished. Empty clears the date. */
  setTaskActuals(pid: string, taskId: string, actuals: { actualStart?: string; actualFinish?: string }) {
    patchProject(pid, (p) => ({ ...p, tasks: p.tasks.map((t) => t.id === taskId ? { ...t, actualStart: actuals.actualStart || undefined, actualFinish: actuals.actualFinish || undefined } : t) }));
  },
  /** Re-type a row: an activity, or a date materials are required by. */
  setTaskKind(pid: string, taskId: string, kind: "activity" | "procurement", by: string) {
    patchProject(pid, (p) => {
      const t = p.tasks.find((x) => x.id === taskId);
      if (!t) return p;
      const next: GanttTask = kind === "procurement"
        ? { ...t, kind, progress: 0, actualStart: undefined, actualFinish: undefined, blocked: undefined, blockReason: undefined, critical: undefined, dependsOn: undefined, procurement: t.procurement ?? { status: "to-order" } }
        : { ...t, kind: undefined, feeds: undefined, procurement: undefined };
      return logActivity({ ...p, tasks: p.tasks.map((x) => x.id === taskId ? next : x) }, by, kind === "procurement" ? `marked "${t.name}" as a material need date` : `marked "${t.name}" as an activity`, "timeline");
    });
  },
  /** Re-type several rows in one commit — the import-cleanup case. */
  setTaskKinds(pid: string, ids: string[], kind: "activity" | "procurement", by: string) {
    patchProject(pid, (p) => {
      const set = new Set(ids);
      const tasks = p.tasks.map((t) => {
        if (!set.has(t.id)) return t;
        return kind === "procurement"
          ? { ...t, kind, progress: 0, actualStart: undefined, actualFinish: undefined, blocked: undefined, blockReason: undefined, critical: undefined, dependsOn: undefined, procurement: t.procurement ?? { status: "to-order" as const } }
          : { ...t, kind: undefined, feeds: undefined, procurement: undefined };
      });
      return logActivity({ ...p, tasks }, by, `marked ${ids.length} programme row${ids.length === 1 ? "" : "s"} as ${kind === "procurement" ? "material need dates" : "activities"}`, "timeline");
    });
  },
  /** Procurement rows: order status, supplier, dates, and the activities fed. */
  setProcurement(pid: string, taskId: string, patch: Partial<NonNullable<GanttTask["procurement"]>> & { feeds?: string[]; requiredBy?: string }) {
    patchProject(pid, (p) => ({
      ...p,
      tasks: p.tasks.map((t) => {
        if (t.id !== taskId) return t;
        const { feeds, requiredBy, ...pr } = patch;
        const out: GanttTask = { ...t, kind: "procurement", procurement: { ...(t.procurement ?? { status: "to-order" }), ...pr } };
        if (feeds) out.feeds = feeds;
        if (requiredBy) { out.start = requiredBy; out.end = requiredBy; }
        if (out.procurement?.status === "delivered" && !out.procurement.deliveredOn) out.procurement.deliveredOn = todayKey();
        return out;
      }),
    }));
  },
  /** Optional programme tooling — subcontractor schedules and the like. */
  setProgrammeFeature(pid: string, key: "subSchedules", on: boolean, by: string) {
    patchProject(pid, (p) => logActivity({ ...p, programmeFeatures: { ...(p.programmeFeatures ?? {}), [key]: on } }, by, `${on ? "switched on" : "switched off"} subcontractor schedules`, "timeline"));
  },
  /** The subcontractor's own parallel schedule on a trade package. */
  upsertSubTask(pid: string, pkgId: string, task: Omit<import("./types").SubTask, "id"> & { id?: string }) {
    const today = todayKey();
    pmActions.patchPackage(pid, pkgId, (pkg) => {
      const rows = pkg.schedule ?? [];
      const stamp = (prev: import("./types").SubTask | undefined, next: import("./types").SubTask) => {
        const out = { ...next };
        if (next.progress > 0 && !out.actualStart) out.actualStart = prev?.actualStart ?? today;
        if (next.progress >= 100 && !out.actualFinish) out.actualFinish = today;
        return out;
      };
      if (task.id) return { ...pkg, schedule: rows.map((r) => r.id === task.id ? stamp(r, { ...r, ...task, id: task.id }) : r) };
      return { ...pkg, schedule: [...rows, stamp(undefined, { ...task, id: uid("st-") } as import("./types").SubTask)] };
    });
  },
  removeSubTask(pid: string, pkgId: string, id: string) {
    pmActions.patchPackage(pid, pkgId, (pkg) => ({ ...pkg, schedule: (pkg.schedule ?? []).filter((r) => r.id !== id) }));
  },
  /** Which main-programme activities a package owns, and which hold it up. */
  setPackageLinks(pid: string, pkgId: string, links: { taskIds?: string[]; blockedByTaskIds?: string[] }) {
    pmActions.patchPackage(pid, pkgId, (pkg) => ({ ...pkg, ...(links.taskIds ? { taskIds: links.taskIds } : {}), ...(links.blockedByTaskIds ? { blockedByTaskIds: links.blockedByTaskIds } : {}) }));
  },
  removeTask(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, tasks: p.tasks.filter((t) => t.id !== id) }));
  },
  /** Set the activities that must finish before this one starts. */
  setTaskDeps(pid: string, taskId: string, dependsOn: string[]) {
    patchProject(pid, (p) => ({ ...p, tasks: p.tasks.map((t) => t.id === taskId ? { ...t, dependsOn } : t) }));
  },
  /** Flag an activity as blocked (work stopped) or clear the block. */
  setTaskBlocked(pid: string, taskId: string, blocked: boolean, reason: string, by: string) {
    patchProject(pid, (p) => {
      const task = p.tasks.find((t) => t.id === taskId);
      const next = { ...p, tasks: p.tasks.map((t) => t.id === taskId ? { ...t, blocked, blockReason: blocked ? reason : undefined, blockedAt: blocked ? nowISO() : undefined } : t) };
      if (blocked && task && by !== STUDIO.manager) notify(STUDIO.managerEmail, `Activity blocked (${p.code})`, `${by} flagged "${task.name}" as blocked: ${reason}`);
      return logActivity(next, by, `${blocked ? "blocked" : "un-blocked"} ${task?.name ?? "an activity"}${blocked && reason ? ` — ${reason}` : ""}`, "timeline");
    });
  },
  /** Add a comment / query / snag / risk against a programme activity. The ball
      lands on the other side (studio ⇄ contractor) so it surfaces on their
      dashboard until resolved. */
  addTaskNote(pid: string, taskId: string, note: { kind: TaskNote["kind"]; text: string; by: string; fromStudio?: boolean; translated?: boolean; originalText?: string }) {
    patchProject(pid, (p) => {
      const task = p.tasks.find((t) => t.id === taskId);
      const ball: TaskNote["ball"] = note.kind === "comment" ? undefined : note.fromStudio ? "contractor" : "studio";
      const entry: TaskNote = { id: uid("tn-"), kind: note.kind, text: note.text, by: note.by, at: nowISO(), ball, translated: note.translated, originalText: note.originalText };
      const next = { ...p, tasks: p.tasks.map((t) => t.id === taskId ? { ...t, notes: [...(t.notes ?? []), entry] } : t) };
      if (note.kind !== "comment" && task && !note.fromStudio) notify(STUDIO.managerEmail, `${note.kind} on ${task.name} (${p.code})`, `${note.by}: ${note.text}`);
      return logActivity(next, note.by, `raised a ${note.kind} on ${task?.name ?? "an activity"}`, "timeline");
    });
  },
  resolveTaskNote(pid: string, taskId: string, noteId: string, resolved: boolean, by: string) {
    patchProject(pid, (p) => ({
      ...p, tasks: p.tasks.map((t) => t.id !== taskId ? t : { ...t, notes: (t.notes ?? []).map((n) => n.id === noteId ? { ...n, resolved, resolvedBy: resolved ? by : undefined, resolvedAt: resolved ? nowISO() : undefined } : n) }),
    }));
  },
  removeTaskNote(pid: string, taskId: string, noteId: string) {
    patchProject(pid, (p) => ({ ...p, tasks: p.tasks.map((t) => t.id !== taskId ? t : { ...t, notes: (t.notes ?? []).filter((n) => n.id !== noteId) }) }));
  },
  /** Programme-wide notes (not tied to one activity). */
  addProgrammeNote(pid: string, note: { kind: TaskNote["kind"]; text: string; by: string; translated?: boolean; originalText?: string }) {
    patchProject(pid, (p) => {
      const entry: TaskNote = { id: uid("pn-"), kind: note.kind, text: note.text, by: note.by, at: nowISO(), translated: note.translated, originalText: note.originalText };
      return { ...p, programmeNotes: [entry, ...(p.programmeNotes ?? [])] };
    });
  },
  resolveProgrammeNote(pid: string, noteId: string, resolved: boolean, by: string) {
    patchProject(pid, (p) => ({ ...p, programmeNotes: (p.programmeNotes ?? []).map((n) => n.id === noteId ? { ...n, resolved, resolvedBy: resolved ? by : undefined, resolvedAt: resolved ? nowISO() : undefined } : n) }));
  },
  removeProgrammeNote(pid: string, noteId: string) {
    patchProject(pid, (p) => ({ ...p, programmeNotes: (p.programmeNotes ?? []).filter((n) => n.id !== noteId) }));
  },
  importProgramme(pid: string, by: string, rows: { name: string; phase: string; start: string; end: string; progress: number; owner?: string; kind?: "activity" | "procurement" }[]) {
    patchProject(pid, (p) => {
      const tasks: GanttTask[] = rows.map((r) => {
        const existing = p.tasks.find((t) => t.name.toLowerCase() === r.name.toLowerCase());
        // a row the studio has already typed keeps its type; a new row that
        // reads like a material need date starts as one
        const kind: GanttTask["kind"] = existing?.kind ?? r.kind ?? (looksLikeProcurement(r.name) ? "procurement" : undefined);
        return {
          id: existing?.id ?? uid("gt-"),
          name: r.name, phase: r.phase || existing?.phase || "General",
          start: r.start, end: r.end, progress: kind === "procurement" ? 0 : Math.max(0, Math.min(100, r.progress)),
          owner: r.owner || existing?.owner || by,
          baselineStart: existing?.baselineStart ?? existing?.start ?? r.start,
          baselineEnd: existing?.baselineEnd ?? existing?.end ?? r.end,
          ...(kind ? { kind } : {}),
          ...(kind === "procurement" ? { procurement: existing?.procurement ?? { status: "to-order" as const }, feeds: existing?.feeds } : {}),
          ...(existing?.actualStart ? { actualStart: existing.actualStart } : {}),
          ...(existing?.actualFinish ? { actualFinish: existing.actualFinish } : {}),
          ...(existing?.dependsOn ? { dependsOn: existing.dependsOn } : {}),
          ...(existing?.notes ? { notes: existing.notes } : {}),
          ...(existing?.blocked ? { blocked: existing.blocked, blockReason: existing.blockReason, blockedAt: existing.blockedAt } : {}),
        };
      });
      const next: Project = { ...p, tasks, programmeUpdatedAt: nowISO(), programmeUpdatedBy: by };
      return logActivity(next, by, `uploaded a programme update — ${tasks.length} activities`, "timeline");
    });
  },

  /* --- materials ---------------------------------------------------------- */
  requestMaterial(pid: string, by: string, data: { item: string; spec?: string; area?: string; qty: number; unit: string; neededBy: string; note?: string; taskId?: string; leadDays?: number; translated?: boolean; originalText?: string }) {
    const id = uid("mr-");
    patchProject(pid, (p) => {
      const n = p.materials.length ? Math.max(...p.materials.map((m) => parseInt(m.ref.split("-")[1], 10))) + 1 : 1;
      const mr: MaterialRequest = { id, ref: `MR-${String(n).padStart(3, "0")}`, status: "requested", requestedBy: by, requestedAt: nowISO(), ...data };
      // mailed by the server off the stored record (notifyProjectEvents)
      return logActivity({ ...p, materials: [mr, ...p.materials] }, by, `requested ${mr.ref} — ${data.item}`, "docs");
    });
    void pmActions.pushMaterialToMindmap(pid, id); // procurement bridge — fire and forget
  },
  /** Push a request into the Task Mind Map procurement board; safe to retry. */
  async pushMaterialToMindmap(pid: string, materialId: string): Promise<boolean> {
    const p = state.projects.find((x) => x.id === pid);
    const m = p?.materials.find((x) => x.id === materialId);
    if (!p || !m || m.mindmap) return !!m?.mindmap;
    try {
      const res = await fetch("/api/mindmap/material", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          project: `${p.name} (${p.code})`,
          product: `${m.item}${m.spec ? ` — ${m.spec}` : ""}`,
          quantity: `${m.qty} ${m.unit}`,
          notes: `${m.ref} from Atelier Project Studio · requested by ${m.requestedBy}${m.note ? ` · ${m.note}` : ""}`,
          neededBy: m.neededBy,
          priority: "med",
        }),
      });
      const out = await res.json() as { linked?: boolean; materialId?: string; taskNodeId?: string };
      if (!res.ok || !out.linked || !out.materialId) return false;
      const link: MindmapLink = { materialId: out.materialId, taskNodeId: out.taskNodeId, status: "requested", pushedAt: nowISO() };
      patchProject(pid, (pr) => logActivity(
        { ...pr, materials: pr.materials.map((x) => x.id === materialId ? { ...x, mindmap: link } : x) },
        "Mind Map", `picked up ${m.ref} on the procurement board`, "docs"));
      return true;
    } catch {
      return false;
    }
  },
  /** Ask the Mind Map for the current procurement status of a linked request. */
  async probeMaterialStatus(pid: string, materialId: string): Promise<MindmapLink | null> {
    const p = state.projects.find((x) => x.id === pid);
    const m = p?.materials.find((x) => x.id === materialId);
    if (!p || !m?.mindmap) return null;
    try {
      const res = await fetch(`/api/mindmap/material/${m.mindmap.materialId}/status`, { headers: authHeader() });
      if (!res.ok) return null;
      const out = await res.json() as { status?: string; latestNote?: string };
      if (!out.status) return null;
      const link: MindmapLink = { ...m.mindmap, status: out.status, latestNote: out.latestNote, lastProbedAt: nowISO() };
      patchProject(pid, (pr) => ({ ...pr, materials: pr.materials.map((x) => x.id === materialId ? { ...x, mindmap: link } : x) }));
      return link;
    } catch {
      return null;
    }
  },
  /** Contractor marks a material shipped / in transit, with carrier, tracking
      and an ETA — surfaces it in the "In transit" view and nudges the studio. */
  markMaterialShipped(pid: string, id: string, data: { carrier?: string; tracking?: string; eta?: string }, by: string) {
    patchProject(pid, (p) => {
      const m = p.materials.find((x) => x.id === id);
      if (!m) return p;
      const shipment = { at: nowISO(), by, carrier: data.carrier?.trim() || undefined, tracking: data.tracking?.trim() || undefined, eta: data.eta || undefined };
      if (by !== STUDIO.manager) notify(STUDIO.managerEmail, `${m.ref} in transit — ${m.item} (${p.code})`,
        `${by} marked ${m.ref} (${m.item}) as shipped${shipment.carrier ? ` via ${shipment.carrier}` : ""}${shipment.eta ? `, ETA ${shipment.eta}` : ""}. Awaiting delivery to site.`);
      return logActivity({ ...p, materials: p.materials.map((x) => x.id === id ? { ...x, status: "in-transit", shipment, updatedAt: nowISO() } : x) },
        by, `marked ${m.ref} in transit`, "docs");
    });
  },
  setMaterialStatus(pid: string, id: string, status: MaterialStatus, by: string, supplier?: string) {
    patchProject(pid, (p) => {
      const m = p.materials.find((x) => x.id === id);
      if (!m) return p;
      const next: Project = { ...p, materials: p.materials.map((x) => x.id === id ? { ...x, status, supplier: supplier ?? x.supplier, updatedAt: nowISO() } : x) };
      return logActivity(next, by, `${status === "rejected" ? "rejected" : "moved"} ${m.ref} ${status === "rejected" ? "" : `to ${status}`}`.trim(), "docs");
    });
  },

  /* --- punch list ---------------------------------------------------------- */
  addPunch(pid: string, by: string, data: { title: string; location: string; trade: string; assignee: string; due?: string }) {
    patchProject(pid, (p) => {
      const n = p.punch.length ? Math.max(...p.punch.map((x) => parseInt(x.ref.split("-")[1], 10))) + 1 : 1;
      const item: PunchItem = { id: uid("pl-"), ref: `PL-${String(n).padStart(3, "0")}`, status: "open", raisedAt: nowISO(), ...data };
      return logActivity({ ...p, punch: [item, ...p.punch] }, by, `raised punch item ${item.ref} — ${data.title}`, "docs");
    });
  },
  setPunchStatus(pid: string, id: string, status: PunchItem["status"]) {
    patchProject(pid, (p) => ({ ...p, punch: p.punch.map((x) => x.id === id ? { ...x, status } : x) }));
  },

  /* --- MEP & services ------------------------------------------------------ */
  addMepSystem(pid: string, by: string, data: { discipline: MepDiscipline; name: string; zone: string; contractor?: string; note?: string }) {
    patchProject(pid, (p) => {
      const rows = p.mep ?? [];
      const n = rows.length ? Math.max(...rows.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const sys: MepSystem = { id: uid("mep-"), ref: `MEP-${String(n).padStart(3, "0")}`, stage: "not-started", tests: [], createdAt: nowISO(), ...data };
      return logActivity({ ...p, mep: [sys, ...rows] }, by, `added ${sys.ref} to the services register — ${data.name}`, "docs");
    });
  },
  updateMepSystem(pid: string, id: string, patch: Partial<Pick<MepSystem, "discipline" | "name" | "zone" | "contractor" | "note">>) {
    patchProject(pid, (p) => ({ ...p, mep: (p.mep ?? []).map((x) => x.id === id ? { ...x, ...patch, updatedAt: nowISO() } : x) }));
  },
  deleteMepSystem(pid: string, id: string, by: string) {
    patchProject(pid, (p) => {
      const sys = (p.mep ?? []).find((x) => x.id === id);
      if (!sys) return p;
      return logActivity({ ...p, mep: (p.mep ?? []).filter((x) => x.id !== id) }, by, `removed ${sys.ref} (${sys.name}) from the services register`, "docs");
    });
  },
  /** Move a system along the install lifecycle. Commissioning is the studio's
      signature, so that step stamps who signed and gets an activity line. */
  setMepStage(pid: string, id: string, stage: MepStage, by: string) {
    patchProject(pid, (p) => {
      const sys = (p.mep ?? []).find((x) => x.id === id);
      if (!sys || sys.stage === stage) return p;
      const commissioned = stage === "commissioned";
      const next: Project = {
        ...p,
        mep: (p.mep ?? []).map((x) => x.id === id ? {
          ...x, stage, updatedAt: nowISO(),
          commissionedBy: commissioned ? by : undefined,
          commissionedAt: commissioned ? nowISO() : undefined,
        } : x),
      };
      return commissioned
        ? logActivity(next, by, `signed off ${sys.ref} (${sys.name}) as commissioned`, "docs")
        : next;
    });
  },
  addMepTest(pid: string, systemId: string, name: string) {
    patchProject(pid, (p) => ({
      ...p,
      mep: (p.mep ?? []).map((x) => x.id === systemId ? { ...x, tests: [...x.tests, { id: uid("mt-"), name, result: "pending" as MepTestResult }], updatedAt: nowISO() } : x),
    }));
  },
  removeMepTest(pid: string, systemId: string, testId: string) {
    patchProject(pid, (p) => ({
      ...p,
      mep: (p.mep ?? []).map((x) => x.id === systemId ? { ...x, tests: x.tests.filter((t) => t.id !== testId), updatedAt: nowISO() } : x),
    }));
  },
  setMepTestResult(pid: string, systemId: string, testId: string, result: MepTestResult, by: string, note?: string) {
    patchProject(pid, (p) => {
      const sys = (p.mep ?? []).find((x) => x.id === systemId);
      const test = sys?.tests.find((t) => t.id === testId);
      if (!sys || !test) return p;
      const cleared = result === "pending";   // undo a mis-tap: the record un-signs itself
      const next: Project = {
        ...p,
        mep: (p.mep ?? []).map((x) => x.id !== systemId ? x : {
          ...x, updatedAt: nowISO(),
          tests: x.tests.map((t) => t.id === testId ? {
            ...t, result, note: note?.trim() || t.note,
            at: cleared ? undefined : nowISO(), by: cleared ? undefined : by,
          } : t),
        }),
      };
      return result === "failed"
        ? logActivity(next, by, `recorded a failed ${test.name} on ${sys.ref} (${sys.name})`, "docs")
        : next;
    });
  },
  /** A failed test becomes a punch item, cross-referenced both ways, so the
      defect enters the same close-out loop as everything else on the job. */
  raiseMepPunch(pid: string, systemId: string, testId: string, by: string) {
    patchProject(pid, (p) => {
      const sys = (p.mep ?? []).find((x) => x.id === systemId);
      const test = sys?.tests.find((t) => t.id === testId);
      if (!sys || !test || test.raisedRef) return p;
      const n = p.punch.length ? Math.max(...p.punch.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const ref = `PL-${String(n).padStart(3, "0")}`;
      const item: PunchItem = {
        id: uid("pl-"), ref, title: `${test.name} failed — ${sys.name}`,
        location: sys.zone, trade: "MEP", assignee: sys.contractor ?? p.contractor.company,
        status: "open", raisedAt: nowISO(), fromMep: sys.ref, ballInCourt: "contractor",
        comments: test.note ? [{ id: uid("cm-"), by, at: nowISO(), text: test.note }] : undefined,
      };
      const next: Project = {
        ...p,
        punch: [item, ...p.punch],
        mep: (p.mep ?? []).map((x) => x.id !== systemId ? x : { ...x, tests: x.tests.map((t) => t.id === testId ? { ...t, raisedRef: ref } : t) }),
      };
      return logActivity(next, by, `raised punch item ${ref} from a failed ${test.name} on ${sys.ref}`, "docs");
    });
  },

  /* --- renders ------------------------------------------------------------ */
  setMainRender(pid: string, photoId: string | undefined) {
    // a different render gets a fresh frame rather than the last one's crop
    patchProject(pid, (p) => ({ ...p, mainRenderId: photoId, mainRenderCrop: undefined }));
  },
  /** Reframe the hero — focal point and zoom. Undefined restores centre/fill. */
  setRenderCrop(pid: string, crop: RenderCrop | undefined) {
    patchProject(pid, (p) => ({ ...p, mainRenderCrop: crop }));
  },

  /* --- letterhead marks ---------------------------------------------------- */
  /** Set or clear one of the two marks that head every issued report. Held on
      the project itself rather than in the photo store: a logo is small, and a
      report generated offline must still be able to print its letterhead. */
  setLogo(pid: string, which: "studio" | "contractor", dataUri: string | undefined, by: string) {
    const key = which === "studio" ? "studioLogo" : "contractorLogo";
    const at = which === "studio" ? "studioLogoAt" : "contractorLogoAt";
    patchProject(pid, (p) => logActivity(
      { ...p, branding: { ...p.branding, [key]: dataUri, [at]: dataUri ? nowISO() : undefined } },
      by,
      dataUri ? `Set the ${which} mark for report letterheads` : `Removed the ${which} mark from report letterheads`,
      "docs",
    ));
    // remember a contractor's logo against their company so re-appointing them
    // on another project brings the mark with them
    if (which === "contractor" && dataUri) {
      const proj = state.projects.find((x) => x.id === pid);
      if (proj?.contractor.company && proj.contractor.company !== "To be appointed") peopleActions.setCompanyLogo(proj.contractor.company, dataUri);
    }
  },
  /** Name printed beside the contractor's mark. Defaults to the company. */
  setBrandingName(pid: string, name: string) {
    patchProject(pid, (p) => ({ ...p, branding: { ...p.branding, contractorName: name.trim() || undefined } }));
  },

  /** Appoint (or re-appoint) the main contractor after the project exists.
      Keeps the same access code so any invite already sent stays valid; mints
      one the first time. Also mirrors the appointment onto the contractor team
      seat and emails the code when an address is given. */
  appointContractor(pid: string, data: { name: string; company: string; email: string }, by: string) {
    const company = data.company.trim();
    if (!company) return;
    const name = data.name.trim() || company;
    const email = data.email.trim();
    peopleActions.remember({ name, company, email, role: "contractor" });
    const knownLogo = companyLogo(company);   // reuse this contractor's mark if on record
    const sharedCode = existingContractorCode(company);  // one login across a firm's jobs
    patchProject(pid, (p) => {
      const prefix = p.code.split("-")[0] || "PRJ";
      const placeholder = !p.contractor.accessCode || p.contractor.company === "To be appointed";
      const code = placeholder
        ? (sharedCode ?? uniqueAccessCode(prefix, company, "SITE", p.team.map((m) => m.accessCode)))
        : p.contractor.accessCode;
      const contractor = { name, company, email, accessCode: code };
      // Update the existing contractor seat, or add one if none exists yet.
      let seatFound = false;
      const team = p.team.map((m) => {
        if (m.role !== "contractor") return m;
        seatFound = true;
        return { ...m, name, company, email, accessCode: code };
      });
      if (!seatFound) {
        team.push({ id: uid("tm-"), name, company, email, role: "contractor", accessCode: code, invitedAt: nowISO(), status: "invited" });
      }
      if (email) {
        void notify([email], `You've been appointed on ${p.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit}: ${company} has been appointed as contractor on ${p.name}. Tap the link below to open your projects directly — your access code is ${code} if you ever need to sign in manually. File daily reports, receive drawings and site instructions.`,
          { link: `${APP_URL}/join?c=${encodeURIComponent(code)}`, label: "Open my projects" });
      }
      const branding = knownLogo && !p.branding?.contractorLogo
        ? { ...p.branding, contractorLogo: knownLogo, contractorLogoAt: nowISO() }
        : p.branding;
      return logActivity({ ...p, contractor, team, branding }, by, `Appointed ${company} as contractor`, "docs");
    });
  },

  /* --- coordination models ------------------------------------------------ */
  addModel(pid: string, m: {
    name: string; discipline?: string; format: ModelFormat; fileId: string;
    fileName: string; size?: string; note?: string; by: string; issued?: boolean;
  }): string {
    const id = uid("mdl-");
    patchProject(pid, (p) => {
      const rows = p.models ?? [];
      // same name + discipline supersedes: the model carries its own revision
      const prior = rows.filter((x) => x.name.toLowerCase() === m.name.trim().toLowerCase());
      const rev = `R${prior.length + 1}`;
      const model: BimModel = {
        id, name: m.name.trim(), discipline: m.discipline, format: m.format, revision: rev,
        fileId: m.fileId, fileName: m.fileName, size: m.size, note: m.note?.trim() || undefined,
        uploadedAt: nowISO(), uploadedBy: m.by, issued: m.issued ?? true,
      };
      return logActivity({ ...p, models: [model, ...rows] }, m.by,
        `uploaded model ${model.name} ${rev}${model.issued ? " and issued it to site" : ""}`, "docs");
    });
    return id;
  },
  setModelIssued(pid: string, id: string, issued: boolean, by: string) {
    patchProject(pid, (p) => {
      const m = (p.models ?? []).find((x) => x.id === id);
      return logActivity(
        { ...p, models: (p.models ?? []).map((x) => x.id === id ? { ...x, issued } : x) },
        by, `${issued ? "issued" : "withdrew"} model ${m?.name ?? ""} ${m?.revision ?? ""}`.trim(), "docs");
    });
  },
  removeModel(pid: string, id: string, by: string) {
    patchProject(pid, (p) => {
      const m = (p.models ?? []).find((x) => x.id === id);
      return logActivity({ ...p, models: (p.models ?? []).filter((x) => x.id !== id) }, by,
        `removed model ${m?.name ?? ""} ${m?.revision ?? ""}`.trim(), "docs");
    });
  },

  /* --- client portal ------------------------------------------------------------ */
  setClientView(pid: string, cfg: ClientViewConfig) {
    patchProject(pid, (p) => logActivity({ ...p, clientView: cfg }, STUDIO.manager, "updated what the client portal shows", "docs"));
  },

  /* --- project phase + drawing stage/issue + share packages -------------- */
  advancePhase(pid: string, phase: ProjectPhase, by: string) {
    patchProject(pid, (p) => logActivity({ ...p, phase, phaseAdvancedAt: nowISO() }, by, `advanced the project to the ${phase} phase`, "timeline"));
  },
  /** Mark a sheet preliminary or issued (formally released). */
  setDrawingStage(pid: string, docId: string, stage: "preliminary" | "issued", by: string) {
    patchProject(pid, (p) => {
      const d = p.drawings.find((x) => x.id === docId);
      const next = { ...p, drawings: p.drawings.map((x) => x.id === docId ? { ...x, stage, issuedAt: stage === "issued" ? nowISO() : x.issuedAt } : x) };
      return logActivity(next, by, `${stage === "issued" ? "issued" : "marked preliminary"} ${d?.number ?? "a sheet"}`, "docs");
    });
  },
  /** Issue a batch of sheets (optionally to a named recipient/trade). */
  issueDrawings(pid: string, docIds: string[], to: string | undefined, by: string) {
    patchProject(pid, (p) => {
      const set = new Set(docIds);
      const next = { ...p, drawings: p.drawings.map((x) => set.has(x.id) ? { ...x, stage: "issued" as const, issuedAt: nowISO(), issuedTo: to || x.issuedTo } : x) };
      return logActivity(next, by, `issued ${docIds.length} sheet${docIds.length === 1 ? "" : "s"}${to ? ` to ${to}` : ""}`, "docs");
    });
  },
  /** Create a tokened drawing package to share; returns it (with the token). */
  createDrawingShare(pid: string, data: {
    title: string; audience: "contractor" | "client" | "subcontractor";
    recipientName?: string; recipientEmail?: string; stage: "preliminary" | "issued";
    docIds: string[]; disciplines?: string[]; note?: string; noDownload?: boolean;
  }, by: string): DrawingShare {
    const token = Array.from(crypto.getRandomValues(new Uint8Array(20))).map((b) => b.toString(16).padStart(2, "0")).join("");
    let created: DrawingShare = { id: uid("shr-"), ref: "PKG-000", token, sharedBy: by, sharedAt: nowISO(), clientNotes: [], ...data };
    patchProject(pid, (p) => {
      const n = (p.drawingShares ?? []).length ? Math.max(...(p.drawingShares ?? []).map((s) => parseInt(s.ref.split("-")[1], 10) || 0)) + 1 : 1;
      created = { ...created, ref: `PKG-${String(n).padStart(3, "0")}` };
      const link = `${APP_URL}/pkg/${token}`;
      if (data.recipientEmail) {
        void notify([data.recipientEmail], `Drawings shared — ${data.title} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit} has shared ${data.docIds.length} drawing${data.docIds.length === 1 ? "" : "s"} with you${data.stage === "preliminary" ? " for review" : ""}.\n\nOpen the link to view the sheets in the studio's viewer${data.noDownload ? "" : " and download them"}${data.audience === "client" ? " and leave any comments" : ""}:\n${link}`,
          { link, label: "Open the drawing package" });
      }
      return logActivity({ ...p, drawingShares: [created, ...(p.drawingShares ?? [])] }, by, `shared ${created.ref} — ${data.title}`, "docs");
    });
    return created;
  },
  /** Publish (or re-issue) the employer's live executive view. One per project;
      calling again mints a fresh token, so the old link dies. */
  publishExecShare(pid: string, opts: { showCost?: boolean; showPhotos?: boolean; showForecast?: boolean; note?: string; email?: string }, by: string): string {
    const token = Array.from(crypto.getRandomValues(new Uint8Array(20))).map((b) => b.toString(16).padStart(2, "0")).join("");
    patchProject(pid, (p) => {
      const share: ExecShare = { token, createdAt: nowISO(), createdBy: by, views: 0, ...opts };
      const link = `${APP_URL}/exec/${token}`;
      const to = opts.email?.trim() || p.client.email;
      if (to) {
        void notify([to], `Project dashboard — ${p.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit} has opened a live dashboard for ${p.name}. It always shows the current position — no sign-in needed.\n\n${opts.note?.trim() ? `${opts.note.trim()}\n\n` : ""}${link}`,
          { link, label: "Open the dashboard" });
      }
      return logActivity({ ...p, execShare: share }, by, "published the client executive dashboard", "docs");
    });
    return token;
  },
  revokeExecShare(pid: string, by: string) {
    patchProject(pid, (p) => p.execShare
      ? logActivity({ ...p, execShare: { ...p.execShare, revokedAt: nowISO() } }, by, "revoked the client dashboard link", "docs")
      : p);
  },
  updateExecShare(pid: string, patch: Partial<Pick<ExecShare, "showCost" | "showPhotos" | "showForecast" | "note">>) {
    patchProject(pid, (p) => p.execShare ? { ...p, execShare: { ...p.execShare, ...patch } } : p);
  },
  removeDrawingShare(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, drawingShares: (p.drawingShares ?? []).filter((s) => s.id !== id) }));
  },
  resolveShareNote(pid: string, shareId: string, noteId: string, resolved: boolean, by: string) {
    patchProject(pid, (p) => ({
      ...p, drawingShares: (p.drawingShares ?? []).map((s) => s.id !== shareId ? s
        : { ...s, clientNotes: (s.clientNotes ?? []).map((n) => n.id === noteId ? { ...n, resolved, resolvedAt: resolved ? nowISO() : undefined } : n) }),
    }));
  },
  /* --- persistent sheet redlines ------------------------------------- */
  addRedline(pid: string, docId: string, data: { markups: Markup[]; note?: string; by: string; rev?: string; kind?: "markup" | "revision-cloud"; comparedTo?: string }) {
    patchProject(pid, (p) => {
      const redline: SheetRedline = { id: uid("rl-"), rev: data.rev, kind: data.kind, comparedTo: data.comparedTo, markups: data.markups, note: data.note?.trim() || undefined, by: data.by, at: nowISO() };
      const drawings = p.drawings.map((d) => d.id === docId ? { ...d, redlines: [...(d.redlines ?? []), redline] } : d);
      const doc = p.drawings.find((d) => d.id === docId);
      // revision clouds are found by the app, not drawn by a person — they
      // neither mail the studio nor crowd the activity feed
      if (data.kind === "revision-cloud") return { ...p, drawings };
      if (data.by !== STUDIO.manager) notify(STUDIO.managerEmail, `Redline on ${doc?.number ?? "a drawing"} (${p.code})`, `${data.by} marked up ${doc?.number}${data.note ? `: ${data.note}` : ""}.`);
      return logActivity({ ...p, drawings }, data.by, `redlined ${doc?.number ?? "a drawing"}`, "docs");
    });
  },

  resolveRedline(pid: string, docId: string, redlineId: string, resolved: boolean, by: string) {
    patchProject(pid, (p) => ({
      ...p, drawings: p.drawings.map((d) => d.id !== docId ? d
        : { ...d, redlines: (d.redlines ?? []).map((r) => r.id === redlineId ? { ...r, resolved, resolvedBy: resolved ? by : undefined, resolvedAt: resolved ? nowISO() : undefined } : r) }),
    }));
  },
  removeRedline(pid: string, docId: string, redlineId: string) {
    patchProject(pid, (p) => ({
      ...p, drawings: p.drawings.map((d) => d.id !== docId ? d : { ...d, redlines: (d.redlines ?? []).filter((r) => r.id !== redlineId) }),
    }));
  },
  /* --- site telemetry --------------------------------------------------- */
  /** Mint (or rotate) the key a gate device or supplier system posts with.
      Rotating kills the old key immediately — that's the point of rotating. */
  rotateTelemetryKey(pid: string, by: string): string {
    const key = Array.from(crypto.getRandomValues(new Uint8Array(20))).map((b) => b.toString(16).padStart(2, "0")).join("");
    patchProject(pid, (p) => logActivity({ ...p, telemetryKey: key }, by, "issued a new site telemetry key", "docs"));
    return key;
  },
  clearTelemetryKey(pid: string, by: string) {
    patchProject(pid, (p) => logActivity({ ...p, telemetryKey: undefined }, by, "revoked the site telemetry key", "docs"));
  },
  addTelemetry(pid: string, e: Omit<TelemetryEvent, "id">) {
    patchProject(pid, (p) => {
      // a delivery gets matched to the material it satisfies, where the name lines up
      let materialId = e.materialId;
      if (!materialId && e.kind === "delivery" && e.item) {
        const needle = e.item.toLowerCase();
        materialId = p.materials.find((m) =>
          m.status !== "delivered" && (m.item.toLowerCase().includes(needle) || needle.includes(m.item.toLowerCase().split(",")[0])))?.id;
      }
      const ev: TelemetryEvent = { ...e, materialId, id: uid("tel-") };
      return { ...p, telemetry: [ev, ...(p.telemetry ?? [])].slice(0, 500) };
    });
  },
  removeTelemetry(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, telemetry: (p.telemetry ?? []).filter((x) => x.id !== id) }));
  },
  /** Accept a delivery reading as the truth for the material it matched. */
  confirmDelivery(pid: string, telemetryId: string, by: string) {
    patchProject(pid, (p) => {
      const ev = (p.telemetry ?? []).find((x) => x.id === telemetryId);
      if (!ev?.materialId) return p;
      const mat = p.materials.find((m) => m.id === ev.materialId);
      return logActivity({
        ...p,
        materials: p.materials.map((m) => m.id === ev.materialId ? { ...m, status: "delivered" as MaterialStatus, updatedAt: nowISO() } : m),
      }, by, `marked ${mat?.ref ?? "a material"} delivered from the gate record`, "docs");
    });
  },

  /* --- foreign currency ------------------------------------------------ */
  /** Set the rate for a currency. One row per code — the newest replaces it,
      but prices already recorded keep the rate they were converted at. */
  setFxRate(pid: string, data: { code: string; rate: number; by: string; note?: string }) {
    const code = data.code.trim().toUpperCase();
    if (!code || !(data.rate > 0)) return;
    patchProject(pid, (p) => logActivity({
      ...p,
      fx: [{ code, rate: data.rate, setAt: nowISO(), setBy: data.by, note: data.note?.trim() || undefined },
        ...(p.fx ?? []).filter((f) => f.code !== code)],
    }, data.by, `set ${code} at ${data.rate} ${p.currency}`, "docs"));
  },
  removeFxRate(pid: string, code: string) {
    patchProject(pid, (p) => ({ ...p, fx: (p.fx ?? []).filter((f) => f.code !== code) }));
  },
  /** Price a material in a foreign currency; the conversion is frozen here. */
  setMaterialForeignPrice(pid: string, id: string, data: { amount: number; code: string }) {
    patchProject(pid, (p) => {
      const rate = (p.fx ?? []).find((f) => f.code === data.code.toUpperCase())?.rate;
      if (!rate) return p;   // no rate on file — refuse rather than guess
      const foreign = { amount: data.amount, code: data.code.toUpperCase(), rate, converted: data.amount * rate, at: nowISO() };
      return { ...p, materials: p.materials.map((m) => m.id === id ? { ...m, foreign } : m) };
    });
  },

  /* --- client approvals (e-signature) ---------------------------------- */
  /** Ask the employer to sign for a decision. Idempotent per record — asking
      twice re-opens the same request rather than stacking duplicates. */
  requestApproval(pid: string, data: { target: ApprovalTarget; recordId: string; ref: string; title: string; amount?: number; days?: number; by: string; note?: string }) {
    patchProject(pid, (p) => {
      const rows = (p.approvals ?? []).filter((a) => !(a.recordId === data.recordId && !a.decision));
      const req: ClientApproval = {
        id: uid("apr-"), target: data.target, recordId: data.recordId, ref: data.ref, title: data.title,
        amount: data.amount, days: data.days, currency: p.currency,
        requestedAt: nowISO(), requestedBy: data.by, note: data.note,
      };
      const to = p.team.filter((m) => m.role === "client" && m.email).map((m) => m.email);
      if (to.length || p.client.email) {
        void notify(to.length ? to : [p.client.email], `Your approval is needed — ${data.ref} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit}: ${data.title}\n\n${data.amount != null ? `Amount: ${data.amount.toLocaleString()} ${p.currency}\n` : ""}${data.days != null ? `Time: ${data.days} days\n` : ""}${data.note ? `\n${data.note}\n` : ""}\nOpen your portal to review and sign.`,
          { link: `${APP_URL}/site/${p.id}`, label: "Review and sign" });
      }
      return logActivity({ ...p, approvals: [req, ...rows] }, data.by, `asked the client to sign for ${data.ref}`, "docs");
    });
  },
  /** The employer signs. The signature and the typed name are both kept —
      one is the mark, the other is the assertion of who made it. */
  signApproval(pid: string, id: string, data: { decision: "approved" | "rejected"; signedName: string; signature?: string; note?: string }) {
    patchProject(pid, (p) => {
      const rows = (p.approvals ?? []).map((a) => a.id === id
        ? { ...a, decision: data.decision, decidedAt: nowISO(), signedName: data.signedName.trim(), signature: data.signature, note: data.note?.trim() || a.note, agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : undefined }
        : a);
      const apr = rows.find((a) => a.id === id);
      if (apr) {
        void notify(STUDIO.managerEmail, `${apr.ref} ${data.decision} by the client (${p.code})`,
          `${data.signedName} ${data.decision} ${apr.ref} — ${apr.title}${apr.amount != null ? ` (${apr.amount.toLocaleString()} ${apr.currency ?? p.currency})` : ""}.${data.note ? `\n\n"${data.note.trim()}"` : ""}`);
      }
      return logActivity({ ...p, approvals: rows }, data.signedName, `${data.decision} ${apr?.ref ?? "an approval"}`, "docs");
    });
  },
  withdrawApproval(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, approvals: (p.approvals ?? []).filter((a) => a.id !== id) }));
  },

  /* --- bill of quantities --------------------------------------------- */
  addBoqItems(pid: string, items: Omit<BoqItem, "id">[], by: string) {
    if (!items.length) return;
    patchProject(pid, (p) => logActivity(
      { ...p, boq: [...(p.boq ?? []), ...items.map((i) => ({ ...i, id: uid("boq-") }))] },
      by, `added ${items.length} bill item${items.length === 1 ? "" : "s"}`, "docs"));
  },
  /** Record the quantity actually placed against a bill item. */
  measureBoq(pid: string, id: string, placed: number) {
    patchProject(pid, (p) => ({ ...p, boq: (p.boq ?? []).map((i) => i.id === id ? { ...i, placed: Math.max(0, placed) } : i) }));
  },
  updateBoqItem(pid: string, id: string, patch: Partial<BoqItem>) {
    patchProject(pid, (p) => ({ ...p, boq: (p.boq ?? []).map((i) => i.id === id ? { ...i, ...patch } : i) }));
  },
  removeBoqItem(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, boq: (p.boq ?? []).filter((i) => i.id !== id) }));
  },

  /* --- construction bottlenecks ----------------------------------------
     One record per thing actually holding the job up. Every action here
     tells every stakeholder by email the moment it happens; the worker's
     morning cron keeps reminding whoever holds the ball until it's closed. */
  /* --- long-lead items ----------------------------------------------------- */
  addLongLead(pid: string, by: string, data: {
    name: string; leadDays: number; category?: string; spec?: string; qty?: string; bufferDays?: number;
    taskIds?: string[]; requiredOn?: string; supplier?: string; note?: string; rationale?: string; source?: "ai" | "manual"; materialId?: string;
  }): string {
    const id = uid("ll-");
    patchProject(pid, (p) => {
      const list = p.longLead ?? [];
      const n = list.length ? Math.max(...list.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      const item: LongLeadItem = {
        id, ref: `LL-${String(n).padStart(3, "0")}`, name: data.name.trim(), category: data.category, spec: data.spec, qty: data.qty,
        leadDays: Math.max(0, Math.round(data.leadDays)), bufferDays: data.bufferDays, taskIds: data.taskIds ?? [], requiredOn: data.requiredOn,
        status: "identified", supplier: data.supplier, note: data.note, rationale: data.rationale, source: data.source ?? "manual",
        materialId: data.materialId, createdAt: nowISO(), updatedAt: nowISO(), by,
      };
      return logActivity({ ...p, longLead: [...list, item] }, by, `logged long-lead item ${item.ref} — ${item.name}`, "docs");
    });
    return id;
  },
  updateLongLead(pid: string, id: string, patch: Partial<LongLeadItem>, by: string) {
    patchProject(pid, (p) => ({
      ...p,
      longLead: (p.longLead ?? []).map((x) => (x.id === id ? { ...x, ...patch, id: x.id, ref: x.ref, updatedAt: nowISO(), by } : x)),
    }));
  },
  setLongLeadStatus(pid: string, id: string, status: LongLeadStatus, by: string, extra: Partial<LongLeadItem> = {}) {
    patchProject(pid, (p) => {
      const item = (p.longLead ?? []).find((x) => x.id === id);
      if (!item) return p;
      const today = nowISO().slice(0, 10);
      const stamp: Partial<LongLeadItem> =
        status === "ordered" ? { orderedOn: extra.orderedOn ?? item.orderedOn ?? today }
        : status === "on-site" ? { onSiteOn: extra.onSiteOn ?? today } : {};
      const next: LongLeadItem = { ...item, ...extra, ...stamp, status, updatedAt: nowISO(), by };
      const label = LONG_LEAD_LABEL[status].toLowerCase();
      if (by !== STUDIO.manager && (status === "ordered" || status === "in-transit" || status === "on-site")) {
        notify(STUDIO.managerEmail, `${item.ref} ${label} — ${item.name} (${p.code})`,
          `${by} marked ${item.ref} ${item.name} as ${label}.${next.eta ? ` ETA ${next.eta}.` : ""}${next.poRef ? ` PO ${next.poRef}.` : ""}`);
      }
      return logActivity({ ...p, longLead: (p.longLead ?? []).map((x) => (x.id === id ? next : x)) }, by, `${item.ref} ${item.name} — ${label}`, "docs");
    });
  },
  removeLongLead(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, longLead: (p.longLead ?? []).filter((x) => x.id !== id) }));
  },
  /** The probe's suggestions folded in — names not already logged, each tied
      to the programme activities it named. Returns how many were added. */
  mergeLongLeadProbe(pid: string, by: string, items: { name: string; category?: string; leadDays: number; rationale?: string; affectedTasks?: string[]; spec?: string }[]): number {
    let added = 0;
    patchProject(pid, (p) => {
      const list = [...(p.longLead ?? [])];
      const have = new Set(list.map((x) => x.name.toLowerCase().trim()));
      let n = list.length ? Math.max(...list.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) : 0;
      for (const it of items) {
        const name = (it.name ?? "").trim();
        if (!name || have.has(name.toLowerCase())) continue;
        n++;
        have.add(name.toLowerCase());
        list.push({
          id: uid("ll-"), ref: `LL-${String(n).padStart(3, "0")}`, name, category: it.category, spec: it.spec,
          leadDays: Math.max(0, Math.round(it.leadDays || 0)), taskIds: matchTasks(p, it.affectedTasks ?? []),
          status: "identified", rationale: it.rationale, source: "ai", createdAt: nowISO(), updatedAt: nowISO(), by,
        });
        added++;
      }
      if (!added) return p;
      return logActivity({ ...p, longLead: list }, by, `AI probe logged ${added} long-lead item${added === 1 ? "" : "s"}`, "docs");
    });
    return added;
  },
  /** Call an item forward: the contractor (and the supplier, when known) are
      told the order-by date and what it gates, and the item is stamped. */
  callForwardLongLead(pid: string, id: string, by: string, note?: string, supplierEmail?: string) {
    patchProject(pid, (p) => {
      const item = (p.longLead ?? []).find((x) => x.id === id);
      if (!item) return p;
      const sch = longLeadSchedule(p, item);
      const to = [...new Set([
        ...(p.team ?? []).filter((m) => m.role === "contractor" && m.email).map((m) => m.email),
        ...(p.contractor?.email ? [p.contractor.email] : []),
        ...(supplierEmail && supplierEmail.includes("@") ? [supplierEmail] : []),
      ])];
      const body =
        `${item.ref} — ${item.name} on ${p.name} (${p.code}) must be ORDERED ${sch.orderBy ? `by ${sch.orderBy}` : "now"} ` +
        `to be on site ${sch.requiredOn ? `by ${sch.requiredOn}` : "in time"}. Lead time ${item.leadDays} days` +
        `${item.bufferDays ?? 14 ? ` plus a ${item.bufferDays ?? 14}-day buffer` : ""}.` +
        (sch.tasks.length ? `\n\nActivities that cannot start without it: ${sch.tasks.map((t) => `${t.name} (starts ${t.start})`).join("; ")}.` : "") +
        (item.spec ? `\n\nSpecification: ${item.spec}` : "") + (item.qty ? `\nQuantity: ${item.qty}` : "") +
        (item.supplier ? `\nSupplier: ${item.supplier}` : "") + (note ? `\n\n${note}` : "") +
        `\n\nPlease confirm the order date and the ETA in the project cockpit.`;
      const origin = typeof location !== "undefined" ? location.origin : "https://studio.studiogy.design";
      if (to.length) void notify(to, `Call forward — ${item.ref} ${item.name} (${p.code})`, body, { link: `${origin}/pm/${p.id}/longlead`, label: "Open long-lead items" });
      return logActivity(
        { ...p, longLead: (p.longLead ?? []).map((x) => (x.id === id ? { ...x, calledForwardOn: nowISO(), updatedAt: nowISO() } : x)) },
        by, `called forward ${item.ref} — ${item.name}`, "docs",
      );
    });
  },

  raiseBottleneck(pid: string, data: {
    title: string; detail?: string; kind: BottleneckKind; impact: BottleneckImpact;
    ballInCourt: BallInCourt; dueBy?: string; blockedTaskIds?: string[];
    stakeholders: BottleneckStakeholder[];
  }, by: string): string {
    let ref = "";
    patchProject(pid, (p) => {
      const rows = p.bottlenecks ?? [];
      const n = rows.length ? Math.max(...rows.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      ref = `BN-${String(n).padStart(3, "0")}`;
      const bn: Bottleneck = {
        id: uid("bn-"), ref, title: data.title.trim(), detail: data.detail?.trim() || undefined,
        kind: data.kind, impact: data.impact, status: "open", ballInCourt: data.ballInCourt,
        raisedBy: by, raisedAt: nowISO(), dueBy: data.dueBy || undefined,
        blockedTaskIds: data.blockedTaskIds?.length ? data.blockedTaskIds : undefined,
        stakeholders: data.stakeholders, comments: [],
      };
      const blocked = (bn.blockedTaskIds ?? []).map((id) => (p.tasks ?? []).find((t) => t.id === id)?.name).filter(Boolean);
      void notify(bn.stakeholders.map((s) => s.email),
        `${ref} · ${bn.impact.toUpperCase()} bottleneck — ${bn.title} (${p.code})`,
        `${by} has raised a bottleneck on ${p.name}:\n\n${bn.title}${bn.detail ? `\n\n${bn.detail}` : ""}\n\n` +
        `The ball is with: ${bottleneckBallLabel(bn.ballInCourt)}.` +
        (bn.dueBy ? `\nNeeds to clear by ${bn.dueBy} to hold the programme.` : "") +
        (blocked.length ? `\n\nWork held up (${blocked.length}): ${blocked.slice(0, 6).join("; ")}${blocked.length > 6 ? "…" : ""}` : "") +
        `\n\nYou'll be emailed as this develops, and reminded until it's closed.`,
        { link: `${APP_URL}/pm/${pid}/bottlenecks`, label: "Open the bottleneck" });
      return logActivity({ ...p, bottlenecks: [bn, ...rows] }, by, `raised ${ref} — ${bn.title}`, "rfi");
    });
    return ref;
  },
  commentBottleneck(pid: string, bnId: string, body: string, by: string) {
    if (!body.trim()) return;
    patchProject(pid, (p) => {
      const bn = (p.bottlenecks ?? []).find((x) => x.id === bnId);
      if (!bn) return p;
      const comment: BottleneckComment = { id: uid("bnc-"), by, at: nowISO(), body: body.trim() };
      // everyone hears except whoever just spoke
      void notify(bn.stakeholders.filter((s) => s.name !== by).map((s) => s.email),
        `${bn.ref} update — ${bn.title} (${p.code})`,
        `${by} on ${bn.ref}:\n\n“${comment.body}”\n\nThe ball is with ${bottleneckBallLabel(bn.ballInCourt)}.`,
        { link: `${APP_URL}/pm/${pid}/bottlenecks`, label: "Reply on the bottleneck" });
      return logActivity({
        ...p,
        bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId ? { ...x, comments: [...x.comments, comment] } : x),
      }, by, `commented on ${bn.ref}`, "rfi");
    });
  },
  setBottleneckBall(pid: string, bnId: string, ball: BallInCourt, by: string) {
    patchProject(pid, (p) => {
      const bn = (p.bottlenecks ?? []).find((x) => x.id === bnId);
      if (!bn || bn.ballInCourt === ball) return p;
      void notify(bn.stakeholders.map((s) => s.email),
        `${bn.ref} — ball now with ${bottleneckBallLabel(ball)} (${p.code})`,
        `${by} moved ${bn.ref} — ${bn.title}.\n\nThe ball is now with ${bottleneckBallLabel(ball)}. It stays open, and the reminders follow the ball, until it's closed.`,
        { link: `${APP_URL}/pm/${pid}/bottlenecks`, label: "Open the bottleneck" });
      return logActivity({
        ...p,
        bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId ? { ...x, ballInCourt: ball } : x),
      }, by, `moved ${bn.ref} to ${bottleneckBallLabel(ball)}`, "rfi");
    });
  },
  closeBottleneck(pid: string, bnId: string, resolution: string, by: string) {
    patchProject(pid, (p) => {
      const bn = (p.bottlenecks ?? []).find((x) => x.id === bnId);
      if (!bn) return p;
      const blocked = (bn.blockedTaskIds ?? []).map((id) => (p.tasks ?? []).find((t) => t.id === id)?.name).filter(Boolean);
      void notify(bn.stakeholders.map((s) => s.email),
        `${bn.ref} CLOSED — ${bn.title} (${p.code})`,
        `${by} has closed ${bn.ref}.\n\nResolution: ${resolution.trim() || "resolved"}.` +
        (blocked.length ? `\n\nThe ${blocked.length} held-up activit${blocked.length === 1 ? "y" : "ies"} can move again.` : "") +
        `\n\nNo further reminders will be sent.`,
        { link: `${APP_URL}/pm/${pid}/bottlenecks`, label: "See the record" });
      return logActivity({
        ...p,
        bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId
          ? { ...x, status: "closed" as const, closedBy: by, closedAt: nowISO(), resolution: resolution.trim() || undefined }
          : x),
      }, by, `closed ${bn.ref} — ${bn.title}`, "rfi");
    });
  },
  reopenBottleneck(pid: string, bnId: string, by: string) {
    patchProject(pid, (p) => {
      const bn = (p.bottlenecks ?? []).find((x) => x.id === bnId);
      if (!bn || bn.status === "open") return p;
      void notify(bn.stakeholders.map((s) => s.email),
        `${bn.ref} RE-OPENED — ${bn.title} (${p.code})`,
        `${by} has re-opened ${bn.ref} — the issue isn't cleared after all. Reminders resume until it's closed again.`,
        { link: `${APP_URL}/pm/${pid}/bottlenecks`, label: "Open the bottleneck" });
      return logActivity({
        ...p,
        bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId
          ? { ...x, status: "open" as const, closedBy: undefined, closedAt: undefined, resolution: undefined, remindedOn: undefined }
          : x),
      }, by, `re-opened ${bn.ref}`, "rfi");
    });
  },
  setBottleneckStakeholders(pid: string, bnId: string, stakeholders: BottleneckStakeholder[]) {
    patchProject(pid, (p) => ({
      ...p,
      bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId ? { ...x, stakeholders } : x),
    }));
  },
  setBottleneckTasks(pid: string, bnId: string, taskIds: string[]) {
    patchProject(pid, (p) => ({
      ...p,
      bottlenecks: (p.bottlenecks ?? []).map((x) => x.id === bnId ? { ...x, blockedTaskIds: taskIds.length ? taskIds : undefined } : x),
    }));
  },

  /* --- sublet trade packages ------------------------------------------ */
  createPackage(pid: string, data: { name: string; trade: string; subcontractor?: string; contact?: string; email?: string; phone?: string; value: number; retentionPercent?: number; scope?: string; startOn?: string; finishOn?: string }, by: string): string {
    let ref = "";
    patchProject(pid, (p) => {
      const rows = p.packages ?? [];
      const n = rows.length ? Math.max(...rows.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      ref = `PKG-${String(n).padStart(3, "0")}`;
      const pkg: TradePackage = { id: uid("pkg-"), ref, status: "draft", payments: [], createdAt: nowISO(), createdBy: by, ...data };
      if (data.email) {
        void notify([data.email], `Trade package ${ref} — ${data.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit}: ${data.subcontractor ?? "your firm"} has been set up on ${p.name} for the ${data.name} package.\n\n${data.scope ?? ""}`);
      }
      return logActivity({ ...p, packages: [pkg, ...rows] }, by, `set up package ${ref} — ${data.name}`, "docs");
    });
    return ref;
  },
  updatePackage(pid: string, id: string, patch: Partial<TradePackage>, by: string) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === id);
      const next = { ...p, packages: (p.packages ?? []).map((x) => x.id === id ? { ...x, ...patch } : x) };
      return patch.status && pkg && patch.status !== pkg.status
        ? logActivity(next, by, `moved ${pkg.ref} to ${patch.status}`, "docs")
        : next;
    });
  },
  removePackage(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, packages: (p.packages ?? []).filter((x) => x.id !== id) }));
  },
  /** Value a sublet package for a period — the back-to-back payment. */
  addPackagePayment(pid: string, pkgId: string, data: { periodTo: string; gross: number; note?: string; by: string }) {
    patchProject(pid, (p) => ({
      ...p,
      packages: (p.packages ?? []).map((x) => {
        if (x.id !== pkgId) return x;
        const rows = x.payments ?? [];
        const n = rows.length + 1;
        const prevPaid = rows.reduce((s, r) => s + (r.gross - r.gross * ((r.retentionPercent ?? x.retentionPercent ?? 0) / 100)), 0);
        const pay: PackagePayment = {
          id: uid("pay-"), ref: `PAY-${String(n).padStart(3, "0")}`, periodTo: data.periodTo,
          gross: data.gross, retentionPercent: x.retentionPercent ?? 5,
          previouslyPaid: prevPaid, status: "draft", note: data.note, at: nowISO(), by: data.by,
        };
        return { ...x, payments: [pay, ...rows] };
      }),
    }));
  },
  setPackagePaymentStatus(pid: string, pkgId: string, payId: string, status: PackagePayment["status"]) {
    patchProject(pid, (p) => ({
      ...p,
      packages: (p.packages ?? []).map((x) => x.id !== pkgId ? x
        : { ...x, payments: (x.payments ?? []).map((r) => r.id === payId ? { ...r, status } : r) }),
    }));
  },

  /* --- the subcontract room --------------------------------------------- */
  /* One patch helper so every action below reads as what it changes. */
  patchPackage(pid: string, pkgId: string, fn: (pkg: TradePackage, p: Project) => TradePackage) {
    patchProject(pid, (p) => ({ ...p, packages: (p.packages ?? []).map((x) => x.id === pkgId ? fn(x, p) : x) }));
  },
  /** Replace the itemised scope (the builder edits the whole table). */
  setPackageScope(pid: string, pkgId: string, items: ScopeItem[]) {
    pmActions.patchPackage(pid, pkgId, (x) => ({ ...x, scopeItems: items }));
  },
  /** What of the project this package's subs are exposed to. */
  setPackageExposure(pid: string, pkgId: string, exposure: SubExposure, docIds: string[]) {
    pmActions.patchPackage(pid, pkgId, (x) => ({ ...x, exposure, docIds }));
  },
  /** Invite a subcontractor to price the package — their code opens ONLY this
      package's room. Returns the access code for handing over by any channel. */
  invitePackageSub(pid: string, pkgId: string, data: { company: string; contact: string; email: string }, by: string): string {
    const code = `SC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      if (!pkg) return p;
      const inv: SubInvite = { id: uid("sci-"), accessCode: code, invitedAt: nowISO(), status: "invited", ...data };
      if (data.email) {
        void notify([data.email], `Invitation to price — ${pkg.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit} invites ${data.company} to price the ${pkg.name} package on ${p.name}.\n\n` +
          `Open your pricing room and sign in with your access code:\n${APP_URL}\n\nAccess code: ${code}\n\n` +
          `The room carries the scope of works, the governing specification notes and the drawings the package is priced against.`,
          { link: APP_URL, label: "Open the pricing room" });
      }
      const next = { ...p, packages: (p.packages ?? []).map((x) => x.id === pkgId ? { ...x, invites: [...(x.invites ?? []), inv] } : x) };
      return logActivity(next, by, `invited ${data.company} to price ${pkg.ref} — ${pkg.name}`, "docs");
    });
    return code;
  },
  /** Invite a subcontractor to set out their own work programme in the
      package room — a link they tap into, no pricing asked. Reuses the firm's
      existing invite when one is picked so a single code opens the room.
      Returns the join link and code for handing over by any channel. */
  invitePackageProgramme(pid: string, pkgId: string, data: { company: string; contact: string; email: string; phone?: string; inviteId?: string }, by: string): { code: string; inviteId: string; link: string } {
    const p = state.projects.find((x) => x.id === pid);
    const pkg = p?.packages?.find((x) => x.id === pkgId);
    if (!p || !pkg) return { code: "", inviteId: "", link: APP_URL };
    const existing = data.inviteId ? (pkg.invites ?? []).find((i) => i.id === data.inviteId) : undefined;
    const code = existing?.accessCode ?? `SC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const inviteId = existing?.id ?? uid("sci-");
    const link = `${APP_URL}/join?c=${encodeURIComponent(code)}&tab=programme`;
    const email = (data.email || existing?.email || "").trim();
    patchProject(pid, (x) => {
      const inv: SubInvite = existing
        ? { ...existing, phone: data.phone || existing.phone, email: email || existing.email, purpose: existing.purpose ?? "programme" }
        : { id: inviteId, accessCode: code, invitedAt: nowISO(), status: "invited", company: data.company, contact: data.contact, email, phone: data.phone || undefined, purpose: "programme" };
      const next = { ...x, packages: (x.packages ?? []).map((k) => k.id === pkgId ? { ...k, invites: existing ? (k.invites ?? []).map((i) => i.id === inv.id ? inv : i) : [...(k.invites ?? []), inv] } : k) };
      return logActivity(next, by, `invited ${inv.company} to set out their ${pkg.ref} — ${pkg.name} work programme`, "docs");
    });
    if (email) {
      void notify([email], `Your work programme — ${pkg.name} (${p.code})`,
        `${STUDIO.name} ${STUDIO.unit} invites ${data.company || existing?.company} to set out your work programme for the ${pkg.name} package on ${p.name}.\n\n` +
        `Tap the link to open your room and add each line of your work — start date, duration, who you wait on, and progress as it happens:\n${link}\n\n` +
        `Access code: ${code} (if you ever need to sign in by hand).\n\n` +
        `Your programme sits beside the main site programme so the studio can see what holds you up and plan around it.`,
        { link, label: "Set out your programme" });
    }
    return { code, inviteId, link };
  },
  markSubActive(pid: string, pkgId: string, inviteId: string) {
    pmActions.patchPackage(pid, pkgId, (x) => ({
      ...x,
      invites: (x.invites ?? []).map((i) => i.id === inviteId ? { ...i, status: i.status === "invited" ? "active" : i.status, lastActive: nowISO() } : i),
    }));
  },
  /** A sub submits (or resubmits) their quote. A fresh submission supersedes
      the same firm's earlier one, which stays on the record as "revised". */
  submitSubQuote(pid: string, pkgId: string, quote: Omit<SubQuote, "id" | "at" | "status">) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      if (!pkg) return p;
      const q: SubQuote = { ...quote, id: uid("scq-"), at: nowISO(), status: "submitted" };
      const total = q.lines.reduce((s, l) => s + (l.amount || 0), 0) + (q.extras ?? []).reduce((s, e) => s + (e.amount || 0), 0);
      void notify(STUDIO.managerEmail, `Quote in — ${pkg.name} (${p.code})`,
        `${q.company} has priced ${pkg.ref} — ${pkg.name} at ${total.toLocaleString()} ${p.currency || ""}.\n\nOpen the package room to compare quotes.`);
      const next = {
        ...p,
        packages: (p.packages ?? []).map((x) => x.id === pkgId ? {
          ...x,
          quotes: [q, ...(x.quotes ?? []).map((o) => o.inviteId === q.inviteId && o.status === "submitted" ? { ...o, status: "revised" as const } : o)],
        } : x),
      };
      return logActivity(next, q.by, `submitted a quote for ${pkg.ref} — ${pkg.name}`, "docs");
    });
  },
  /** Award the package to one quote: the sub becomes the package's firm, the
      order value becomes the quote total, and the room switches to delivery. */
  awardPackage(pid: string, pkgId: string, quoteId: string, by: string) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      const q = pkg?.quotes?.find((x) => x.id === quoteId);
      if (!pkg || !q) return p;
      const inv = (pkg.invites ?? []).find((i) => i.id === q.inviteId);
      const total = q.lines.reduce((s, l) => s + (l.amount || 0), 0) + (q.extras ?? []).reduce((s, e) => s + (e.amount || 0), 0);
      if (inv?.email) {
        void notify([inv.email], `Package awarded — ${pkg.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit}: ${inv.company} has been awarded the ${pkg.name} package on ${p.name} at ${total.toLocaleString()} ${p.currency || ""}.\n\nYour room now runs the job: progress against the scope, payment requests and claims all live there.\n\n${APP_URL}`,
          { link: APP_URL, label: "Open your package room" });
      }
      const next = {
        ...p,
        packages: (p.packages ?? []).map((x) => x.id === pkgId ? {
          ...x,
          status: "let" as const, value: total, awardedInviteId: q.inviteId,
          subcontractor: inv?.company ?? q.company, contact: inv?.contact, email: inv?.email,
          quotes: (x.quotes ?? []).map((o) => o.id === quoteId ? { ...o, status: "accepted" as const }
            : o.status === "submitted" ? { ...o, status: "declined" as const } : o),
        } : x),
      };
      return logActivity(next, by, `awarded ${pkg.ref} — ${pkg.name} to ${inv?.company ?? q.company}`, "award");
    });
  },
  /** The sub reports progress on one scope line — a claim until checked off. */
  setScopeProgress(pid: string, pkgId: string, itemId: string, progress: number) {
    pmActions.patchPackage(pid, pkgId, (x) => ({
      ...x,
      scopeItems: (x.scopeItems ?? []).map((i) => i.id === itemId ? { ...i, progress: Math.min(100, Math.max(0, Math.round(progress))) } : i),
    }));
  },
  /** Studio check-off: the line is built, seen and accepted (or reopened). */
  checkOffScope(pid: string, pkgId: string, itemId: string, done: boolean, by: string) {
    pmActions.patchPackage(pid, pkgId, (x) => ({
      ...x,
      scopeItems: (x.scopeItems ?? []).map((i) => i.id === itemId
        ? { ...i, done, doneBy: done ? by : undefined, doneAt: done ? nowISO() : undefined, progress: done ? 100 : i.progress }
        : i),
    }));
  },
  /** A payment request arrives from the sub's room — claimed figure, backup,
      and optionally a request for the work to be valued on site. */
  submitPaymentRequest(pid: string, pkgId: string, data: { periodTo: string; gross: number; note?: string; attachments?: SubAttachment[]; valuationRequested?: boolean; by: string }) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      if (!pkg) return p;
      const rows = pkg.payments ?? [];
      const pay: PackagePayment = {
        id: uid("pay-"), ref: `PAY-${String(rows.length + 1).padStart(3, "0")}`, periodTo: data.periodTo,
        gross: data.gross, retentionPercent: pkg.retentionPercent ?? 5, status: "requested",
        note: data.note, at: nowISO(), by: data.by, requestedBy: data.by,
        attachments: data.attachments?.length ? data.attachments : undefined,
        valuationRequested: data.valuationRequested || undefined,
      };
      void notify(STUDIO.managerEmail, `Payment request ${pay.ref} — ${pkg.name} (${p.code})`,
        `${pkg.subcontractor ?? data.by} requests ${data.gross.toLocaleString()} ${p.currency || ""} for the period to ${data.periodTo}.` +
        (data.valuationRequested ? "\n\nThey have asked for the work to be VALUED ON SITE before certification." : "") +
        `\n\nOpen the package room to certify.`);
      const next = { ...p, packages: (p.packages ?? []).map((x) => x.id === pkgId ? { ...x, payments: [pay, ...rows] } : x) };
      return logActivity(next, data.by, `requested payment ${pay.ref} on ${pkg.ref} — ${pkg.name}`, "docs");
    });
  },
  /** Certify a payment request — at the claimed figure or the studio's own. */
  certifyPaymentRequest(pid: string, pkgId: string, payId: string, certifiedGross: number, by: string) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      const row = pkg?.payments?.find((r) => r.id === payId);
      if (!pkg || !row) return p;
      if (pkg.email) {
        void notify([pkg.email], `Payment certified — ${row.ref} on ${pkg.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit} has certified ${certifiedGross.toLocaleString()} ${p.currency || ""} gross against your request${certifiedGross !== row.gross ? ` of ${row.gross.toLocaleString()}` : ""} for the period to ${row.periodTo}.`);
      }
      const next = {
        ...p,
        packages: (p.packages ?? []).map((x) => x.id !== pkgId ? x : {
          ...x,
          payments: (x.payments ?? []).map((r) => r.id === payId ? {
            ...r, status: "certified" as const,
            certifiedGross: certifiedGross !== r.gross ? certifiedGross : undefined,
            certifiedBy: by, certifiedAt: nowISO(),
          } : r),
        }),
      };
      return logActivity(next, by, `certified ${row.ref} on ${pkg.ref} — ${pkg.name}`, "docs");
    });
  },
  /** The sub raises a claim from their room. */
  raiseSubClaim(pid: string, pkgId: string, data: { title: string; detail: string; amount?: number; attachments?: SubAttachment[]; by: string }): string {
    let ref = "";
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      if (!pkg) return p;
      const rows = pkg.claims ?? [];
      ref = `CLM-${String(rows.length + 1).padStart(3, "0")}`;
      const claim: SubClaim = {
        id: uid("clm-"), ref, title: data.title, detail: data.detail, amount: data.amount,
        attachments: data.attachments?.length ? data.attachments : undefined,
        at: nowISO(), by: data.by, status: "open",
      };
      void notify(STUDIO.managerEmail, `${ref} — claim on ${pkg.name} (${p.code})`,
        `${pkg.subcontractor ?? data.by}: ${data.title}${data.amount ? ` — ${data.amount.toLocaleString()} ${p.currency || ""}` : ""}\n\n${data.detail}`);
      const next = { ...p, packages: (p.packages ?? []).map((x) => x.id === pkgId ? { ...x, claims: [claim, ...rows] } : x) };
      return logActivity(next, data.by, `raised ${ref} on ${pkg.ref} — ${data.title}`, "docs");
    });
    return ref;
  },
  /** The studio answers a claim — assessed / settled (with a figure) / rejected. */
  respondSubClaim(pid: string, pkgId: string, claimId: string, data: { status: SubClaim["status"]; response: string; settledAmount?: number; by: string }) {
    patchProject(pid, (p) => {
      const pkg = (p.packages ?? []).find((x) => x.id === pkgId);
      const claim = pkg?.claims?.find((cl) => cl.id === claimId);
      if (!pkg || !claim) return p;
      if (pkg.email) {
        void notify([pkg.email], `${claim.ref} ${data.status} — ${pkg.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit} on your claim "${claim.title}": ${data.status.toUpperCase()}${data.settledAmount ? ` at ${data.settledAmount.toLocaleString()} ${p.currency || ""}` : ""}.\n\n${data.response}`);
      }
      const next = {
        ...p,
        packages: (p.packages ?? []).map((x) => x.id !== pkgId ? x : {
          ...x,
          claims: (x.claims ?? []).map((cl) => cl.id === claimId ? {
            ...cl, status: data.status, response: data.response, settledAmount: data.settledAmount,
            respondedBy: data.by, respondedAt: nowISO(),
          } : cl),
        }),
      };
      return logActivity(next, data.by, `${data.status} ${claim.ref} on ${pkg.ref}`, "docs");
    });
  },

  /* --- health & safety -------------------------------------------------- */
  addSafetyRecord(pid: string, data: Omit<SafetyRecord, "id" | "ref">): string {
    let ref = "";
    patchProject(pid, (p) => {
      const rows = p.safety ?? [];
      const n = rows.length ? Math.max(...rows.map((x) => parseInt(x.ref.split("-")[1], 10) || 0)) + 1 : 1;
      ref = `HS-${String(n).padStart(3, "0")}`;
      const rec: SafetyRecord = { id: uid("hs-"), ref, ...data };
      // anything with an injury, or reportable, reaches the studio at once
      if (rec.reportable || (rec.severity && rec.severity !== "none")) {
        void notify(STUDIO.managerEmail, `${ref} — ${rec.kind} on ${p.name} (${p.code})`,
          `${rec.title}\n\nSeverity: ${rec.severity ?? "n/a"}${rec.reportable ? " · REPORTABLE" : ""}\nRaised by ${rec.by}${rec.location ? ` at ${rec.location}` : ""}.\n\n${rec.detail ?? ""}`);
      }
      return logActivity({ ...p, safety: [rec, ...rows] }, data.by, `logged ${ref} — ${rec.title}`, "docs");
    });
    return ref;
  },
  updateSafetyRecord(pid: string, id: string, patch: Partial<SafetyRecord>) {
    patchProject(pid, (p) => ({ ...p, safety: (p.safety ?? []).map((x) => x.id === id ? { ...x, ...patch } : x) }));
  },
  removeSafetyRecord(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, safety: (p.safety ?? []).filter((x) => x.id !== id) }));
  },

  /* --- the governing contract, indexed -------------------------------- */
  setContractIndex(pid: string, data: { form: string; clauses: Omit<ContractClause, "id">[]; by: string; sourceDocId?: string }) {
    patchProject(pid, (p) => logActivity({
      ...p,
      contractIndex: {
        form: data.form, sourceDocId: data.sourceDocId,
        indexedAt: nowISO(), indexedBy: data.by,
        clauses: data.clauses.map((cl) => ({ ...cl, id: uid("cl-") })),
      },
    }, data.by, `indexed the contract — ${data.clauses.length} clauses (${data.form})`, "docs"));
  },
  clearContractIndex(pid: string) {
    patchProject(pid, (p) => ({ ...p, contractIndex: undefined }));
  },

  /* --- period snapshots ------------------------------------------------ */
  /** Freeze the headline position. The reading is computed by the caller
      (pmRegisters.readPosition) so this stays a pure write. */
  takeSnapshot(pid: string, data: { label: string; by: string; auto?: boolean } & Record<string, any>) {
    patchProject(pid, (p) => {
      const { label, by, auto, ...reading } = data;
      const snap = { id: uid("snap-"), at: nowISO(), label: label.trim() || new Date().toISOString().slice(0, 10), by, auto, ...reading } as any;
      return logActivity({ ...p, snapshots: [snap, ...(p.snapshots ?? [])].slice(0, 60) }, by, `froze a snapshot — ${snap.label}`, "docs");
    });
  },
  removeSnapshot(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, snapshots: (p.snapshots ?? []).filter((s) => s.id !== id) }));
  },

  /* --- weather delay register + EOT ---------------------------------- */
  logDelay(pid: string, data: { date: string; weather: Weather; claimedDays: number; criticalImpact: boolean; tasksAffected?: string[]; note?: string; by: string }) {
    patchProject(pid, (p) => {
      if ((p.delays ?? []).some((d) => d.date === data.date)) return p; // one per day
      const delay = { id: uid("dly-"), date: data.date, weather: data.weather, claimedDays: data.claimedDays, criticalImpact: data.criticalImpact, tasksAffected: data.tasksAffected, note: data.note?.trim() || undefined, loggedBy: data.by, loggedAt: nowISO(), includeInEot: data.criticalImpact };
      return logActivity({ ...p, delays: [delay, ...(p.delays ?? [])] }, data.by, `logged a weather delay on ${data.date}`, "docs");
    });
  },
  updateDelay(pid: string, id: string, patch: Partial<{ claimedDays: number; criticalImpact: boolean; note: string; includeInEot: boolean }>) {
    patchProject(pid, (p) => ({ ...p, delays: (p.delays ?? []).map((d) => d.id === id ? { ...d, ...patch } : d) }));
  },
  removeDelay(pid: string, id: string) {
    patchProject(pid, (p) => ({ ...p, delays: (p.delays ?? []).filter((d) => d.id !== id) }));
  },
  /** Stamp the EOT claim ref on the delays it covers, and clear them from the pool. */
  markDelaysClaimed(pid: string, ids: string[], eotRef: string) {
    const set = new Set(ids);
    patchProject(pid, (p) => ({ ...p, delays: (p.delays ?? []).map((d) => set.has(d.id) ? { ...d, eotRef, includeInEot: false } : d) }));
  },
  recordClientVisit(pid: string, name: string) {
    const p = state.projects.find((x) => x.id === pid);
    const last = p?.clientSeen?.[0];
    if (last && last.name === name && Date.now() - +new Date(last.at) < 30 * 60_000) return; // throttle
    patchProject(pid, (pr) => ({
      ...pr,
      clientSeen: [{ at: nowISO(), name }, ...(pr.clientSeen ?? [])].slice(0, 30),
    }));
  },

  /* --- things we need from the client ------------------------------------------- */
  /** Raise a request for something the client must supply. Emails their seats. */
  addClientRequest(pid: string, by: string, data: { title: string; detail?: string; dueOn?: string }): string | null {
    let ref: string | null = null;
    patchProject(pid, (p) => {
      const list = p.clientRequests ?? [];
      const n = list.length ? Math.max(...list.map((r) => parseInt(r.ref.split("-")[1], 10) || 0)) + 1 : 1;
      ref = `REQ-${String(n).padStart(2, "0")}`;
      const req: ClientRequest = {
        id: uid("cq-"), ref, title: data.title.trim(), detail: data.detail?.trim() || undefined,
        dueOn: data.dueOn || undefined, status: "open", requestedOn: nowISO(), requestedBy: by,
      };
      const to = p.team.filter((m) => m.role === "client" && m.email).map((m) => m.email);
      if (to.length) {
        notify(to, `Action needed on ${p.name} — ${req.title}`,
          `${STUDIO.name}: we need something from you on ${p.name} (${p.code}).\n\n${req.ref} — ${req.title}${req.detail ? `\n${req.detail}` : ""}${req.dueOn ? `\nNeeded by ${req.dueOn}` : ""}\n\nOpen your project portal to provide it.`);
      }
      return logActivity({ ...p, clientRequests: [req, ...list] }, by, `asked the client for ${req.title}`, "docs");
    });
    return ref;
  },
  /** The client supplies it — optionally with a note and an attached file. */
  markRequestProvided(pid: string, reqId: string, by: string, data: { note?: string; fileId?: string; fileName?: string }) {
    patchProject(pid, (p) => {
      const req = (p.clientRequests ?? []).find((r) => r.id === reqId);
      notify([STUDIO.managerEmail],
        `${by} provided "${req?.title ?? "a requested item"}" — ${p.name}`,
        `${by} marked ${req?.ref ?? "a request"} as provided on ${p.name} (${p.code}).${data.note ? `\n\nTheir note: ${data.note}` : ""}${data.fileName ? `\nAttached: ${data.fileName}` : ""}`);
      return logActivity({
        ...p,
        clientRequests: (p.clientRequests ?? []).map((r) => r.id === reqId
          ? { ...r, status: "provided" as const, providedOn: nowISO(), providedNote: data.note?.trim() || undefined, fileId: data.fileId, fileName: data.fileName }
          : r),
      }, by, `provided ${req?.ref ?? "a requested item"}`, "docs");
    });
  },
  /** The studio accepts what came back, closing the item. */
  acceptClientRequest(pid: string, reqId: string, by: string) {
    patchProject(pid, (p) => {
      const req = (p.clientRequests ?? []).find((r) => r.id === reqId);
      return logActivity({
        ...p,
        clientRequests: (p.clientRequests ?? []).map((r) => r.id === reqId ? { ...r, status: "accepted" as const, acceptedOn: nowISO() } : r),
      }, by, `accepted ${req?.ref ?? "a client item"}`, "docs");
    });
  },
  /** Reopen or drop a request. */
  reopenClientRequest(pid: string, reqId: string) {
    patchProject(pid, (p) => ({ ...p, clientRequests: (p.clientRequests ?? []).map((r) => r.id === reqId ? { ...r, status: "open" as const, acceptedOn: undefined } : r) }));
  },
  removeClientRequest(pid: string, reqId: string) {
    patchProject(pid, (p) => ({ ...p, clientRequests: (p.clientRequests ?? []).filter((r) => r.id !== reqId) }));
  },
  /** Stamp that a chase went out, so the follow-up history is on the record. */
  markRequestsChased(pid: string, reqIds: string[]) {
    patchProject(pid, (p) => logActivity({
      ...p,
      clientRequests: (p.clientRequests ?? []).map((r) => reqIds.includes(r.id) ? { ...r, chasedOn: [nowISO(), ...(r.chasedOn ?? [])].slice(0, 10) } : r),
    }, STUDIO.manager, `followed up with the client on ${reqIds.length} outstanding item${reqIds.length === 1 ? "" : "s"}`, "docs"));
  },

  /* --- studio ↔ client thread ---------------------------------------------------- */
  sendClientMessage(pid: string, from: "studio" | "client", authorName: string, body: string, requestId?: string) {
    const text = body.trim();
    if (!text) return;
    patchProject(pid, (p) => {
      const msg: ClientMessage = { id: uid("cm-"), from, authorName, body: text, createdOn: nowISO(), requestId };
      if (from === "client") {
        notify([STUDIO.managerEmail], `Message from ${authorName} — ${p.name}`,
          `${authorName} wrote on ${p.name} (${p.code}):\n\n${text}`);
      } else {
        const to = p.team.filter((m) => m.role === "client" && m.email).map((m) => m.email);
        if (to.length) notify(to, `Message from ${STUDIO.name} — ${p.name}`, `${authorName} wrote:\n\n${text}\n\nReply in your project portal.`);
      }
      return logActivity({ ...p, clientChat: [...(p.clientChat ?? []), msg].slice(-200) },
        authorName, from === "client" ? "sent the studio a message" : "messaged the client", "docs");
    });
  },

  /* --- team invitations -------------------------------------------------------- */
  inviteMember(pid: string, data: { name: string; company: string; email: string; phone?: string; role: TeamRole }, presetCode?: string): string {
    let issued = presetCode ?? "";
    patchProject(pid, (p) => {
      const prefix = p.code.split("-")[1]?.slice(0, 2) ?? "PR";
      /* Re-inviting somebody already on the project used to append a SECOND
         seat with a SECOND code. The crew then held two or three codes for one
         job, each invite pointing at a different one, and a device signed in on
         the old seat looked fine while its work went nowhere the studio was
         looking. So an existing seat is refreshed in place and keeps its code —
         every invite already sent stays valid. */
      const existing = p.team.find((x) =>
        x.company.trim().toLowerCase() === data.company.trim().toLowerCase() &&
        (x.email.trim().toLowerCase() === data.email.trim().toLowerCase() ||
         x.name.trim().toLowerCase() === data.name.trim().toLowerCase()));
      if (existing && !presetCode) {
        issued = existing.accessCode;
        notify([data.email],
          `Your access to ${p.name} (${p.code})`,
          `${STUDIO.name} ${STUDIO.unit}: here is your link for ${p.name} again. Your access code is unchanged — ${existing.accessCode}.`,
          { link: `${APP_URL}/join?c=${encodeURIComponent(existing.accessCode)}`, label: "Open my project" });
        return logActivity(
          { ...p, team: p.team.map((x) => x.id === existing.id ? { ...x, ...data, accessCode: existing.accessCode } : x) },
          STUDIO.manager, `re-sent ${data.company}'s invitation (same access code)`, "bidder");
      }
      const code = presetCode ?? uniqueAccessCode(prefix, data.company);
      issued = code;
      const m: TeamMember = { id: uid("tm-"), ...data, accessCode: code, invitedAt: nowISO(), status: "invited" };
      peopleActions.remember({ name: data.name, company: data.company, email: data.email, phone: data.phone, role: data.role });
      notify([data.email],
        `You've been invited to ${p.name} (${p.code})`,
        `${STUDIO.name} ${STUDIO.unit}: you've been added to ${p.name} as ${data.role}. Tap the link below to open your project directly — your access code is ${code} if you ever need to sign in manually.`,
        { link: `${APP_URL}/join?c=${encodeURIComponent(code)}`, label: "Open my project" });
      return logActivity({ ...p, team: [...p.team, m] }, STUDIO.manager, `invited ${data.company} to the project as ${data.role}`, "bidder");
    });
    return issued;
  },
  markMemberActive(pid: string, memberId: string) {
    patchProject(pid, (p) => ({ ...p, team: p.team.map((m) => m.id === memberId && m.status === "invited" ? { ...m, status: "active" as const } : m) }));
  },
  /** Add or update a member's WhatsApp number (empty string clears it). */
  setMemberPhone(pid: string, memberId: string, phone: string) {
    patchProject(pid, (p) => ({
      ...p,
      team: p.team.map((m) => m.id === memberId ? { ...m, phone: phone.trim() || undefined } : m),
    }));
  },
  setMemberRole(pid: string, memberId: string, role: TeamRole) {
    patchProject(pid, (p) => {
      const m = p.team.find((x) => x.id === memberId);
      if (!m || m.role === role) return p;
      const next: Project = { ...p, team: p.team.map((x) => x.id === memberId ? { ...x, role } : x) };
      return logActivity(next, STUDIO.manager, `changed ${m.name}'s role to ${role}`, "bidder");
    });
  },
  /** Issue a fresh, guaranteed-unique access code (fixes shared/leaked codes)
      and email it to the member. Returns the new code. */
  reissueMemberCode(pid: string, memberId: string): string {
    let issued = "";
    patchProject(pid, (p) => {
      const m = p.team.find((x) => x.id === memberId);
      if (!m) return p;
      const prefix = p.code.split("-")[1]?.slice(0, 2) ?? "PR";
      const code = uniqueAccessCode(prefix, m.company, "TEAM", [m.accessCode]);
      issued = code;
      if (m.email) notify([m.email],
        `Your new access code for ${p.name} (${p.code})`,
        `${STUDIO.name} ${STUDIO.unit}: your access code for ${p.name} has been replaced. Sign in with ${code} at https://studio.studiogy.design — the old code no longer works.`);
      /* rotation is only real if devices already signed in fall out too —
         token labels are minted role:name, and this member signed in either
         as a client seat (pclient) or a contractor-side seat */
      void import("./managerAuth").then(({ authHeader }) =>
        fetch("/api/auth/revoke", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader() },
          body: JSON.stringify({ labels: [`pclient:${m.name}`, `contractor:${m.name}`] }),
        }).catch(() => { /* best-effort; the code change alone blocks new sign-ins */ }));
      const next: Project = { ...p, team: p.team.map((x) => x.id === memberId ? { ...x, accessCode: code } : x) };
      return logActivity(next, STUDIO.manager, `issued a new access code to ${m.name}`, "bidder");
    });
    return issued;
  },

  /* --- reports ---------------------------------------------------------------- */
  issueReport(pid: string, data: { title: string; period: string; summary: string; highlights: string[]; progressPercent: number }) {
    patchProject(pid, (p) => {
      const rep: ProgressReport = { id: uid("pr-"), issuedAt: nowISO(), ...data };
      return logActivity({ ...p, reports: [rep, ...p.reports] }, STUDIO.manager, `issued ${data.title}`, "docs");
    });
  },

  /** Issue an already-compiled period report (weekly/monthly). If it comes
      pre-assembled from the compiler it keeps its id; otherwise gets a fresh
      one. Prepended so the newest sits on top of the reports list. */
  issuePeriodReport(pid: string, report: ProgressReport, by = STUDIO.manager) {
    patchProject(pid, (p) => {
      const rep: ProgressReport = { ...report, id: report.id || uid("pr-"), issuedAt: report.issuedAt || nowISO() };
      return logActivity({ ...p, reports: [rep, ...p.reports.filter((r) => r.id !== rep.id)] }, by, `issued ${rep.title}`, "docs");
    });
  },

  /** Push an issued report to the client — marks it shared and emails the
      client team a note with a link into their portal. */
  sharePeriodReportWithClient(pid: string, reportId: string, by = STUDIO.manager) {
    patchProject(pid, (p) => {
      const rep = p.reports.find((r) => r.id === reportId);
      if (!rep) return p;
      const stamp = nowISO();
      const clientEmails = p.team.filter((m) => m.role === "client" && m.email).map((m) => m.email);
      if (clientEmails.length) {
        void notify(clientEmails, `${p.name} — ${rep.title}`,
          `${STUDIO.name} ${STUDIO.unit}: your ${rep.kind === "monthly" ? "monthly" : rep.kind === "weekly" ? "weekly" : ""} progress report for ${rep.period} is ready.\n\n${rep.summary}\n\nOpen your project portal to read the full report and download the PDF.`,
          { link: APP_URL, label: "Open the project portal" });
      }
      return logActivity(
        { ...p, reports: p.reports.map((r) => r.id === reportId ? { ...r, sharedWithClient: true, sharedAt: stamp } : r) },
        by, `shared ${rep.title} with the client`, "docs");
    });
  },
};
